import { buildProductFamilyThumbnail } from '../utils/productImages';
import { toSupplierProductRef, toCatalogItemRef } from '../utils/wizardHelpers';

export const initialFiles = [
  {
    id: 1,
    clientId: 'C002',
    offerId: 'CAR-20260125',
    fileName: 'CAR-20260125_Carlsberg_Order.pdf',
    fileType: 'pdf',
    description: 'Order confirmation for branded merchandise',
    uploadDate: '2026-01-25 10:30',
    uploadedBy: 'Peter Sørensen',
    generatedFromOffer: true,
  },
  {
    id: 2,
    clientId: 'C004',
    offerId: 'DAN-20260128',
    fileName: 'DAN-20260128_DanishCrown_Order.pdf',
    fileType: 'pdf',
    description: 'Large order for branded work uniforms',
    uploadDate: '2026-01-28 14:15',
    uploadedBy: 'Maria Kristensen',
    generatedFromOffer: true,
  },
  {
    id: 3,
    clientId: 'C002',
    fileName: 'Carlsberg_Logo_Guidelines.pdf',
    fileType: 'pdf',
    description: 'Brand guidelines for logo usage',
    uploadDate: '2026-01-20 09:00',
    uploadedBy: 'Peter Sørensen',
    generatedFromOffer: false,
  },
  {
    id: 4,
    clientId: 'C004',
    offerId: 'DAN-20260128',
    itemId: 'DAN-POLO-ART',
    productName: 'Bags',
    fileName: 'DanishCrown_print_artwork.ai',
    fileType: 'ai',
    description: 'Print artwork attached to the tote bag line',
    uploadDate: '2026-01-24 11:10',
    uploadedBy: 'Maria Kristensen',
    generatedFromOffer: false,
    source: 'Product attachment',
  },
  {
    id: 5,
    clientId: 'C002',
    offerId: 'CAR-20260125',
    itemId: 'CAR-TSH-SIZE',
    productName: 'Umbrellas',
    fileName: 'Carlsberg_quantity_breakdown.xlsx',
    fileType: 'xlsx',
    description: 'Quantity split uploaded while adding the umbrella product',
    uploadDate: '2026-01-20 12:45',
    uploadedBy: 'Peter Sørensen',
    generatedFromOffer: false,
    source: 'Product attachment',
  },
  {
    id: 6,
    clientId: 'C004',
    offerId: 'DAN-20260128',
    itemId: 'DAN-CAP-PROOF',
    productName: 'Umbrellas',
    fileName: 'DanishCrown_umbrella_panel_proof.pdf',
    fileType: 'pdf',
    description: 'Supplier proof for umbrella panel print',
    uploadDate: '2026-01-24 15:20',
    uploadedBy: 'Maria Kristensen',
    generatedFromOffer: false,
    source: 'Product attachment',
  },
  {
    id: 7,
    clientId: 'C004',
    offerId: 'DAN-20260128',
    itemId: 'DAN-PEN-ART',
    productName: 'Bags',
    fileName: 'DanishCrown_bag_logo_vector.svg',
    fileType: 'svg',
    description: 'Vector logo attached to the tote bag line',
    uploadDate: '2026-01-25 09:35',
    uploadedBy: 'Mads Mark-Buur',
    generatedFromOffer: false,
    source: 'Product attachment',
  },
  {
    id: 8,
    clientId: 'C006',
    offerId: 'ARL-20260125',
    itemId: 'ARL-MUG-LOGO',
    productName: 'Mugs',
    fileName: 'Arla_updated_mug_logo_pack.zip',
    fileType: 'zip',
    description: 'Updated mug artwork package attached to the product',
    uploadDate: '2026-02-01 10:05',
    uploadedBy: 'Lars Nielsen',
    generatedFromOffer: false,
    source: 'Product attachment',
  },
  {
    id: 9,
    clientId: 'C009',
    offerId: 'NET-20251105',
    itemId: 'NET-TOTE-PRINT',
    productName: 'Bags',
    fileName: 'Netto_tote_bag_print_layout.pdf',
    fileType: 'pdf',
    description: 'Print layout connected to the tote bag product row',
    uploadDate: '2025-10-29 13:45',
    uploadedBy: 'Mads Mark-Buur',
    generatedFromOffer: false,
    source: 'Product attachment',
  },
  {
    id: 10,
    clientId: 'C002',
    offerId: 'CAR-20260125',
    fileName: 'Carlsberg_customer_approval_email.pdf',
    fileType: 'pdf',
    description: 'Customer approval email for confirmed Carlsberg order',
    uploadDate: '2026-01-25 11:05',
    uploadedBy: 'Peter Sørensen',
    generatedFromOffer: false,
    approvalDocument: true,
    source: 'Order confirmation approval',
  },
  {
    id: 11,
    clientId: 'L001',
    offerId: 'NOV-20260201',
    itemId: 'QUO-L001-20260205-offer-item',
    productName: 'Bottles',
    fileName: 'Novo_bottle_logo_reference.jpg',
    fileType: 'jpg',
    description: 'Logo reference supplied with the quote for the water bottle line',
    uploadDate: '2026-02-05 11:30',
    uploadedBy: 'Mads Mark-Buur',
    generatedFromOffer: false,
    source: 'Product attachment',
  },
];

// Base product data (pre-Increment-1 shape); extended below into the exported
// `initialProducts` once `initialSuppliers` is available, so each Standard
// product can carry its owning supplier + supplier item number
// (product-module-naming.md; product-module-analysis.md §12 migration rule:
// "take its cheapest SupplierProduct row as the owning supplier").
// `categoryId` already reflects the customer's 9-category list below, not the
// demo's original 6 (naming §4 / §12.3 — Drinkware & Tech splits into
// Drinkware + Electronics, Bags & Outdoor splits into Bags + Outdoor).
// Standard products are limited to the customer's own named examples
// (product-module-naming.md / customer requirement note): Bottles, Mugs, Bags,
// Umbrellas, Power Banks. Colour-variant siblings (copied from these) are
// added separately below as `STANDARD_PRODUCT_VARIANTS`.
const PRODUCT_FAMILY_BY_ID = {
  3: 'Tote Bag',
  4: 'Coffee Mug',
  8: 'Water Bottle',
  12: 'Umbrella',
  16: 'Power Bank',
};

const PRODUCT_BASE = [
  { id: 3, code: 'BAG-TOT', name: 'Bags', categoryId: 1, description: 'Reusable shopping bags with custom print', material: '100% cotton canvas, 280 gsm', colour: 'Natural', status: 'Active' },
  { id: 4, code: 'MUG-CER', name: 'Mugs', categoryId: 2, description: 'Ceramic mugs with logo printing', material: 'Ceramic', colour: 'White', status: 'Active' },
  { id: 8, code: 'BOT-STL', name: 'Bottles', categoryId: 2, description: 'Stainless steel bottles with logo', material: 'Stainless steel 304', colour: 'Silver', status: 'Active' },
  { id: 12, code: 'UMB-STD', name: 'Umbrellas', categoryId: 8, description: 'Full-size umbrellas with logo print', material: 'Pongee fabric, steel frame', colour: 'Black', status: 'Active' },
  { id: 16, code: 'PWR-5K', name: 'Power Banks', categoryId: 6, description: '5000mAh portable chargers with logo', material: 'ABS plastic', colour: 'Black', status: 'Active' },
];

// The customer's 9 categories (product-module-naming.md §4), replacing the
// demo's original 6.
export const initialProductCategories = [
  { id: 1, name: 'Bags', description: 'Tote bags and other promotional carrying bags.', status: 'Active' },
  { id: 2, name: 'Drinkware', description: 'Mugs, bottles, and other reusable drinking vessels.', status: 'Active' },
  { id: 3, name: 'Textiles', description: 'Wearables and other fabric-based promotional products.', status: 'Active' },
  { id: 4, name: 'Toys', description: 'Novelty and play items used as promotional giveaways.', status: 'Active' },
  { id: 5, name: 'Sports', description: 'Sports and fitness-related promotional products.', status: 'Active' },
  { id: 6, name: 'Electronics', description: 'USB drives, power banks, and other tech accessories.', status: 'Active' },
  { id: 7, name: 'Office', description: 'Pens, notebooks, keychains, and other desk/office items.', status: 'Active' },
  { id: 8, name: 'Outdoor', description: 'Umbrellas, cooler bags, towels, and other outdoor-use items.', status: 'Active' },
  { id: 9, name: 'Packaging', description: 'Packaging and presentation materials for promotional products.', status: 'Active' },
];

// Fixed seed catalogue (product-module-analysis.md §5.2 / Q5 — not user-maintained
// CRUD beyond this admin screen). serviceType drives Decoration-vs-Charge pricing
// behaviour: Decoration scales per unit with order quantity; Charge is a flat fee.
// supplierId: owning supplier (product-module-services-logistics correction —
// same "one item, one supplier" ownership Product already has). Screen Print
// (id 2) -> ColorCraft Decoration (11) and Sea Freight (id 2) -> SwiftCargo
// Express (12) must stay as-is: the Increment-3 seed order lines
// (DAN-20260128, CAR-20260125 in initialOrderSuppliersRaw) already reference
// exactly those pairings.
export const initialServiceItems = [
  { id: 1, code: 'SRV-DIG', name: 'Digital Print', description: 'Full-colour digital printing, no minimum order quantity.', serviceType: 'Decoration', pricingUnit: 'PerUnit', status: 'Active', supplierId: 2 },
  { id: 2, code: 'SRV-SCR', name: 'Screen Print', description: 'Screen printing for solid-colour logos on textiles and hard goods.', serviceType: 'Decoration', pricingUnit: 'PerUnit', status: 'Active', supplierId: 11 },
  { id: 3, code: 'SRV-PAD', name: 'Pad Printing', description: 'Pad printing for curved or irregular surfaces (pens, bottles, gadgets).', serviceType: 'Decoration', pricingUnit: 'PerUnit', status: 'Active', supplierId: 11 },
  { id: 4, code: 'SRV-LAS', name: 'Laser Engraving', description: 'Permanent laser-engraved branding on metal, wood, and glass.', serviceType: 'Decoration', pricingUnit: 'PerUnit', status: 'Active', supplierId: 9 },
  { id: 5, code: 'SRV-EMB', name: 'Embroidery', description: 'Embroidered branding for textiles and bags.', serviceType: 'Decoration', pricingUnit: 'PerUnit', status: 'Active', supplierId: 9 },
  { id: 6, code: 'SRV-HEA', name: 'Heat Transfer', description: 'Heat-transfer branding for apparel and soft goods.', serviceType: 'Decoration', pricingUnit: 'PerUnit', status: 'Active', supplierId: 2 },
  { id: 7, code: 'SRV-SUB', name: 'Sublimation', description: 'All-over sublimation printing for polyester textiles and hard goods.', serviceType: 'Decoration', pricingUnit: 'PerUnit', status: 'Active', supplierId: 11 },
  { id: 8, code: 'SRV-SET', name: 'Setup Charge', description: 'One-off charge to prepare artwork/screens/plates for a production run.', serviceType: 'Charge', pricingUnit: 'FixedFee', status: 'Active', supplierId: 2 },
  { id: 9, code: 'SRV-SAM', name: 'Sample Charge', description: 'One-off charge to produce a pre-production sample for approval.', serviceType: 'Charge', pricingUnit: 'FixedFee', status: 'Active', supplierId: 11 },
  { id: 10, code: 'SRV-TOL', name: 'Tooling / Mold Charge', description: 'One-off charge for new tooling or molds required for a custom product.', serviceType: 'Charge', pricingUnit: 'FixedFee', status: 'Active', supplierId: 9 },
  // Owned by the multi-role suppliers added for the inquiry-module-redesign
  // (13, 15, 16, 17 — see EXTRA_SUPPLIER_PRODUCTS below, which already
  // shows them quoting a Services role) so their derived Supplier Type
  // badge (derivedSupplierTypes) has matching rows in this catalogue's own
  // Supplier Services tab, instead of a role tag with nothing behind it.
  { id: 11, code: 'SRV-BPP', name: 'Screen Print (EU)', description: 'Screen printing on cotton bags, EU production.', serviceType: 'Decoration', pricingUnit: 'PerUnit', status: 'Active', supplierId: 13 },
  { id: 12, code: 'SRV-MBS', name: 'Silk-Screen Branding', description: 'Premium silk-screen branding for outdoor items.', serviceType: 'Decoration', pricingUnit: 'PerUnit', status: 'Active', supplierId: 15 },
  { id: 13, code: 'SRV-AGT', name: 'UV Printing', description: 'UV printing on ABS casing for electronics.', serviceType: 'Decoration', pricingUnit: 'PerUnit', status: 'Active', supplierId: 16 },
  { id: 14, code: 'SRV-ITW', name: 'Embroidery (TR)', description: 'Embroidered branding for textiles and bags.', serviceType: 'Decoration', pricingUnit: 'PerUnit', status: 'Active', supplierId: 17 },
];

export const initialLogisticsItems = [
  { id: 1, code: 'LOG-HND', name: 'Handling', description: 'Warehouse handling, consolidation and dispatch preparation.', pricingUnit: 'FixedFee', status: 'Active', supplierId: 10 },
  { id: 2, code: 'LOG-SEA', name: 'Sea Freight', description: 'Ocean freight, priced per cubic metre.', pricingUnit: 'PerCbm', status: 'Active', supplierId: 12 },
  { id: 3, code: 'LOG-AIR', name: 'Air Freight', description: 'Air freight, priced per kilogram.', pricingUnit: 'PerKg', status: 'Active', supplierId: 10 },
  { id: 4, code: 'LOG-COU', name: 'Courier', description: 'Express courier for small parcels, priced per kilogram.', pricingUnit: 'PerKg', status: 'Active', supplierId: 12 },
  // Owned by the multi-role suppliers added for the inquiry-module-redesign
  // (14, 15, 16, 17, 18 — see EXTRA_SUPPLIER_PRODUCTS below, which already
  // shows them quoting a Logistics role) — same reasoning as the Services
  // block above.
  { id: 5, code: 'LOG-SCM', name: 'Sea Freight (Asia)', description: 'Consolidated sea-freight delivery ex-Vietnam.', pricingUnit: 'PerCbm', status: 'Active', supplierId: 14 },
  { id: 6, code: 'LOG-MBS', name: 'Regional Courier (IT)', description: 'Regional courier delivery within Italy/EU.', pricingUnit: 'PerKg', status: 'Active', supplierId: 15 },
  { id: 7, code: 'LOG-AGT', name: 'EU Distribution', description: 'EU-wide distribution network.', pricingUnit: 'PerKg', status: 'Active', supplierId: 16 },
  { id: 8, code: 'LOG-ITW', name: 'Overland Freight (TR-EU)', description: 'Overland freight to EU hubs.', pricingUnit: 'PerKg', status: 'Active', supplierId: 17 },
  { id: 9, code: 'LOG-ONS', name: 'Nordic Regional Delivery', description: 'Nordic regional delivery network.', pricingUnit: 'PerKg', status: 'Active', supplierId: 18 },
];

// Per-category packaging defaults (product-module-analysis.md §6.1 shape),
// applied to every Standard product below unless overridden per-product.
const CATEGORY_PACKAGING_DEFAULTS = {
  1: { unitPackaging: 'Polybag', itemsPerCarton: 100, cartonDimensionsCm: '55 x 40 x 45', cartonGrossWeightKg: 10 },
  2: { unitPackaging: 'Individual gift box', itemsPerCarton: 24, cartonDimensionsCm: '40 x 30 x 35', cartonGrossWeightKg: 9.6 },
  3: { unitPackaging: 'Polybag', itemsPerCarton: 100, cartonDimensionsCm: '60 x 40 x 50', cartonGrossWeightKg: 12 },
  4: { unitPackaging: 'Polybag', itemsPerCarton: 200, cartonDimensionsCm: '45 x 35 x 35', cartonGrossWeightKg: 9 },
  5: { unitPackaging: 'Polybag', itemsPerCarton: 50, cartonDimensionsCm: '50 x 40 x 40', cartonGrossWeightKg: 10 },
  6: { unitPackaging: 'Individual box', itemsPerCarton: 50, cartonDimensionsCm: '35 x 25 x 20', cartonGrossWeightKg: 7 },
  7: { unitPackaging: 'Bulk pack', itemsPerCarton: 500, cartonDimensionsCm: '40 x 30 x 30', cartonGrossWeightKg: 8 },
  8: { unitPackaging: 'Polybag', itemsPerCarton: 30, cartonDimensionsCm: '70 x 40 x 40', cartonGrossWeightKg: 11 },
  9: { unitPackaging: 'Flat pack', itemsPerCarton: 250, cartonDimensionsCm: '50 x 35 x 15', cartonGrossWeightKg: 6 },
};

export const initialSuppliers = [
  { id: 1, name: 'Nordic Textiles A/S', visibility: 'Public', supplierType: 'Manufacturing', cvr: '12345678', address: 'Textilvej 10, 8000 Aarhus', country: 'Denmark', currency: 'DKK', phone: '+45 86 12 34 56', email: 'info@nordictextiles.dk', website: 'https://www.nordictextiles.dk', status: 'Active', created: '2024-03-12' },
  { id: 2, name: 'PrintPartner ApS', visibility: 'Public', supplierType: 'Customization', cvr: '23456789', address: 'Trykkersgade 5, 2200 Copenhagen', country: 'Denmark', currency: 'DKK', phone: '+45 35 12 00 00', email: 'orders@printpartner.dk', website: 'https://www.printpartner.dk', status: 'Active', created: '2024-04-02' },
  { id: 3, name: 'CeramicWorks DK', visibility: 'Private', supplierType: 'Manufacturing', cvr: '34567890', address: 'No. 88 Jingdezhen Ceramics Road, Jingdezhen', country: 'China', currency: 'CNY', phone: '+86 798 123 4567', email: 'sales@ceramicworks.dk', website: 'https://www.ceramicworks.dk', status: 'Active', created: '2024-05-20' },
  { id: 4, name: 'TechGifts EU', visibility: 'Public', supplierType: 'Product', cvr: '45678901', address: 'Gadgetstraße 22, 10115 Berlin', country: 'Germany', currency: 'EUR', phone: '+49 30 1234 5678', email: 'contact@techgifts.eu', website: 'https://www.techgifts.eu', status: 'Active', created: '2024-06-15' },
  { id: 5, name: 'PaperCraft ApS', visibility: 'Private', supplierType: 'Manufacturing', cvr: '56789012', address: 'Papirvej 8, 7100 Vejle', country: 'Denmark', currency: 'DKK', phone: '+45 76 40 50 60', email: 'info@papercraft.dk', website: 'https://www.papercraft.dk', status: 'Active', created: '2024-07-08' },
  { id: 6, name: 'MetalGoods Scandinavia', visibility: 'Public', supplierType: 'Manufacturing', cvr: '67890123', address: 'Plot 14, Pune Industrial Estate, Pune', country: 'India', currency: 'INR', phone: '+91 20 6123 4567', email: 'sales@metalgoods.dk', website: 'https://www.metalgoods.dk', status: 'Active', created: '2024-08-25' },
  { id: 7, name: 'EventSupply Co.', visibility: 'Public', supplierType: 'Fulfilment', cvr: '78901234', address: 'Eventparken 1, 2630 Taastrup', country: 'Denmark', currency: 'DKK', phone: '+45 43 55 66 77', email: 'hello@eventsupply.dk', website: 'https://www.eventsupply.dk', status: 'Active', created: '2024-09-10' },
  { id: 8, name: 'PromoGoods DK', visibility: 'Private', supplierType: 'Product', cvr: '89012345', address: 'Sector 18, Industrial Area, Gurugram', country: 'India', currency: 'INR', phone: '+91 124 456 7890', email: 'order@promogoods.dk', website: 'https://www.promogoods.dk', status: 'Active', created: '2024-10-30' },
  { id: 9, name: 'BrandEmboss Studio', visibility: 'Public', supplierType: 'Branding', cvr: '90123456', address: 'Broderivej 4, 6000 Kolding', country: 'Denmark', currency: 'DKK', phone: '+45 75 11 22 33', email: 'hello@brandembossstudio.dk', website: 'https://www.brandembossstudio.dk', status: 'Active', created: '2024-11-18' },
  { id: 10, name: 'NordicFreight Logistics', visibility: 'Public', supplierType: 'Logistics', cvr: '01234567', address: 'Fragtvej 12, 7000 Fredericia', country: 'Denmark', currency: 'DKK', phone: '+45 75 99 88 77', email: 'dispatch@nordicfreight.dk', website: 'https://www.nordicfreight.dk', status: 'Active', created: '2024-12-05' },
  { id: 11, name: 'ColorCraft Decoration', visibility: 'Public', supplierType: 'Branding', cvr: '11223344', address: 'Building 6, Shenzhen Bao’an Industrial Park, Shenzhen', country: 'China', currency: 'CNY', phone: '+86 755 1234 5678', email: 'hello@colorcraftdecoration.dk', website: 'https://www.colorcraftdecoration.dk', status: 'Active', created: '2025-01-22' },
  { id: 12, name: 'SwiftCargo Express', visibility: 'Public', supplierType: 'Logistics', cvr: '22334455', address: 'Frachtgatan 6, 211 20 Malmö', country: 'Sweden', currency: 'SEK', phone: '+46 40 12 34 56', email: 'dispatch@swiftcargo.dk', website: 'https://www.swiftcargo.dk', status: 'Active', created: '2025-02-14' },
  // Multi-role suppliers from additional countries (inquiry-module-redesign)
  // — each holds two or three Supplier_Product roles at once, demonstrating
  // derivedSupplierTypes returning multiple simultaneous tags.
  { id: 13, name: 'Baltic Pack Polska', visibility: 'Public', supplierType: 'Manufacturing', cvr: '33445566', address: 'ul. Przemysłowa 12, 61-003 Poznań', country: 'Poland', currency: 'PLN', phone: '+48 61 234 5678', email: 'sales@balticpack.pl', website: 'https://www.balticpack.pl', status: 'Active', created: '2025-03-10' },
  { id: 14, name: 'Saigon Craft Manufacturing', visibility: 'Public', supplierType: 'Manufacturing', cvr: '44556677', address: '128 Nguyễn Văn Cừ, District 5, Ho Chi Minh City', country: 'Vietnam', currency: 'VND', phone: '+84 28 3925 1234', email: 'export@saigoncraft.vn', website: 'https://www.saigoncraft.vn', status: 'Active', created: '2025-04-02' },
  { id: 15, name: 'Milano Branding Studio', visibility: 'Public', supplierType: 'Branding', cvr: '55667788', address: 'Via Tortona 25, 20144 Milano', country: 'Italy', currency: 'EUR', phone: '+39 02 8765 4321', email: 'info@milanobranding.it', website: 'https://www.milanobranding.it', status: 'Active', created: '2025-04-28' },
  { id: 16, name: 'Amsterdam Global Trade', visibility: 'Public', supplierType: 'Manufacturing', cvr: '66778899', address: 'Herengracht 210, 1016 BT Amsterdam', country: 'Netherlands', currency: 'EUR', phone: '+31 20 654 3210', email: 'contact@amsterdamglobaltrade.nl', website: 'https://www.amsterdamglobaltrade.nl', status: 'Active', created: '2025-05-15' },
  { id: 17, name: 'Istanbul Textile Works', visibility: 'Public', supplierType: 'Manufacturing', cvr: '77889900', address: 'Merter Sanayi Sitesi, 34169 İstanbul', country: 'Turkey', currency: 'TRY', phone: '+90 212 555 6677', email: 'export@istanbultextile.com.tr', website: 'https://www.istanbultextile.com.tr', status: 'Active', created: '2025-06-08' },
  { id: 18, name: 'Oslo Nordic Supplies', visibility: 'Public', supplierType: 'Manufacturing', cvr: '88990011', address: 'Grensen 14, 0159 Oslo', country: 'Norway', currency: 'NOK', phone: '+47 22 33 44 55', email: 'post@oslonordicsupplies.no', website: 'https://www.oslonordicsupplies.no', status: 'Active', created: '2025-07-20' },
];

// Legacy per-product internal prices, normalized below into initialSupplierProducts
// (supplier-specific data no longer lives on the generic Product record).
const LEGACY_PRODUCT_SUPPLIER_PRICES = {
  // Production cost is filled in for the 7 products actively quoted in
  // initialQuotes (1, 2, 3, 6, 7, 10, 15), and those 3 also get a 2nd supplier
  // so every role assignable from QuoteDetailPage has a real choice to compare
  // instead of "No eligible suppliers" — see SupplierComparisonDialog's
  // per-(productId, role) filtering.
  3: [{ supplier: 'Nordic Textiles A/S', article: 'NT-BAG-TOT', internalPrice: 28, productionPrice: 3.5 }, { supplier: 'PrintPartner ApS', article: 'PP-TOTE-420', internalPrice: 31, productionPrice: 4 }],
  4: [{ supplier: 'CeramicWorks DK', article: 'CW-MUG-330', internalPrice: 20 }, { supplier: 'PromoGoods DK', article: 'PG-MUG-WHT', internalPrice: 22 }],
  8: [{ supplier: 'MetalGoods Scandinavia', article: 'MG-BOT-750', internalPrice: 31 }, { supplier: 'TechGifts EU', article: 'TG-BOT-STL', internalPrice: 35 }],
  12: [{ supplier: 'Nordic Textiles A/S', article: 'NT-UMB-STD', internalPrice: 49 }],
  16: [{ supplier: 'TechGifts EU', article: 'TG-PWR-5K', internalPrice: 82 }, { supplier: 'PromoGoods DK', article: 'PG-PWR-5K', internalPrice: 88 }],
};

// Increment 1 — each Standard product's owning supplier + supplier item
// number is the cheapest entry above (product-module-analysis.md §12
// migration rule). The remaining entries stay in `initialSupplierProducts`
// as alternative sourcing (product-module-naming.md §12.2).
const cheapestEntry = (entries) => (
  entries.reduce((min, entry) => (entry.internalPrice < min.internalPrice ? entry : min), entries[0])
);

const STANDARD_PRODUCTS = PRODUCT_BASE.map((product) => {
  const owner = cheapestEntry(LEGACY_PRODUCT_SUPPLIER_PRICES[product.id]);
  const ownerSupplier = initialSuppliers.find((supplier) => supplier.name === owner.supplier);
  return {
    ...product,
    productType: 'Standard',
    supplierId: ownerSupplier?.id ?? null,
    supplierItemNumber: owner.article,
    internalNumber: null,
    productFamily: PRODUCT_FAMILY_BY_ID[product.id] || '',
    copiedFromProductId: null,
    countryOfOrigin: ownerSupplier?.country ?? null,
    packaging: CATEGORY_PACKAGING_DEFAULTS[product.categoryId] || null,
    specifications: null,
    materials: null,
    productionNotes: null,
    primaryImageId: product.id,
    createdBy: 'Lars Nielsen',
    createdAt: '2024-01-15 09:00',
  };
});

// Colour-variant Standard products — one copy per kept family, demonstrating
// Copy Product's colour-variation use case (product-module-analysis.md §8.2:
// "Open AYA Bottle Black → Copy → change item number and colour → AYA Bottle
// Pink exists in seconds"). Same owning supplier as the source by default,
// new supplier item number + colour, no entry needed in the legacy per-
// supplier price map (that map only backs the original 5 for the
// SupplierComparisonDialog demo described above it).
const STANDARD_PRODUCT_VARIANTS = [
  {
    id: 25, code: 'BAG-TOT-NVY', name: 'Bags', categoryId: 1,
    productType: 'Standard', supplierId: 1, supplierItemNumber: 'NT-BAG-TOT-NVY',
    internalNumber: null, productFamily: 'Tote Bag', copiedFromProductId: 3,
    colour: 'Navy', description: 'Reusable shopping bags with custom print',
    material: '100% cotton canvas, 280 gsm', countryOfOrigin: 'Denmark',
    packaging: CATEGORY_PACKAGING_DEFAULTS[1], specifications: null, materials: null,
    productionNotes: null, primaryImageId: 25, createdBy: 'Lars Nielsen',
    createdAt: '2026-01-10 09:00', status: 'Active',
  },
  {
    id: 26, code: 'MUG-CER-BLU', name: 'Mugs', categoryId: 2,
    productType: 'Standard', supplierId: 3, supplierItemNumber: 'CW-MUG-330-BLU',
    internalNumber: null, productFamily: 'Coffee Mug', copiedFromProductId: 4,
    colour: 'Blue', description: 'Ceramic mugs with logo printing',
    material: 'Ceramic', countryOfOrigin: 'China',
    packaging: CATEGORY_PACKAGING_DEFAULTS[2], specifications: null, materials: null,
    productionNotes: null, primaryImageId: 26, createdBy: 'Lars Nielsen',
    createdAt: '2026-01-12 09:00', status: 'Active',
  },
  {
    id: 27, code: 'BOT-STL-BLK', name: 'Bottles', categoryId: 2,
    productType: 'Standard', supplierId: 6, supplierItemNumber: 'MG-BOT-750-BLK',
    internalNumber: null, productFamily: 'Water Bottle', copiedFromProductId: 8,
    colour: 'Black', description: 'Stainless steel bottles with logo',
    material: 'Stainless steel 304', countryOfOrigin: 'India',
    packaging: CATEGORY_PACKAGING_DEFAULTS[2], specifications: null, materials: null,
    productionNotes: null, primaryImageId: 27, createdBy: 'Lars Nielsen',
    createdAt: '2026-01-14 09:00', status: 'Active',
  },
  {
    id: 28, code: 'UMB-STD-NVY', name: 'Umbrellas', categoryId: 8,
    productType: 'Standard', supplierId: 1, supplierItemNumber: 'NT-UMB-STD-NVY',
    internalNumber: null, productFamily: 'Umbrella', copiedFromProductId: 12,
    colour: 'Navy', description: 'Full-size umbrellas with logo print',
    material: 'Pongee fabric, steel frame', countryOfOrigin: 'Denmark',
    packaging: CATEGORY_PACKAGING_DEFAULTS[8], specifications: null, materials: null,
    productionNotes: null, primaryImageId: 28, createdBy: 'Lars Nielsen',
    createdAt: '2026-01-16 09:00', status: 'Active',
  },
  {
    id: 29, code: 'PWR-5K-WHT', name: 'Power Banks', categoryId: 6,
    productType: 'Standard', supplierId: 4, supplierItemNumber: 'TG-PWR-5K-WHT',
    internalNumber: null, productFamily: 'Power Bank', copiedFromProductId: 16,
    colour: 'White', description: '5000mAh portable chargers with logo',
    material: 'ABS plastic', countryOfOrigin: 'Germany',
    packaging: CATEGORY_PACKAGING_DEFAULTS[6], specifications: null, materials: null,
    productionNotes: null, primaryImageId: 29, createdBy: 'Lars Nielsen',
    createdAt: '2026-01-18 09:00', status: 'Active',
  },
];

// Same generic product, offered by a different supplier as its own catalog
// entry — distinct from STANDARD_PRODUCT_VARIANTS above (colour variants
// from the SAME supplier). Each supplierItemNumber here matches that
// supplier's existing Supplier_Product record for the product (see
// generatedSupplierProducts / EXTRA_SUPPLIER_PRODUCTS below), so the
// Products Management catalog and the Selection_Wizard's Products step stay
// consistent with each other.
const STANDARD_PRODUCT_ALT_SUPPLIERS = [
  {
    id: 31, code: 'BAG-TOT-PP', name: 'Bags', categoryId: 1,
    productType: 'Standard', supplierId: 2, supplierItemNumber: 'PP-TOTE-420',
    internalNumber: null, productFamily: 'Tote Bag', copiedFromProductId: null,
    colour: 'Natural', description: 'Reusable shopping bags with custom print',
    material: '100% cotton canvas, 280 gsm', countryOfOrigin: 'Denmark',
    packaging: CATEGORY_PACKAGING_DEFAULTS[1], specifications: null, materials: null,
    productionNotes: null, primaryImageId: 31, createdBy: 'Lars Nielsen',
    createdAt: '2026-03-01 09:00', status: 'Active',
  },
  {
    id: 32, code: 'MUG-CER-PG', name: 'Mugs', categoryId: 2,
    productType: 'Standard', supplierId: 8, supplierItemNumber: 'PG-MUG-WHT',
    internalNumber: null, productFamily: 'Coffee Mug', copiedFromProductId: null,
    colour: 'White', description: 'Ceramic mugs with logo printing',
    material: 'Ceramic', countryOfOrigin: 'India',
    packaging: CATEGORY_PACKAGING_DEFAULTS[2], specifications: null, materials: null,
    productionNotes: null, primaryImageId: 32, createdBy: 'Lars Nielsen',
    createdAt: '2026-03-02 09:00', status: 'Active',
  },
  {
    id: 33, code: 'BOT-STL-TG', name: 'Bottles', categoryId: 2,
    productType: 'Standard', supplierId: 4, supplierItemNumber: 'TG-BOT-STL',
    internalNumber: null, productFamily: 'Water Bottle', copiedFromProductId: null,
    colour: 'Silver', description: 'Stainless steel bottles with logo',
    material: 'Stainless steel 304', countryOfOrigin: 'Germany',
    packaging: CATEGORY_PACKAGING_DEFAULTS[2], specifications: null, materials: null,
    productionNotes: null, primaryImageId: 33, createdBy: 'Lars Nielsen',
    createdAt: '2026-03-03 09:00', status: 'Active',
  },
  {
    id: 34, code: 'PWR-5K-PG', name: 'Power Banks', categoryId: 6,
    productType: 'Standard', supplierId: 8, supplierItemNumber: 'PG-PWR-5K',
    internalNumber: null, productFamily: 'Power Bank', copiedFromProductId: null,
    colour: 'Black', description: '5000mAh portable chargers with logo',
    material: 'ABS plastic', countryOfOrigin: 'India',
    packaging: CATEGORY_PACKAGING_DEFAULTS[6], specifications: null, materials: null,
    productionNotes: null, primaryImageId: 34, createdBy: 'Lars Nielsen',
    createdAt: '2026-03-04 09:00', status: 'Active',
  },
  {
    id: 35, code: 'BAG-TOT-BPP', name: 'Bags', categoryId: 1,
    productType: 'Standard', supplierId: 13, supplierItemNumber: 'BPP-BAG-TOT',
    internalNumber: null, productFamily: 'Tote Bag', copiedFromProductId: null,
    colour: 'Natural', description: 'Cotton canvas tote bags, EU production',
    material: '100% cotton canvas, 280 gsm', countryOfOrigin: 'Poland',
    packaging: CATEGORY_PACKAGING_DEFAULTS[1], specifications: null, materials: null,
    productionNotes: null, primaryImageId: 35, createdBy: 'Lars Nielsen',
    createdAt: '2026-03-05 09:00', status: 'Active',
  },
  {
    id: 36, code: 'BOT-STL-SCM', name: 'Bottles', categoryId: 2,
    productType: 'Standard', supplierId: 14, supplierItemNumber: 'SCM-BOT-750',
    internalNumber: null, productFamily: 'Water Bottle', copiedFromProductId: null,
    colour: 'Silver', description: 'Stainless steel bottles, high-volume production',
    material: 'Stainless steel 304', countryOfOrigin: 'Vietnam',
    packaging: CATEGORY_PACKAGING_DEFAULTS[2], specifications: null, materials: null,
    productionNotes: null, primaryImageId: 36, createdBy: 'Lars Nielsen',
    createdAt: '2026-03-06 09:00', status: 'Active',
  },
  {
    id: 37, code: 'PWR-5K-AGT', name: 'Power Banks', categoryId: 6,
    productType: 'Standard', supplierId: 16, supplierItemNumber: 'AGT-PWR-5K',
    internalNumber: null, productFamily: 'Power Bank', copiedFromProductId: null,
    colour: 'White', description: 'European-assembled power banks',
    material: 'ABS plastic', countryOfOrigin: 'Netherlands',
    packaging: CATEGORY_PACKAGING_DEFAULTS[6], specifications: null, materials: null,
    productionNotes: null, primaryImageId: 37, createdBy: 'Lars Nielsen',
    createdAt: '2026-03-07 09:00', status: 'Active',
  },
  {
    id: 38, code: 'MUG-CER-ONS', name: 'Mugs', categoryId: 2,
    productType: 'Standard', supplierId: 18, supplierItemNumber: 'ONS-MUG-330',
    internalNumber: null, productFamily: 'Coffee Mug', copiedFromProductId: null,
    colour: 'White', description: 'Ceramic mugs, Nordic quality standard',
    material: 'Ceramic', countryOfOrigin: 'Norway',
    packaging: CATEGORY_PACKAGING_DEFAULTS[2], specifications: null, materials: null,
    productionNotes: null, primaryImageId: 38, createdBy: 'Lars Nielsen',
    createdAt: '2026-03-08 09:00', status: 'Active',
  },
  {
    id: 39, code: 'BAG-TOT-ITW', name: 'Bags', categoryId: 1,
    productType: 'Standard', supplierId: 17, supplierItemNumber: 'ITW-BAG-TOT',
    internalNumber: null, productFamily: 'Tote Bag', copiedFromProductId: null,
    colour: 'Natural', description: 'Heavyweight cotton canvas bags',
    material: '100% cotton canvas, 280 gsm', countryOfOrigin: 'Turkey',
    packaging: CATEGORY_PACKAGING_DEFAULTS[1], specifications: null, materials: null,
    productionNotes: null, primaryImageId: 39, createdBy: 'Lars Nielsen',
    createdAt: '2026-03-09 09:00', status: 'Active',
  },
];

// Custom products (product-module-analysis.md §12) — two copy lineages:
// CTM-000002 is a repeat of CTM-000001 for a different customer (prices/
// artwork/images never copy between customers — see productCopy.js), and
// CTM-000006 is a repeat of CTM-000004 the same way.
const CUSTOM_PRODUCTS = [
  {
    id: 19, code: '', name: 'Custom Football', categoryId: 5, supplierId: 8,
    productType: 'Custom', internalNumber: 'CTM-000001', supplierItemNumber: '',
    productFamily: 'Custom Football', copiedFromProductId: null, colour: 'Green/White',
    description: 'Match-quality promotional football produced for Carlsberg Group.',
    specifications: 'Size 5, hand-stitched, 32-panel construction, butyl bladder.',
    materials: 'PU synthetic leather outer, cotton/polyester lining',
    productionNotes: 'Customer logo printed on all panels; matches Carlsberg brand guidelines on file.',
    packaging: { unitPackaging: 'Individual box', itemsPerCarton: 20, cartonDimensionsCm: '45 x 45 x 45', cartonGrossWeightKg: 9 },
    primaryImageId: 19, createdBy: 'Maria Kristensen', createdAt: '2025-11-20 10:00', status: 'Active',
  },
  {
    id: 20, code: '', name: 'Custom Football', categoryId: 5, supplierId: 8,
    productType: 'Custom', internalNumber: 'CTM-000002', supplierItemNumber: '',
    productFamily: 'Custom Football', copiedFromProductId: 19, colour: 'Red/White',
    description: 'Match-quality promotional football produced for Danish Crown A/S.',
    specifications: 'Size 5, hand-stitched, 32-panel construction, butyl bladder.',
    materials: 'PU synthetic leather outer, cotton/polyester lining',
    productionNotes: 'Customer logo printed on all panels; matches Danish Crown brand guidelines on file.',
    packaging: { unitPackaging: 'Individual box', itemsPerCarton: 20, cartonDimensionsCm: '45 x 45 x 45', cartonGrossWeightKg: 9 },
    primaryImageId: 20, createdBy: 'Maria Kristensen', createdAt: '2026-01-24 11:30', status: 'Active',
  },
  {
    id: 21, code: '', name: 'Custom Tote Bag', categoryId: 1, supplierId: 1,
    productType: 'Custom', internalNumber: 'CTM-000003', supplierItemNumber: '',
    productFamily: '', copiedFromProductId: null, colour: 'Natural with navy print',
    description: 'Retail campaign tote bag produced for Bestseller A/S store launches.',
    specifications: '38 x 42 cm, long handles, gusseted base.',
    materials: '100% cotton canvas, 340 gsm',
    productionNotes: 'Per-store size/quantity breakdown confirmed with customer before production.',
    packaging: { unitPackaging: 'Polybag', itemsPerCarton: 100, cartonDimensionsCm: '55 x 40 x 45', cartonGrossWeightKg: 11 },
    primaryImageId: 21, createdBy: 'Emma Andersen', createdAt: '2026-01-20 09:15', status: 'Active',
  },
  {
    id: 22, code: '', name: 'Custom Plush Toy', categoryId: 4, supplierId: 8,
    productType: 'Custom', internalNumber: 'CTM-000004', supplierItemNumber: '',
    productFamily: 'Custom Plush Toy', copiedFromProductId: null, colour: 'Brown/White',
    description: 'Branded plush cow toy produced for Arla Foods A/S.',
    specifications: '25 cm standing height, embroidered eyes, safety-locked joints.',
    materials: 'Polyester plush fabric, polyester fibre filling',
    productionNotes: 'EN71 toy safety test report required before first shipment.',
    packaging: { unitPackaging: 'Polybag', itemsPerCarton: 150, cartonDimensionsCm: '45 x 35 x 35', cartonGrossWeightKg: 8 },
    primaryImageId: 22, createdBy: 'Lars Nielsen', createdAt: '2025-09-05 13:45', status: 'Active',
  },
  {
    id: 23, code: '', name: 'Custom Umbrella', categoryId: 8, supplierId: 1,
    productType: 'Custom', internalNumber: 'CTM-000005', supplierItemNumber: '',
    productFamily: '', copiedFromProductId: null, colour: 'Black/Yellow',
    description: 'Windproof golf umbrella produced for Rambøll Group A/S engineering teams.',
    specifications: '130 cm diameter, fibreglass ribs, wind-vented canopy.',
    materials: 'Pongee fabric canopy, fibreglass ribs, rubber-coated steel shaft',
    productionNotes: 'Sample requested by customer before committing to full volume.',
    packaging: { unitPackaging: 'Polybag', itemsPerCarton: 25, cartonDimensionsCm: '75 x 15 x 15', cartonGrossWeightKg: 10 },
    primaryImageId: 23, createdBy: 'Mads Mark-Buur', createdAt: '2026-02-01 14:00', status: 'Active',
  },
  {
    id: 24, code: '', name: 'Custom Plush Toy', categoryId: 4, supplierId: 8,
    productType: 'Custom', internalNumber: 'CTM-000006', supplierItemNumber: '',
    productFamily: 'Custom Plush Toy', copiedFromProductId: 22, colour: 'Yellow/Black',
    description: 'Branded plush mascot toy produced for Netto Supermarked store campaigns.',
    specifications: '25 cm standing height, embroidered eyes, safety-locked joints.',
    materials: 'Polyester plush fabric, polyester fibre filling',
    productionNotes: 'Repeat autumn campaign order; keep pricing on file for next re-order.',
    packaging: { unitPackaging: 'Polybag', itemsPerCarton: 150, cartonDimensionsCm: '45 x 35 x 35', cartonGrossWeightKg: 8 },
    primaryImageId: 24, createdBy: 'Mads Mark-Buur', createdAt: '2025-11-10 10:20', status: 'Active',
  },
];

export const initialProducts = [...STANDARD_PRODUCTS, ...STANDARD_PRODUCT_VARIANTS, ...STANDARD_PRODUCT_ALT_SUPPLIERS, ...CUSTOM_PRODUCTS];

// Production Notes tab (Product Details) — a running, dated/authored note
// thread per product, same shape/UX as the Comments feature elsewhere
// (CommentsModal.js) but scoped to `productId` instead of `clientId`/
// `entityId`, since Product ids share the same small numeric range as
// Supplier/Lead ids and would otherwise collide in a shared comments pool.
export const initialProductionNotes = [
  { id: 1, productId: 19, author: 'Maria Kristensen', authorId: 4, note: 'Customer logo printed on all panels; matches Carlsberg brand guidelines on file.', timestamp: '2025-11-20 10:00' },
  { id: 2, productId: 20, author: 'Maria Kristensen', authorId: 4, note: 'Customer logo printed on all panels; matches Danish Crown brand guidelines on file.', timestamp: '2026-01-24 11:30' },
  { id: 3, productId: 21, author: 'Emma Andersen', authorId: 2, note: 'Per-store size/quantity breakdown confirmed with customer before production.', timestamp: '2026-01-20 09:15' },
  { id: 4, productId: 22, author: 'Lars Nielsen', authorId: 5, note: 'EN71 toy safety test report required before first shipment.', timestamp: '2025-09-05 13:45' },
  { id: 5, productId: 23, author: 'Mads Mark-Buur', authorId: 1, note: 'Sample requested by customer before committing to full volume.', timestamp: '2026-02-01 14:00' },
  { id: 6, productId: 24, author: 'Mads Mark-Buur', authorId: 1, note: 'Repeat autumn campaign order; keep pricing on file for next re-order.', timestamp: '2025-11-10 10:20' },
];

// Branding and delivery specialists each quote the same 7 actively-used
// products (see comment above), so the Branding and Delivery roles in
// SupplierComparisonDialog also have 2 real options to compare, not just 1.
const BRANDING_DELIVERY_SPECIALIST_PRICES = {
  3: { branding: [4, 4.5], delivery: [0.05, 0.045] },
  4: { branding: [3.8, 4.1], delivery: [0.045, 0.042] },
  8: { branding: [5.2, 5.6], delivery: [0.055, 0.05] },
  12: { branding: [6.5, 6.9], delivery: [0.065, 0.06] },
  16: { branding: [7, 7.4], delivery: [0.07, 0.066] },
};

const generatedSupplierProducts = Object.entries(LEGACY_PRODUCT_SUPPLIER_PRICES).flatMap(
  ([productId, entries]) => {
    const product = initialProducts.find((item) => item.id === Number(productId));
    return entries.map((entry) => ({
      supplierId: initialSuppliers.find((supplier) => supplier.name === entry.supplier)?.id,
      productId: product.id,
      categoryId: product.categoryId,
      productName: product.name,
      supplierItemNumber: entry.article,
      description: '',
      samplePhotoUrl: null,
      avgItemPrice: entry.internalPrice,
      avgProductionPrice: entry.productionPrice ?? null,
      avgBrandingPrice: null,
      avgDeliveryPricePerItemPerKm: null,
      status: 'Active',
    }));
  },
);

const generatedBrandingDeliveryProducts = Object.entries(BRANDING_DELIVERY_SPECIALIST_PRICES).flatMap(
  ([productId, prices]) => {
    const product = initialProducts.find((item) => item.id === Number(productId));
    const shared = {
      productId: product.id,
      categoryId: product.categoryId,
      productName: product.name,
      supplierItemNumber: '',
      samplePhotoUrl: null,
      avgItemPrice: null,
      avgProductionPrice: null,
      status: 'Active',
    };
    return [
      { ...shared, supplierId: 9, description: 'Embroidery/branding finishing', avgBrandingPrice: prices.branding[0], avgDeliveryPricePerItemPerKm: null },
      { ...shared, supplierId: 11, description: 'Branding finishing', avgBrandingPrice: prices.branding[1], avgDeliveryPricePerItemPerKm: null },
      { ...shared, supplierId: 10, description: 'Palletised delivery', avgBrandingPrice: null, avgDeliveryPricePerItemPerKm: prices.delivery[0] },
      { ...shared, supplierId: 12, description: 'Standard delivery', avgBrandingPrice: null, avgDeliveryPricePerItemPerKm: prices.delivery[1] },
    ];
  },
);

// Extra Supplier_Product records (inquiry-module-redesign): (a) give the two
// previously-untyped suppliers (5, 7) at least one connected record so
// neither shows "—" in the derived Type column, and (b) seed several new
// suppliers from additional countries holding two or three roles
// simultaneously, demonstrating derivedSupplierTypes returning multiple
// tags for one supplier.
const EXTRA_SUPPLIER_PRODUCTS = [
  // 5 — PaperCraft ApS (Denmark): Manufacturing only (was untyped)
  {
    supplierId: 5, productId: 3, categoryId: 1, productName: 'Bags',
    supplierItemNumber: 'PC-BAG-TOT', description: 'Kraft-paper reinforced tote bags',
    avgItemPrice: 26, avgProductionPrice: 3.2, avgBrandingPrice: null, avgDeliveryPricePerItemPerKm: null, status: 'Active',
  },
  // 7 — EventSupply Co. (Denmark): Logistics only (was untyped)
  {
    supplierId: 7, productId: 12, categoryId: 8, productName: 'Umbrellas',
    supplierItemNumber: '', description: 'Event-day handling and on-site distribution',
    avgItemPrice: null, avgProductionPrice: null, avgBrandingPrice: null, avgDeliveryPricePerItemPerKm: 0.06, status: 'Active',
  },

  // 13 — Baltic Pack Polska (Poland): Manufacturing + Services
  {
    supplierId: 13, productId: 3, categoryId: 1, productName: 'Bags',
    supplierItemNumber: 'BPP-BAG-TOT', description: 'Cotton canvas tote bags, EU production',
    avgItemPrice: 24, avgProductionPrice: 3, avgBrandingPrice: null, avgDeliveryPricePerItemPerKm: null, status: 'Active',
  },
  {
    supplierId: 13, productId: 3, categoryId: 1, productName: 'Bags',
    supplierItemNumber: '', description: 'Screen printing on cotton bags',
    avgItemPrice: null, avgProductionPrice: null, avgBrandingPrice: 3.6, avgDeliveryPricePerItemPerKm: null, status: 'Active',
  },

  // 14 — Saigon Craft Manufacturing (Vietnam): Manufacturing + Logistics
  {
    supplierId: 14, productId: 8, categoryId: 2, productName: 'Bottles',
    supplierItemNumber: 'SCM-BOT-750', description: 'Stainless steel bottles, high-volume production',
    avgItemPrice: 27, avgProductionPrice: 3.8, avgBrandingPrice: null, avgDeliveryPricePerItemPerKm: null, status: 'Active',
  },
  {
    supplierId: 14, productId: 8, categoryId: 2, productName: 'Bottles',
    supplierItemNumber: '', description: 'Consolidated sea-freight delivery',
    avgItemPrice: null, avgProductionPrice: null, avgBrandingPrice: null, avgDeliveryPricePerItemPerKm: 0.03, status: 'Active',
  },

  // 15 — Milano Branding Studio (Italy): Services + Logistics
  {
    supplierId: 15, productId: 12, categoryId: 8, productName: 'Umbrellas',
    supplierItemNumber: '', description: 'Premium silk-screen branding',
    avgItemPrice: null, avgProductionPrice: null, avgBrandingPrice: 6.2, avgDeliveryPricePerItemPerKm: null, status: 'Active',
  },
  {
    supplierId: 15, productId: 12, categoryId: 8, productName: 'Umbrellas',
    supplierItemNumber: '', description: 'Regional courier delivery',
    avgItemPrice: null, avgProductionPrice: null, avgBrandingPrice: null, avgDeliveryPricePerItemPerKm: 0.07, status: 'Active',
  },

  // 16 — Amsterdam Global Trade (Netherlands): Manufacturing + Services + Logistics
  {
    supplierId: 16, productId: 16, categoryId: 6, productName: 'Power Banks',
    supplierItemNumber: 'AGT-PWR-5K', description: 'European-assembled power banks',
    avgItemPrice: 79, avgProductionPrice: 9, avgBrandingPrice: null, avgDeliveryPricePerItemPerKm: null, status: 'Active',
  },
  {
    supplierId: 16, productId: 16, categoryId: 6, productName: 'Power Banks',
    supplierItemNumber: '', description: 'UV printing on ABS casing',
    avgItemPrice: null, avgProductionPrice: null, avgBrandingPrice: 5.4, avgDeliveryPricePerItemPerKm: null, status: 'Active',
  },
  {
    supplierId: 16, productId: 16, categoryId: 6, productName: 'Power Banks',
    supplierItemNumber: '', description: 'EU-wide distribution network',
    avgItemPrice: null, avgProductionPrice: null, avgBrandingPrice: null, avgDeliveryPricePerItemPerKm: 0.045, status: 'Active',
  },

  // 17 — Istanbul Textile Works (Turkey): Manufacturing + Services + Logistics
  {
    supplierId: 17, productId: 3, categoryId: 1, productName: 'Bags',
    supplierItemNumber: 'ITW-BAG-TOT', description: 'Heavyweight cotton canvas bags',
    avgItemPrice: 25, avgProductionPrice: 3.1, avgBrandingPrice: null, avgDeliveryPricePerItemPerKm: null, status: 'Active',
  },
  {
    supplierId: 17, productId: 3, categoryId: 1, productName: 'Bags',
    supplierItemNumber: '', description: 'Embroidered branding',
    avgItemPrice: null, avgProductionPrice: null, avgBrandingPrice: 4.4, avgDeliveryPricePerItemPerKm: null, status: 'Active',
  },
  {
    supplierId: 17, productId: 3, categoryId: 1, productName: 'Bags',
    supplierItemNumber: '', description: 'Overland freight to EU hubs',
    avgItemPrice: null, avgProductionPrice: null, avgBrandingPrice: null, avgDeliveryPricePerItemPerKm: 0.035, status: 'Active',
  },

  // 18 — Oslo Nordic Supplies (Norway): Manufacturing + Logistics
  {
    supplierId: 18, productId: 4, categoryId: 2, productName: 'Mugs',
    supplierItemNumber: 'ONS-MUG-330', description: 'Ceramic mugs, Nordic quality standard',
    avgItemPrice: 23, avgProductionPrice: 2.6, avgBrandingPrice: null, avgDeliveryPricePerItemPerKm: null, status: 'Active',
  },
  {
    supplierId: 18, productId: 4, categoryId: 2, productName: 'Mugs',
    supplierItemNumber: '', description: 'Nordic regional delivery',
    avgItemPrice: null, avgProductionPrice: null, avgBrandingPrice: null, avgDeliveryPricePerItemPerKm: 0.08, status: 'Active',
  },

  // Manufacturing rows for three of the existing Services/Logistics catalog
  // owners (initialServiceItems/initialLogisticsItems below) — so the
  // Selection Wizard's "connected supplier" badge (Requirements 6.4/7.3) has
  // more than the single PrintPartner ApS/Bags case to demonstrate, and so a
  // Logistics connection is achievable at all (10/12 are the only two
  // suppliers who own a Logistics catalog item).
  {
    supplierId: 9, productId: 4, categoryId: 2, productName: 'Mugs',
    supplierItemNumber: 'BES-MUG-330', description: 'Ceramic mugs, embossed finish house line',
    avgItemPrice: 22, avgProductionPrice: 2.5, avgBrandingPrice: null, avgDeliveryPricePerItemPerKm: null, status: 'Active',
  },
  {
    supplierId: 11, productId: 16, categoryId: 6, productName: 'Power Banks',
    supplierItemNumber: 'CCD-PWR-5K', description: 'Shenzhen-assembled power banks, in-house decoration',
    avgItemPrice: 21, avgProductionPrice: 2.3, avgBrandingPrice: null, avgDeliveryPricePerItemPerKm: null, status: 'Active',
  },
  {
    supplierId: 12, productId: 8, categoryId: 2, productName: 'Bottles',
    supplierItemNumber: 'SCE-BOT-750', description: 'Stainless steel bottles, direct-ship merchant line',
    avgItemPrice: 28, avgProductionPrice: 3.5, avgBrandingPrice: null, avgDeliveryPricePerItemPerKm: null, status: 'Active',
  },

  // Category 3 (Textiles) had zero catalog coverage, but several legacy
  // Inquiry_Items already at 'Quote Created' reference it — that status
  // only exists once Products, Services, and Logistics have all been
  // selected, so the category needs all three roles available to migrate
  // those items into a complete wizardResult (see migrateLegacyInquiryItem).
  {
    supplierId: 1, productId: 30, categoryId: 3, productName: 'Apparel',
    supplierItemNumber: 'NT-APL-STD', description: 'Custom-printed textile apparel',
    avgItemPrice: 32, avgProductionPrice: 4, avgBrandingPrice: null, avgDeliveryPricePerItemPerKm: null, status: 'Active',
  },
  {
    supplierId: 9, productId: 30, categoryId: 3, productName: 'Apparel',
    supplierItemNumber: '', description: 'Embroidered branding on apparel',
    avgItemPrice: null, avgProductionPrice: null, avgBrandingPrice: 4.8, avgDeliveryPricePerItemPerKm: null, status: 'Active',
  },
  {
    supplierId: 10, productId: 30, categoryId: 3, productName: 'Apparel',
    supplierItemNumber: '', description: 'Standard delivery',
    avgItemPrice: null, avgProductionPrice: null, avgBrandingPrice: null, avgDeliveryPricePerItemPerKm: 0.055, status: 'Active',
  },
];

export const initialSupplierProducts = [
  ...generatedSupplierProducts,
  ...generatedBrandingDeliveryProducts,
  ...EXTRA_SUPPLIER_PRODUCTS,
].map((item, index) => ({
  id: index + 1,
  ...item,
  description: item.description || `${item.productName} supplied to the agreed quality specification.`,
  samplePhotoUrl: item.samplePhotoUrl || `https://picsum.photos/seed/supplier-product-${index + 1}/640/420`,
}));

// Product-scoped documents (product-module-analysis.md §6.4) — one primary
// image seeded per existing product so the Documents tab isn't empty; further
// entries (datasheets, branding templates, artwork, inherited/unverified
// compliance-adjacent examples) are added alongside the Custom product seed
// data below. Standard products (the 5 named families) get a thumbnail that
// actually matches the product type + colour (see utils/productImages.js);
// Custom products keep a generic seeded stock photo since they aren't one of
// the customer's 5 named examples.
export const initialProductDocuments = initialProducts.map((product) => ({
  id: product.id,
  productId: product.id,
  documentType: 'Image',
  name: `${product.name} — front view`,
  fileName: `${product.code || product.id}_front.jpg`,
  fileUrl: buildProductFamilyThumbnail(product.productFamily, product.colour)
    || `https://picsum.photos/seed/product-${product.id}/640/480`,
  isPrimaryImage: true,
  inherited: false,
  uploadedBy: product.createdBy,
  uploadDate: product.createdAt,
}));

export const initialContactPersons = [
  { id: 1, entityId: 'L001', name: 'Lars Jensen', position: 'Marketing Manager', email: 'lars.jensen@novonordisk.com', phone: '+45 44 44 88 88' },
  { id: 2, entityId: 'C002', name: 'Maria Nielsen', position: 'Brand Coordinator', email: 'maria.nielsen@carlsberg.com', phone: '+45 33 27 33 27' },
  { id: 3, entityId: 'L003', name: 'Anders Petersen', position: 'Procurement Lead', email: 'anders.p@tdc.dk', phone: '+45 33 63 33 63' },
  { id: 4, entityId: 'C004', name: 'Mette Hansen', position: 'Purchasing Manager', email: 'mette.hansen@danishcrown.com', phone: '+45 89 19 19 19' },
  { id: 5, entityId: 'C006', name: 'Anne Rasmussen', position: 'Office Manager', email: 'anne.rasmussen@arlafoods.dk', phone: '+45 89 38 10 00' },
  { id: 6, entityId: 1, name: 'Signe Holm', position: 'Account Manager', email: 'info@nordictextiles.dk', phone: '+45 86 12 34 56' },
  { id: 7, entityId: 2, name: 'Jonas Vester', position: 'Production Coordinator', email: 'orders@printpartner.dk', phone: '+45 35 12 00 00' },
  { id: 8, entityId: 3, name: 'Nina Frandsen', position: 'Sales Representative', email: 'sales@ceramicworks.dk', phone: '+45 66 13 14 15' },
];

export const initialComments = [
  { id: 1, clientId: 'L001', clientName: 'Novo Nordisk A/S', comment: 'Very interested in long-term partnership. Decision maker on vacation until Feb 15.', author: 'Mads Mark-Buur', authorId: 1, timestamp: '2026-02-03 14:30' },
  { id: 2, clientId: 'C002', clientName: 'Carlsberg Group', comment: 'Excellent customer. Always pays on time. Looking to expand order volume next quarter.', author: 'Mads Mark-Buur', authorId: 1, timestamp: '2026-02-01 09:15' },
  { id: 3, clientId: 'C002', clientName: 'Carlsberg Group', comment: 'Requested samples for new product line. Sent via courier today.', author: 'Peter Sørensen', authorId: 3, timestamp: '2026-01-28 16:45' },
  { id: 4, clientId: 'L003', clientName: 'TDC NET A/S', comment: 'Need to follow up on quote. Contact mentioned budget approval process takes 2-3 weeks.', author: 'Mads Mark-Buur', authorId: 1, timestamp: '2026-01-30 11:20' },
  { id: 5, clientId: 'C004', clientName: 'Danish Crown A/S', comment: 'Large order in progress. Customer requested expedited delivery - confirmed possible.', author: 'Maria Kristensen', authorId: 4, timestamp: '2026-01-29 10:00' },
  { id: 6, clientId: 'L005', clientName: 'Danske Bank A/S', comment: 'High potential client. Very specific requirements. Schedule presentation meeting for next week.', author: 'Mads Mark-Buur', authorId: 1, timestamp: '2026-01-27 13:45' },
  { id: 7, clientId: 'C006', clientName: 'Arla Foods A/S', comment: 'Loyal customer since 2023. Always professional and easy to work with.', author: 'Lars Nielsen', authorId: 5, timestamp: '2026-01-26 15:30' },
  { id: 8, clientId: 1, clientName: 'Nordic Textiles A/S', comment: 'Reliable supplier with clear production updates and good embroidery quality.', author: 'Emma Andersen', authorId: 2, timestamp: '2026-02-02 10:10' },
  { id: 9, clientId: 2, clientName: 'PrintPartner ApS', comment: 'Strong on urgent proofs and fast turnarounds for custom printed accessories.', author: 'Mads Mark-Buur', authorId: 1, timestamp: '2026-01-31 15:05' },
  { id: 15, clientId: 3, clientName: 'CeramicWorks DK', comment: 'Good glaze quality but slower turnaround on custom colours — build in extra buffer for seasonal artwork changes.', author: 'Lars Nielsen', authorId: 5, timestamp: '2026-01-22 09:50' },
  { id: 16, clientId: 4, clientName: 'TechGifts EU', comment: 'Consistent on tech items, but lead times stretch during Chinese New Year — order USB/power bank stock early in Q1.', author: 'Maria Kristensen', authorId: 4, timestamp: '2026-01-18 14:20' },
  { id: 17, clientId: 5, clientName: 'PaperCraft ApS', comment: 'Reliable on paper stock and debossing quality — good fit for the recurring CBS notebook reorder.', author: 'Mads Mark-Buur', authorId: 1, timestamp: '2026-01-15 11:05' },
  { id: 18, clientId: 6, clientName: 'MetalGoods Scandinavia', comment: 'Solid on steel bottles, but minimum order quantities are high — best for larger campaigns only.', author: 'Lars Nielsen', authorId: 5, timestamp: '2026-01-10 16:30' },
  { id: 19, clientId: 7, clientName: 'EventSupply Co.', comment: 'Fast turnaround on lanyards and pens, useful as a backup when PrintPartner is fully booked.', author: 'Maria Kristensen', authorId: 4, timestamp: '2026-01-08 13:40' },
  { id: 20, clientId: 8, clientName: 'PromoGoods DK', comment: 'New supplier for safety vests — compliance certificate still outstanding, do not confirm with the customer until it is in hand.', author: 'Maria Kristensen', authorId: 4, timestamp: '2026-02-04 09:45' },
  { id: 21, clientId: 9, clientName: 'BrandEmboss Studio', comment: 'Specialist embroidery/branding finisher — good as a second branding quote alongside the garment supplier’s in-house option.', author: 'Lars Nielsen', authorId: 5, timestamp: '2026-01-19 10:15' },
  { id: 22, clientId: 10, clientName: 'NordicFreight Logistics', comment: 'Dependable for palletised textile deliveries, slightly pricier per km than SwiftCargo but better on time-critical jobs.', author: 'Lars Nielsen', authorId: 5, timestamp: '2026-01-19 10:25' },
  { id: 23, clientId: 11, clientName: 'ColorCraft Decoration', comment: 'Newer branding partner, competitive pricing on small-format items like pens and totes — still building a track record.', author: 'Maria Kristensen', authorId: 4, timestamp: '2026-01-21 12:00' },
  { id: 24, clientId: 12, clientName: 'SwiftCargo Express', comment: 'Cheaper per-km than NordicFreight on smaller item runs, good as the default comparison quote.', author: 'Maria Kristensen', authorId: 4, timestamp: '2026-01-21 12:05' },
  { id: 25, clientId: 'L007', clientName: 'Rambøll Group A/S', comment: 'First offer sent for review — engineering team wants samples before committing to volume.', author: 'Mads Mark-Buur', authorId: 1, timestamp: '2026-02-01 14:10' },
  { id: 26, clientId: 'L008', clientName: 'Bestseller A/S', comment: 'Retail client, very size-breakdown sensitive — confirm per-store splits before every quote.', author: 'Emma Andersen', authorId: 2, timestamp: '2026-01-26 17:00' },
  { id: 27, clientId: 'C009', clientName: 'Netto Supermarked', comment: 'Repeat customer for store campaign merchandise — re-order likely each autumn, keep pricing on file.', author: 'Mads Mark-Buur', authorId: 1, timestamp: '2025-11-06 09:30' },
  { id: 28, clientId: 'L010', clientName: 'Dansk Supermarked A/S', comment: 'Introductory call held, interested in a pilot order for one store chain before committing group-wide.', author: 'Mads Mark-Buur', authorId: 1, timestamp: '2026-01-19 10:45' },
  { id: 29, clientId: 'L011', clientName: 'Copenhagen Business School', comment: 'Recurring annual order around campus welcome week — treat as a priority reorder each January.', author: 'Mads Mark-Buur', authorId: 1, timestamp: '2026-01-12 11:00' },
  { id: 30, clientId: 'L012', clientName: 'Saxo Bank A/S', comment: 'VIP gift concept still being finalised internally — do not expect a decision before management sign-off.', author: 'Emma Andersen', authorId: 2, timestamp: '2026-01-06 15:20' },
  { id: 10, clientId: 'INQ-L001-20260205', entityId: 'INQ-L001-20260205', clientName: 'INQ-L001-20260205', comment: 'Customer approved the quotation structure and asked Sales to prepare the final offer.', author: 'Mads Mark-Buur', authorId: 1, timestamp: '2026-02-06 10:20' },
  { id: 11, clientId: 'INQ-C002-20260207', entityId: 'INQ-C002-20260207', clientName: 'INQ-C002-20260207', comment: 'Procurement should confirm rain jacket supplier options before Friday.', author: 'Maria Kristensen', authorId: 4, timestamp: '2026-02-07 15:10' },
  { id: 12, clientId: 'INQ-L008-20260126', entityId: 'INQ-L008-20260126', clientName: 'INQ-L008-20260126', comment: 'Waiting for Bestseller to confirm the final per-store size breakdown before sending to procurement.', author: 'Emma Andersen', authorId: 2, timestamp: '2026-01-26 16:05' },
  { id: 13, clientId: 'INQ-C004-20260128', entityId: 'INQ-C004-20260128', clientName: 'INQ-C004-20260128', comment: 'All three quotations are in and the safety vest sample passed compliance review. Ready to convert to an offer.', author: 'Maria Kristensen', authorId: 4, timestamp: '2026-02-04 09:40' },
  { id: 14, clientId: 'INQ-L011-20260112', entityId: 'INQ-L011-20260112', clientName: 'INQ-L011-20260112', comment: 'Coffee mug pricing confirmed. Still waiting on procurement quotes for umbrellas and tote bags before we can finalise.', author: 'Mads Mark-Buur', authorId: 1, timestamp: '2026-01-20 13:15' },
  { id: 31, clientId: 'QUO-L001-20260205', entityId: 'QUO-L001-20260205', clientName: 'QUO-L001-20260205', comment: 'Both line items quoted and within budget — preparing the customer-facing offer now.', author: 'Mads Mark-Buur', authorId: 1, timestamp: '2026-02-06 09:10' },
  { id: 32, clientId: 'QUO-C002-20260207', entityId: 'QUO-C002-20260207', clientName: 'QUO-C002-20260207', comment: 'Cooler bag and rain jacket pricing requests sent to Nordic Textiles and PrintPartner — waiting on their quotes before this can move to Ready for Sales Quote.', author: 'Peter Sørensen', authorId: 3, timestamp: '2026-02-08 11:30' },
  { id: 33, clientId: 'QUO-C004-20260128', entityId: 'QUO-C004-20260128', clientName: 'QUO-C004-20260128', comment: 'All three lines quoted, including the new safety vest pending PromoGoods’ compliance certificate. Ready to convert once that arrives.', author: 'Maria Kristensen', authorId: 4, timestamp: '2026-02-04 09:50' },
  { id: 34, clientId: 'QUO-L011-20260112', entityId: 'QUO-L011-20260112', clientName: 'QUO-L011-20260112', comment: 'Coffee mug pricing locked with CeramicWorks DK. Still chasing umbrella and tote bag quotes before we can finalise — this is a reorder so margins should match last year’s.', author: 'Mads Mark-Buur', authorId: 1, timestamp: '2026-01-15 09:25' },
  { id: 35, clientId: 'INQ-L005-20260623', entityId: 'INQ-L005-20260623', clientName: 'INQ-L005-20260623', comment: 'Thomas confirmed the gift sets are for the private banking VIP client programme — discretion on branding is critical, no loud logos.', author: 'Mads Mark-Buur', authorId: 1, timestamp: '2026-06-23 09:30' },
  { id: 36, clientId: 'INQ-L005-20260623', entityId: 'INQ-L005-20260623', clientName: 'INQ-L005-20260623', comment: 'Added a power bank line to round out the gift set with a tech item — checking with PrintPartner and TechGifts EU on engraving lead times before sending to procurement.', author: 'Mads Mark-Buur', authorId: 1, timestamp: '2026-06-23 14:05' },
];

export const initialUsers = [
  { id: 1, email: 'mba@profilbureauet.com', login: 'mba@profilbureauet.com', name: 'Mads Mark-Buur', firstName: 'Mads', lastName: 'Mark-Buur', phone: '+45 21 43 65 87', preferredLanguage: 'en', password: 'Sandtoften10!', role: 'admin', active: true },
  { id: 2, email: 'sales@profilbureauet.com', login: 'sales', name: 'Emma Andersen', firstName: 'Emma', lastName: 'Andersen', phone: '+45 31 22 11 44', preferredLanguage: 'en', password: 'Sandtoften10!', role: 'sales manager', active: true },
  { id: 3, email: 'peter.sorensen@profilbureauet.com', login: 'peter.sorensen', name: 'Peter Sørensen', firstName: 'Peter', lastName: 'Sørensen', phone: '+45 28 44 91 22', preferredLanguage: 'da', password: 'Sandtoften10!', role: 'sales user', active: true },
  { id: 4, email: 'maria.kristensen@profilbureauet.com', login: 'maria.kristensen', name: 'Maria Kristensen', firstName: 'Maria', lastName: 'Kristensen', phone: '+45 24 71 55 63', preferredLanguage: 'da', password: 'Sandtoften10!', role: 'procurement manager', active: true },
  { id: 5, email: 'lars.nielsen@profilbureauet.com', login: 'lars.nielsen', name: 'Lars Nielsen', firstName: 'Lars', lastName: 'Nielsen', phone: '+45 29 15 84 10', preferredLanguage: 'en', password: 'Sandtoften10!', role: 'procurement user', active: true },
];

export const initialLeads = [
  { key: '1', id: 'L001', name: 'Novo Nordisk A/S', cvr: '24256790', contact: 'Lars Jensen', email: 'lars.jensen@novonordisk.com', phone: '+45 44 44 88 88', status: 'Lead/Prospect', created: '2026-01-15', address: 'Novo Allé, 2880 Bagsværd' },
  { key: '2', id: 'C002', name: 'Carlsberg Group', cvr: '61056416', contact: 'Maria Nielsen', email: 'maria.nielsen@carlsberg.com', phone: '+45 33 27 33 27', status: 'Customer', created: '2025-12-20', address: 'Ny Carlsberg Vej 100, 1799 Copenhagen' },
  { key: '3', id: 'L003', name: 'TDC NET A/S', cvr: '22756214', contact: 'Anders Petersen', email: 'anders.p@tdc.dk', phone: '+45 33 63 33 63', status: 'Lead/Prospect', created: '2026-01-28', address: 'Teglholmsgade 1, 2450 Copenhagen' },
  { key: '4', id: 'C004', name: 'Danish Crown A/S', cvr: '25313763', contact: 'Mette Hansen', email: 'mette.hansen@danishcrown.com', phone: '+45 89 19 19 19', status: 'Customer', created: '2025-11-10', address: 'Marsvej 43, 8960 Randers' },
  { key: '5', id: 'L005', name: 'Danske Bank A/S', cvr: '61126228', contact: 'Thomas Christiansen', email: 'thomas.c@danskebank.dk', phone: '+45 33 44 00 00', status: 'Lead/Prospect', created: '2026-01-20', address: 'Holmens Kanal 2-12, 1092 Copenhagen' },
  { key: '6', id: 'C006', name: 'Arla Foods A/S', cvr: '54562312', contact: 'Anne Rasmussen', email: 'anne.rasmussen@arlafoods.dk', phone: '+45 89 38 10 00', status: 'Customer', created: '2025-10-15', address: 'Sønderhøj 14, 8260 Viby J' },
  { key: '7', id: 'L007', name: 'Rambøll Group A/S', cvr: '58233528', contact: 'Henrik Sørensen', email: 'henrik.sorensen@ramboll.dk', phone: '+45 51 61 10 00', status: 'Lead/Prospect', created: '2026-02-01', address: 'Hannemanns Allé 53, 2300 Copenhagen' },
  { key: '8', id: 'L008', name: 'Bestseller A/S', cvr: '36213728', contact: 'Sofie Larsen', email: 'sofie.larsen@bestseller.com', phone: '+45 99 42 76 00', status: 'Lead/Prospect', created: '2026-01-25', address: 'Brændkjærsvej 2, 7100 Vejle' },
  { key: '9', id: 'C009', name: 'Netto Supermarked', cvr: '69749917', contact: 'Peter Andersen', email: 'peter.andersen@netto.dk', phone: '+45 43 86 43 86', status: 'Customer', created: '2025-09-20', address: 'Industrigrenen 1B, 2635 Ishøj' },
  { key: '10', id: 'L010', name: 'Dansk Supermarked A/S', cvr: '20016175', contact: 'Camilla Berg', email: 'camilla.berg@dansksupermarked.dk', phone: '+45 46 30 88 88', status: 'Lead/Prospect', created: '2026-01-18', address: 'Roskildevej 65, 2620 Albertslund' },
  { key: '11', id: 'L011', name: 'Copenhagen Business School', cvr: '29979812', contact: 'Michael Thomsen', email: 'michael.thomsen@cbs.dk', phone: '+45 38 15 30 30', status: 'Lead/Prospect', created: '2026-01-10', address: 'Solbjerg Plads 3, 2000 Frederiksberg' },
  { key: '12', id: 'L012', name: 'Saxo Bank A/S', cvr: '15731249', contact: 'Julie Andersen', email: 'julie.andersen@saxobank.com', phone: '+45 39 77 40 00', status: 'Lead/Prospect', created: '2026-01-05', address: 'Philip Heymans Allé 15, 2900 Hellerup' },
];

export const initialContacts = [
  { id: 1, client: 'Novo Nordisk A/S', clientId: 'L001', date: '2026-02-01', type: 'Phone', subject: 'Initial inquiry about branded conference materials', description: 'Discussed requirements for upcoming pharmaceutical conference. Need branded notebooks, pens, and USB drives with company logo. Budget approximately 50.000 DKK.', userId: 1, userName: 'Mads Mark-Buur' },
  { id: 2, client: 'Carlsberg Group', clientId: 'C002', date: '2026-01-30', type: 'Email', subject: 'Order confirmation - Branded merchandise', description: 'Confirmed order for 5.000 branded t-shirts and 10.000 beer coasters. Delivery scheduled for March 15th.', userId: 3, userName: 'Peter Sørensen' },
  { id: 3, client: 'TDC NET A/S', clientId: 'L003', date: '2026-01-29', type: 'Email', subject: 'Request for catalog and pricing', description: 'Sent complete catalog with pricing for corporate gifts. Client interested in branded power banks and USB drives for employee gifts.', userId: 1, userName: 'Mads Mark-Buur' },
  { id: 4, client: 'Danish Crown A/S', clientId: 'C004', date: '2026-01-28', type: 'Network', networkName: 'Food & Industry Purchasing Network', subject: 'On-site meeting at customer facility', description: 'Visited production facility to discuss branded work uniforms. Showed samples of embroidered polo shirts, caps, and safety vests with company logo.', userId: 4, userName: 'Maria Kristensen' },
  { id: 5, client: 'Arla Foods A/S', clientId: 'C006', date: '2026-01-27', type: 'Phone', subject: 'Reorder discussion - Coffee cups', description: 'Customer very satisfied with previous order of 3.000 branded reusable coffee cups. Wants to reorder with updated logo design. Delivery needed by end of March.', userId: 5, userName: 'Lars Nielsen' },
  { id: 6, client: 'Danske Bank A/S', clientId: 'L005', date: '2026-01-26', type: 'Email', subject: 'Quote request for customer gifts', description: 'Requested pricing for premium gift sets including notebooks, pens, and card holders. Quantity: 500 sets. Need samples before final decision.', userId: 1, userName: 'Mads Mark-Buur' },
  { id: 7, client: 'Novo Nordisk A/S', clientId: 'L001', date: '2026-01-25', type: 'Phone', subject: 'Follow-up on conference materials quote', description: 'Discussed delivery timeline and logo placement options. Client approved the quote and ready to proceed with order.', userId: 1, userName: 'Mads Mark-Buur' },
  { id: 8, client: 'TDC NET A/S', clientId: 'L003', date: '2026-01-24', type: 'Referral', referredBy: 'Novo Nordisk A/S', referredByClientId: 'L001', subject: 'Sample approval meeting', description: 'Client reviewed physical samples of branded merchandise. Approved t-shirt quality and color. Requested minor adjustment to cap embroidery placement.', userId: 2, userName: 'Emma Andersen' },
  { id: 9, client: 'Carlsberg Group', clientId: 'C002', date: '2026-01-23', type: 'Phone', subject: 'New campaign discussion', description: 'Preliminary discussion about summer promotional campaign. Interest in branded beach towels, cooler bags, and bottle openers. Estimated budget: 150.000 DKK.', userId: 1, userName: 'Mads Mark-Buur' },
  { id: 10, client: 'Danish Crown A/S', clientId: 'C004', date: '2026-01-22', type: 'Email', subject: 'Uniform size collection update', description: 'Sent employee size collection forms. Need to finalize quantities for each size before production can start.', userId: 4, userName: 'Maria Kristensen' },
  { id: 11, client: 'Arla Foods A/S', clientId: 'C006', date: '2026-01-20', type: 'Email', subject: 'Logo file request', description: 'Requested high-resolution logo files in vector format for production. Client will send updated brand guidelines.', userId: 1, userName: 'Mads Mark-Buur' },
  { id: 12, client: 'Danske Bank A/S', clientId: 'L005', date: '2026-01-19', type: 'Phone', subject: 'Initial contact - Corporate gifts', description: 'New lead from website inquiry. Looking for high-quality corporate gifts for top clients. Budget flexible, quality is priority.', userId: 1, userName: 'Mads Mark-Buur' },
  { id: 13, client: 'Rambøll Group A/S', clientId: 'L007', date: '2026-02-01', type: 'Phone', subject: 'Initial consultation call', description: 'Discussed promotional materials for employee anniversary event. Interest in branded bags, notebooks, and promotional items for 200+ employees.', userId: 1, userName: 'Mads Mark-Buur' },
  { id: 14, client: 'Bestseller A/S', clientId: 'L008', date: '2026-01-26', type: 'Email', subject: 'Quote request for retail staff uniforms', description: 'Sent detailed quotation for branded polo shirts and caps for retail staff across 50 stores. Total quantity approximately 1.200 pieces.', userId: 2, userName: 'Emma Andersen' },
  { id: 15, client: 'Netto Supermarked', clientId: 'C009', date: '2025-11-05', type: 'Fair/Event', eventName: 'Retail Expo Copenhagen', eventDate: '2025-11-05', subject: 'Trade show materials delivery', description: 'Successfully delivered branded promotional items for trade show participation. Customer very satisfied with quality and turnaround time.', userId: 1, userName: 'Mads Mark-Buur' },
  { id: 16, client: 'Copenhagen Business School', clientId: 'L011', date: '2026-01-12', type: 'Email', subject: 'Student welcome package inquiry', description: 'Discussed branded welcome packages for new students. Items include notebooks, pens, USB drives, and tote bags. Estimated 800 packages needed.', userId: 1, userName: 'Mads Mark-Buur' },
  { id: 17, client: 'Saxo Bank A/S', clientId: 'L012', date: '2026-01-05', type: 'Phone', subject: 'Initial inquiry - Client gifts', description: 'New lead interested in premium corporate gifts for VIP clients. Looking for high-end items like branded leather accessories and gift sets.', userId: 3, userName: 'Peter Sørensen' },
  { id: 18, client: 'Nordic Textiles A/S', clientId: 1, date: '2026-01-31', type: 'Email', subject: 'Production capacity confirmation', description: 'Supplier confirmed available production slot for branded textile run in week 7.', userId: 2, userName: 'Emma Andersen' },
  { id: 19, client: 'PrintPartner ApS', clientId: 2, date: '2026-01-29', type: 'Phone', subject: 'Proof timeline alignment', description: 'Reviewed artwork proof timeline and agreed on same-day feedback process for urgent jobs.', userId: 1, userName: 'Mads Mark-Buur' },
  { id: 20, client: 'CeramicWorks DK', clientId: 3, date: '2026-01-27', type: 'Network', networkName: 'Supplier Quality Network', subject: 'Sample glazing review meeting', description: 'Met supplier to review ceramic glaze options and packaging standards for mug production.', userId: 4, userName: 'Maria Kristensen' },
  { id: 21, client: 'Rambøll Group A/S', clientId: 'L007', date: '2026-02-02', type: 'Referral', referredBy: 'Carlsberg Group', referredByClientId: 'C002', subject: 'Referral from campaign team', description: 'Carlsberg contact introduced Rambøll events team after a completed merchandise campaign. Rambøll asked for a condensed proposal with sustainable product options.', userId: 1, userName: 'Mads Mark-Buur' },
  { id: 22, client: 'Bestseller A/S', clientId: 'L008', date: '2026-01-30', type: 'Fair/Event', eventName: 'Copenhagen Fashion Forum', eventDate: '2026-01-30', subject: 'Retail uniform follow-up from event', description: 'Met Sofie Larsen at the sourcing booth. She requested fabric swatches, embroidery samples, and pricing tiers for rollout across selected stores.', userId: 2, userName: 'Emma Andersen' },
  { id: 23, client: 'Saxo Bank A/S', clientId: 'L012', date: '2026-01-08', type: 'Network', networkName: 'Executive Gifts Roundtable', subject: 'VIP gift set discussion', description: 'Network introduction focused on premium gifts for high-value client onboarding. Client wants leather, metal, and desk accessories with understated branding.', userId: 3, userName: 'Peter Sørensen' },
  { id: 24, client: 'Copenhagen Business School', clientId: 'L011', date: '2026-01-14', type: 'Phone', subject: 'Welcome package quantity clarification', description: 'Clarified campus delivery windows and potential split shipment for international programme welcome packages.', userId: 5, userName: 'Lars Nielsen' },
  { id: 25, client: 'Netto Supermarked', clientId: 'C009', date: '2025-11-12', type: 'Email', subject: 'Post-event reorder inquiry', description: 'Customer asked whether tote bags and pens can be reordered with the same artwork for spring store campaigns.', userId: 1, userName: 'Mads Mark-Buur' },
];

const initialOffersRaw = [
  {
    key: '1',
    id: 'NOV-20260201',
    type: 'Offer',
    client: 'Novo Nordisk A/S',
    clientId: 'L001',
    date: '2026-02-01',
    total: '48.750 DKK',
    internalTotal: '19.600 DKK',
    status: 'Sent to the Customer',
    items: 2,
    validUntil: '2026-03-01',
    sourceQuoteIds: ['QUO-L001-20260205', 'QUO-L001-20260205-2'],
    descriptionToCustomer: 'Customer-facing offer prepared from the completed Novo Nordisk procurement inquiry.',
    deliveryDate: '2026-03-01',
    paymentTerms: 'Net 14 days',
    convertedOrderId: 'NOV-20260301',
  },
  {
    key: '1b',
    id: 'NOV-20260301',
    type: 'Order',
    client: 'Novo Nordisk A/S',
    clientId: 'L001',
    date: '2026-03-01',
    total: '48.750 DKK',
    internalTotal: '19.600 DKK',
    status: 'Sent to the Implementation',
    items: 2,
    deliveryDate: '2026-03-20',
    sourceOfferId: 'NOV-20260201',
    descriptionToCustomer: 'Customer-facing offer prepared from the completed Novo Nordisk procurement inquiry.',
    paymentTerms: 'Net 14 days',
  },
  { key: '2', id: 'CAR-20260125', type: 'Order', client: 'Carlsberg Group', clientId: 'C002', date: '2026-01-25', total: '71.700 DKK', status: 'Accepted by Customer', items: 3, deliveryDate: '2026-02-15', sourceOfferId: 'CAR-20260123' },
  { key: '3', id: 'TDC-20260129', type: 'Offer', client: 'TDC NET A/S', clientId: 'L003', date: '2026-01-29', total: '125.600 DKK', status: 'Sent to the Customer', items: 5, validUntil: '2026-02-28', sourceQuoteIds: ['QUO-L003-20260120'] },
  { key: '4', id: 'DAN-20260128', type: 'Order', client: 'Danish Crown A/S', clientId: 'C004', date: '2026-01-28', total: '34.000 DKK', status: 'Sent to the Implementation', items: 5, deliveryDate: '2026-03-15', sourceQuoteIds: ['QUO-C004-20260128', 'QUO-C004-20260128-2'] },
  { key: '5', id: 'DAN-20260126', type: 'Offer', client: 'Danske Bank A/S', clientId: 'L005', date: '2026-01-26', total: '87.500 DKK', status: 'New Sales Quote', items: 4, validUntil: '2026-02-26', sourceQuoteIds: ['QUO-L005-20260117'] },
  { key: '6', id: 'ARL-20260125', type: 'Order', client: 'Arla Foods A/S', clientId: 'C006', date: '2026-01-25', total: '45.000 DKK', status: 'Sent to the Implementation', items: 1, deliveryDate: '2026-03-30' },
  { key: '7', id: 'ARL-20251120', type: 'Order', client: 'Arla Foods A/S', clientId: 'C006', date: '2025-11-20', total: '42.000 DKK', status: 'Accepted by Customer', items: 1, deliveryDate: '2025-12-15' },
  { key: '8', id: 'CAR-20260123', type: 'Offer', client: 'Carlsberg Group', clientId: 'C002', date: '2026-01-23', total: '156.000 DKK', status: 'Customer Accepted', items: 6, validUntil: '2026-02-23', convertedOrderId: 'CAR-20260125', sourceQuoteIds: ['QUO-C002-20260118', 'QUO-C002-20260118-2'] },
  { key: '9', id: 'DAN-20251210', type: 'Order', client: 'Danish Crown A/S', clientId: 'C004', date: '2025-12-10', total: '69.600 DKK', status: 'Accepted by Customer', items: 3, deliveryDate: '2026-01-20' },
  { key: '10', id: 'TDC-20260124', type: 'Offer', client: 'TDC NET A/S', clientId: 'L003', date: '2026-01-24', total: '76.800 DKK', status: 'Sent to the Customer', items: 4, validUntil: '2026-02-24', sourceQuoteIds: ['QUO-L003-20260115'] },
  { key: '11', id: 'CAR-20260120', type: 'Order', client: 'Carlsberg Group', clientId: 'C002', date: '2026-01-20', total: '52.500 DKK', status: 'New Order', items: 2, deliveryDate: '2026-02-28' },
  { key: '12', id: 'NOV-20251215', type: 'Offer', client: 'Novo Nordisk A/S', clientId: 'L001', date: '2025-12-15', total: '34.200 DKK', status: 'Sent to the Customer', items: 2, validUntil: '2026-01-15', sourceQuoteIds: ['QUO-L001-20251205', 'QUO-L001-20251205-2'] },
  { key: '13', id: 'ARL-20251018', type: 'Order', client: 'Arla Foods A/S', clientId: 'C006', date: '2025-10-18', total: '26.800 DKK', status: 'Accepted by Customer', items: 3, deliveryDate: '2025-11-20' },
  { key: '14', id: 'DAN-20260119', type: 'Offer', client: 'Danske Bank A/S', clientId: 'L005', date: '2026-01-19', total: '143.000 DKK', status: 'Sent to the Customer', items: 7, validUntil: '2026-02-19', sourceQuoteIds: ['QUO-L005-20260112'] },
  { key: '15', id: 'RAM-20260201', type: 'Offer', client: 'Rambøll Group A/S', clientId: 'L007', date: '2026-02-01', total: '98.400 DKK', status: 'Sent to the Customer', items: 5, validUntil: '2026-03-01', sourceQuoteIds: ['QUO-L007-20260123'] },
  { key: '16', id: 'BES-20260126', type: 'Offer', client: 'Bestseller A/S', clientId: 'L008', date: '2026-01-26', total: '215.000 DKK', status: 'Sent to the Customer', items: 9, validUntil: '2026-02-26', sourceQuoteIds: ['QUO-L008-20260126'] },
  { key: '17', id: 'NET-20251105', type: 'Order', client: 'Netto Supermarked', clientId: 'C009', date: '2025-11-05', total: '143.000 DKK', status: 'Accepted by Customer', items: 6, deliveryDate: '2025-12-10' },
  { key: '18', id: 'COP-20260112', type: 'Offer', client: 'Copenhagen Business School', clientId: 'L011', date: '2026-01-12', total: '64.800 DKK', status: 'Customer Accepted', items: 4, validUntil: '2026-02-12', sourceQuoteIds: ['QUO-L011-20260112'] },
  { key: '19', id: 'SAX-20260105', type: 'Offer', client: 'Saxo Bank A/S', clientId: 'L012', date: '2026-01-05', total: '36.400 DKK', status: 'New Sales Quote', items: 3, validUntil: '2026-02-05', sourceQuoteIds: ['QUO-L012-20251228'] },
];

const responsibleByClientId = {
  L001: 'Mads Mark-Buur',
  C002: 'Peter Sørensen',
  L003: 'Emma Andersen',
  C004: 'Maria Kristensen',
  L005: 'Mads Mark-Buur',
  C006: 'Lars Nielsen',
  L007: 'Mads Mark-Buur',
  L008: 'Emma Andersen',
  C009: 'Mads Mark-Buur',
  L011: 'Lars Nielsen',
  L012: 'Peter Sørensen',
};

// Procurement-workflow-redesign additions: every Offer/Order record gets an
// `archived` flag; Orders additionally get an `overallStatus` header value
// (separate from the per-document `status`); Offers get a `sourceQuoteIds`
// array (derived from the legacy singular `sourceQuoteId` where present).
export const initialOffers = initialOffersRaw.map((record) => ({
  archived: false,
  priority: record.priority || 'Medium',
  responsible: responsibleByClientId[record.clientId] || '',
  createdBy: record.createdBy || responsibleByClientId[record.clientId] || 'Sales team',
  descriptionToCustomer: record.descriptionToCustomer || `Proposal prepared for ${record.client}.`,
  paymentTerms: record.paymentTerms || 'Net 14 days',
  deliveryDate: record.deliveryDate || record.validUntil || record.date,
  sourceQuoteIds: record.sourceQuoteId ? [record.sourceQuoteId] : [],
  ...(record.type === 'Order' ? { overallStatus: 'In Progress' } : {}),
  ...record,
}));

export const initialFollowUps = [
  { id: 1, offerId: 'NOV-20260201', date: '2026-02-05', action: 'Follow up on offer acceptance with Lars Jensen', responsible: 'Mads M-B', reminder: 'Overdue!', status: 'Not Done', notes: 'No response received' },
  { id: 2, offerId: 'NOV-20260201', date: '2026-02-10', action: 'Schedule product sample delivery to Novo Nordisk', responsible: 'Emma A.', reminder: 'In 7 days', status: 'Pending', notes: null },
  { id: 3, offerId: 'RAM-20260201', date: '2026-02-08', action: 'Call Henrik Sørensen to confirm offer details', responsible: 'Mads M-B', reminder: 'In 5 days', status: 'Pending', notes: null },
  { id: 4, offerId: 'TDC-20260129', date: '2026-01-31', action: 'Send revised pricing to Anders Petersen', responsible: 'Mads M-B', reminder: null, status: 'Done', notes: 'Email sent with updated catalog' },
  { id: 5, offerId: 'TDC-20260129', date: '2026-02-03', action: 'Confirm sample delivery address with TDC', responsible: 'Emma A.', reminder: 'Tomorrow', status: 'In Production', notes: 'Awaiting address confirmation' },
  { id: 6, offerId: 'TDC-20260129', date: '2026-02-07', action: 'Follow up on offer decision', responsible: 'Mads M-B', reminder: 'In 4 days', status: 'Pending', notes: null },
  { id: 7, offerId: 'DAN-20260128', date: '2026-01-30', action: 'Call Nordic Textiles to confirm fabric samples received', responsible: 'Sofie Lund', reminder: '1 day before', status: 'Done', notes: 'Samples confirmed by supplier' },
  { id: 8, offerId: 'DAN-20260128', date: '2026-02-05', action: 'Approve first print sample with PrintPartner', responsible: 'Mads Mark-Buur', reminder: '1 day before', status: 'In Production', notes: 'Awaiting final artwork approval' },
  { id: 9, offerId: 'DAN-20260128', date: '2026-02-10', action: 'Check production progress with both suppliers', responsible: 'Henrik Strøm', reminder: '1 day before', status: 'Open', notes: null },
  { id: 10, offerId: 'DAN-20260128', date: '2026-02-13', action: 'Final QC inspection before dispatch', responsible: 'Anne Dahl', reminder: '3 days before', status: 'Open', notes: null },
  { id: 11, offerId: 'DAN-20260128', date: '2026-02-14', action: 'Confirm delivery receipt with Danish Crown', responsible: 'Mads Mark-Buur', reminder: 'At the time', status: 'Open', notes: null },
  { id: 12, offerId: 'CAR-20260125', date: '2026-01-20', action: 'Confirm t-shirt quantity split by size', responsible: 'Peter S.', reminder: null, status: 'Done', notes: 'Size breakdown confirmed via email' },
  { id: 13, offerId: 'CAR-20260125', date: '2026-01-22', action: 'Approve Carlsberg pantone red color proof', responsible: 'Peter S.', reminder: null, status: 'Done', notes: 'Carlsberg approved pantone 485 C' },
  { id: 14, offerId: 'ARL-20260125', date: '2026-02-01', action: 'Confirm updated Arla logo files received', responsible: 'Lars N.', reminder: null, status: 'Done', notes: 'Vector file received from Arla brand team' },
  { id: 15, offerId: 'ARL-20260125', date: '2026-03-20', action: 'Quality check before dispatch to Arla', responsible: 'Lars N.', reminder: 'In 47 days', status: 'Pending', notes: null },
  { id: 16, offerId: 'BES-20260126', date: '2026-02-03', action: 'Send fabric swatches to Bestseller offices', responsible: 'Emma A.', reminder: 'In 1 day', status: 'Pending', notes: null },
  { id: 17, offerId: 'BES-20260126', date: '2026-02-06', action: 'Follow up with Sofie Larsen on offer review', responsible: 'Emma A.', reminder: 'In 4 days', status: 'Pending', notes: null },
].map((followUp) => ({
  ...followUp,
  reminder: followUp.reminder || 'No reminder set',
  notes: followUp.notes || 'No notes recorded.',
}));

const initialOrderSuppliersRaw = {
  'CAR-20260125': [
    {
      name: 'Nordic Textiles A/S',
      number: 1,
      status: 'Completed',
      poSent: '2026-01-20',
      items: [
        { row: 1, itemId: 'CAR-TSH-SIZE', description: 'Umbrellas — Carlsberg red print', article: 'NT-UMB-STD', qty: 5000, unitPrice: 12, costFromSupplier: 7, baseInternalPrice: 7, productionCost: 0, deliveryCost: 0, margin: 71.4, total: 60000, notes: 'Panel print in pantone-matched red.', status: 'Completed' },
      ],
    },
    {
      name: 'PrintPartner ApS',
      number: 2,
      status: 'Completed',
      poSent: '2026-01-20',
      items: [
        { row: 2, description: 'Beer coasters — full bleed print 100mm', article: 'PP-CST-100', qty: 10000, unitPrice: 0.75, costFromSupplier: 0.4, baseInternalPrice: 0.4, productionCost: 0, deliveryCost: 0, margin: 87.5, total: 7500, notes: 'Matte finish on both sides, packed in sleeves of 100.', status: 'Completed' },
      ],
    },
    {
      name: 'SwiftCargo Express',
      number: 3,
      status: 'Completed',
      poSent: '2026-01-20',
      items: [
        { row: 3, lineType: 'Logistics', logisticsId: 2, description: 'Sea Freight', article: 'LOG-SEA', qty: 1, unitPrice: 4200, costFromSupplier: 3600, baseInternalPrice: 3600, productionCost: 0, deliveryCost: 0, margin: 16.7, total: 4200, notes: 'Consolidated ocean freight, Shenzhen to Aarhus.', status: 'Completed' },
      ],
    },
  ],
  'DAN-20260128': [
    {
      name: 'Nordic Textiles A/S',
      number: 1,
      status: 'Production',
      poSent: '2026-01-23',
      items: [
        { row: 1, itemId: 'DAN-POLO-ART', description: 'Bags — Danish Crown logo', article: 'NT-BAG-TOT', qty: 200, unitPrice: 95, costFromSupplier: 68, baseInternalPrice: 68, productionCost: 0, deliveryCost: 0, margin: 39.7, total: 19000, notes: 'Natural canvas with front panel print, size split confirmed.', status: 'Completed' },
        { row: 2, itemId: 'DAN-CAP-PROOF', description: 'Umbrellas — embroidered logo', article: 'NT-UMB-STD', qty: 100, unitPrice: 68, costFromSupplier: 45, baseInternalPrice: 45, productionCost: 0, deliveryCost: 0, margin: 51.1, total: 6800, notes: 'Logo centered on panel print.', status: 'Production' },
      ],
    },
    {
      name: 'PrintPartner ApS',
      number: 2,
      status: 'Proof Pending',
      poSent: '2026-01-24',
      items: [
        { row: 3, itemId: 'DAN-PEN-ART', description: 'Bags — 2-color logo print', article: 'PP-TOTE-420', qty: 500, unitPrice: 8, costFromSupplier: 5, baseInternalPrice: 5, productionCost: 0, deliveryCost: 0, margin: 60, total: 4000, notes: 'Natural canvas with white logo print.', status: 'Proof Pending' },
        { row: 4, description: 'Mugs — custom design', article: 'PP-MUG-CER', qty: 250, unitPrice: 12, costFromSupplier: 7, baseInternalPrice: 7, productionCost: 0, deliveryCost: 0, margin: 71.4, total: 3000, notes: 'Ceramic mugs, packed individually.', status: 'Pending' },
      ],
    },
    {
      name: 'ColorCraft Decoration',
      number: 3,
      status: 'Completed',
      poSent: '2026-01-24',
      items: [
        { row: 5, lineType: 'Service', serviceId: 2, description: 'Screen Print', article: 'SRV-SCR', qty: 200, unitPrice: 6, costFromSupplier: 3.5, baseInternalPrice: 3.5, productionCost: 0, deliveryCost: 0, margin: 71.4, total: 1200, notes: 'Single-colour front logo screen print on tote bags.', status: 'Completed' },
      ],
    },
  ],
  'ARL-20260125': [
    {
      name: 'Nordic Textiles A/S',
      number: 1,
      status: 'Production',
      poSent: '2026-01-26',
      items: [
        { row: 1, itemId: 'ARL-MUG-LOGO', description: 'Mugs — updated Arla logo print', article: 'CW-MUG-330', qty: 3000, unitPrice: 15, costFromSupplier: 10, baseInternalPrice: 10, productionCost: 0, deliveryCost: 0, margin: 50, total: 45000, notes: 'Updated logo artwork approved, packed in retail cartons.', status: 'Production' },
      ],
    },
  ],
  'ARL-20251120': [
    {
      name: 'Nordic Textiles A/S',
      number: 1,
      status: 'Completed',
      poSent: '2025-11-15',
      items: [
        { row: 1, description: 'Mugs — Arla original logo', qty: 2000, unitPrice: 21, costFromSupplier: 13, baseInternalPrice: 13, productionCost: 0, deliveryCost: 0, margin: 61.5, total: 42000, notes: 'White ceramic mugs with one-color wrap print.', status: 'Completed' },
      ],
    },
  ],
  'DAN-20251210': [
    {
      name: 'Nordic Textiles A/S',
      number: 1,
      status: 'Completed',
      poSent: '2025-12-05',
      items: [
        { row: 1, description: 'Umbrellas — Danish Crown logo', qty: 500, unitPrice: 85, costFromSupplier: 55, baseInternalPrice: 55, productionCost: 0, deliveryCost: 0, margin: 54.5, total: 42500, notes: 'Navy canopy with single-colour panel print.', status: 'Completed' },
        { row: 2, description: 'Bags', qty: 200, unitPrice: 68, costFromSupplier: 44, baseInternalPrice: 44, productionCost: 0, deliveryCost: 0, margin: 54.5, total: 13600, notes: 'Navy canvas with front panel print.', status: 'Completed' },
      ],
    },
    {
      name: 'PrintPartner ApS',
      number: 2,
      status: 'Completed',
      poSent: '2025-12-06',
      items: [
        { row: 3, description: 'Bags — custom print', qty: 300, unitPrice: 45, costFromSupplier: 28, baseInternalPrice: 28, productionCost: 0, deliveryCost: 0, margin: 60.7, total: 13500, notes: 'Natural cotton with full-color one-side print.', status: 'Completed' },
      ],
    },
  ],
  'CAR-20260120': [
    {
      name: 'CeramicWorks DK',
      number: 1,
      status: 'Pending',
      poSent: '2026-01-21',
      items: [
        { row: 1, description: 'Mugs — Carlsberg summer edition', qty: 1500, unitPrice: 35, costFromSupplier: 20, baseInternalPrice: 20, productionCost: 0, deliveryCost: 0, margin: 75, total: 52500, notes: 'Seasonal artwork version, final glaze sample pending.', status: 'Pending' },
      ],
    },
  ],
  'NET-20251105': [
    {
      name: 'Nordic Textiles A/S',
      number: 1,
      status: 'Completed',
      poSent: '2025-10-28',
      items: [
        { row: 1, description: 'Umbrellas — Netto logo', qty: 2000, unitPrice: 45, costFromSupplier: 28, baseInternalPrice: 28, productionCost: 0, deliveryCost: 0, margin: 60.7, total: 90000, notes: 'Budget panel print, red logo, store campaign delivery.', status: 'Completed' },
        { row: 2, itemId: 'NET-TOTE-PRINT', description: 'Bags — custom print', qty: 1000, unitPrice: 28, costFromSupplier: 18, baseInternalPrice: 18, productionCost: 0, deliveryCost: 0, margin: 55.6, total: 28000, notes: 'Reusable bag with long handles, artwork on both sides.', status: 'Completed' },
      ],
    },
    {
      name: 'PrintPartner ApS',
      number: 2,
      status: 'Completed',
      poSent: '2025-10-28',
      items: [
        { row: 3, description: 'Power Banks — 2-color logo', qty: 5000, unitPrice: 5, costFromSupplier: 3, baseInternalPrice: 3, productionCost: 0, deliveryCost: 0, margin: 66.7, total: 25000, notes: 'Bulk-packed for in-store promotional stands.', status: 'Completed' },
      ],
    },
  ],
  'ARL-20251018': [
    {
      name: 'PromoGoods DK',
      number: 1,
      status: 'Completed',
      poSent: '2025-10-10',
      items: [
        { row: 1, description: 'Mugs — Arla logo', article: 'PG-MUG-WHT', qty: 500, unitPrice: 18, costFromSupplier: 10, baseInternalPrice: 10, productionCost: 0, deliveryCost: 0, margin: 80, total: 9000, notes: 'White ceramic mugs with blue logo print.', status: 'Completed' },
        { row: 2, description: 'Power Banks', article: 'PG-PWR-5K', qty: 400, unitPrice: 22, costFromSupplier: 12, baseInternalPrice: 12, productionCost: 0, deliveryCost: 0, margin: 83.3, total: 8800, notes: 'Black housing, laser-engraved logo.', status: 'Completed' },
        { row: 3, description: 'Bottles', qty: 600, unitPrice: 15, costFromSupplier: 8, baseInternalPrice: 8, productionCost: 0, deliveryCost: 0, margin: 87.5, total: 9000, notes: 'Stainless steel, laser-engraved logo.', status: 'Completed' },
      ],
    },
  ],
  'COP-20260112': [
    {
      name: 'No supplier',
      number: 1,
      status: 'Pending',
      poSent: '2026-01-12',
      items: [
        { row: 1, description: 'Student welcome package assembly', article: '', qty: 800, unitPrice: 22, costFromSupplier: 12, baseInternalPrice: 12, productionCost: 0, deliveryCost: 0, margin: 83.3, total: 17600, notes: 'Manual packing and kitting estimate before supplier selection.' },
        { row: 2, description: 'Campus delivery split handling', article: '', qty: 4, unitPrice: 1800, costFromSupplier: 950, baseInternalPrice: 950, productionCost: 0, deliveryCost: 0, margin: 89.5, total: 7200, notes: 'Separate deliveries for main campus and satellite locations.' },
      ],
    },
    {
      name: 'PrintPartner ApS',
      number: 2,
      status: 'Proof Pending',
      poSent: '2026-01-12',
      items: [
        { row: 3, description: 'Mugs — CBS welcome logo', article: 'PP-MUG-CER', qty: 5000, unitPrice: 8, costFromSupplier: 5, baseInternalPrice: 5, productionCost: 0, deliveryCost: 0, margin: 60, total: 40000, notes: 'Proof needed before student welcome packs are assembled.' },
      ],
    },
  ],
  'SAX-20260105': [
    {
      name: 'No supplier',
      number: 1,
      status: 'Pending',
      poSent: '2026-01-05',
      items: [
        { row: 1, description: 'Premium gift box concept and sourcing', article: '', qty: 200, unitPrice: 98, costFromSupplier: 58, baseInternalPrice: 58, productionCost: 0, deliveryCost: 0, margin: 69, total: 19600, notes: 'Supplier not selected while VIP gift concept is being finalized.' },
        { row: 2, description: 'Logo placement mockup package', article: '', qty: 1, unitPrice: 4800, costFromSupplier: 2400, baseInternalPrice: 2400, productionCost: 0, deliveryCost: 0, margin: 100, total: 4800, notes: 'Internal creative estimate for client approval round.' },
        { row: 3, description: 'Packaging insert cards', article: '', qty: 200, unitPrice: 60, costFromSupplier: 34, baseInternalPrice: 34, productionCost: 0, deliveryCost: 0, margin: 76.5, total: 12000, notes: 'Awaiting final paper and print supplier decision.' },
      ],
    },
  ],
};

// Keep legacy Offer/Order lines compatible with the Product Details popup.
// The original descriptions remain intact for existing views; these fields
// provide the same product-information shape as Quote records.
export const initialOrderSuppliers = Object.fromEntries(
  Object.entries(initialOrderSuppliersRaw).map(([offerId, sections]) => [
    offerId,
    sections.map((section) => ({
      ...section,
      items: section.items.map((item) => ({
        ...item,
        productName: item.productName || String(item.description || '').split(' — ')[0] || 'Custom product',
        article: item.article || 'Custom specification',
        productDetails: item.productDetails || item.description || 'Product specification to be confirmed with the supplier.',
        brandingDecoration: item.brandingDecoration || item.brandingDescription || 'Branding specification to be confirmed with the customer.',
        comments: item.comments || item.notes || 'No additional comments.',
        baseInternalPrice: Number(item.baseInternalPrice ?? item.internalPrice ?? item.costFromSupplier ?? 0),
        productionCost: Number(item.productionCost ?? 0),
        deliveryCost: Number(item.deliveryCost ?? item.shippingCost ?? 0),
        shippingCost: Number(item.shippingCost ?? item.deliveryCost ?? 0),
        margin: Number(item.margin ?? item.marginPercent ?? 0),
        discount: Number(item.discount ?? item.discountPercent ?? 0),
        files: item.files || [],
      })),
    })),
  ]),
);

const RAW_INQUIRIES = [
  {
    id: 'INQ-L001-20260205',
    key: 'INQ-L001-20260205',
    type: 'Inquiry',
    clientId: 'L001',
    client: 'Novo Nordisk A/S',
    date: '2026-02-05',
    requestedBy: 'Lars Jensen',
    priority: 'High',
    neededBefore: '2026-03-01',
    requestedDeliveryDate: '2026-03-08',
    productCategoryId: 1,
    productCategoryName: 'Textiles',
    requestedQuantity: 400,
    budgetTargetPrice: 30000,
    printingDecorationType: 'Print & embroidery',
    urgent: true,
    reorder: false,
    specialRequirements: '<p>All items must carry the Novo Nordisk 2026 logo in Pantone 300C blue.</p>',
    notes: '<p>Deliver to HQ reception, Novo Allé, 2880 Bagsværd. Attention: Lars Jensen.</p>',
    status: 'Sent to Procurement',
    createdBy: 'Mads Mark-Buur',
    createdAt: '2026-02-05 09:12',
    attachments: [],
    country: 'Denmark',
    nextStep: 'Awaiting procurement pricing',
    archived: false,
    items: [
      {
        id: 'ITEM-novo-tshirt',
        productId: 8,
        productName: 'Bottles',
        specifications: '750ml stainless steel, double-wall insulated',
        productCategory: 'Drinkware',
        quantity: 200,
        brandingDescription: 'Laser-engraved logo, single position',
        deliveryRequirements: 'Individual gift box, 24 per carton',
        sourcingStatus: 'Quote Created',
        selectedSuppliers: [
          { supplierId: 6, supplierName: 'MetalGoods Scandinavia' },
          { supplierId: 4, supplierName: 'TechGifts EU' },
        ],
        files: [
          {
            uid: 'inq-novo-shirt-logo',
            name: 'Novo_bottle_logo_reference.jpg',
            status: 'done',
            url: 'https://picsum.photos/seed/novo-tshirt/300/300',
          },
        ],
      },
      {
        id: 'ITEM-novo-notebook',
        productId: 16,
        productName: 'Power Banks',
        specifications: '5000mAh, USB-A + USB-C output',
        productCategory: 'Electronics',
        quantity: 200,
        brandingDescription: 'Laser-engraved logo, top panel',
        deliveryRequirements: 'Individual box, 50 per carton',
        sourcingStatus: 'Quote Created',
        selectedSuppliers: [
          { supplierId: 4, supplierName: 'TechGifts EU' },
          { supplierId: 8, supplierName: 'PromoGoods DK' },
        ],
        files: [],
      },
    ],
  },
  {
    id: 'INQ-C002-20260207',
    key: 'INQ-C002-20260207',
    type: 'Inquiry',
    clientId: 'C002',
    client: 'Carlsberg Group',
    date: '2026-02-07',
    requestedBy: 'Maria Nielsen',
    priority: 'Medium',
    neededBefore: '2026-04-01',
    requestedDeliveryDate: '2026-04-10',
    productCategoryId: 3,
    productCategoryName: 'Bags & Outdoor',
    requestedQuantity: 650,
    budgetTargetPrice: 45000,
    printingDecorationType: 'Digital print & embroidery',
    urgent: false,
    reorder: false,
    specialRequirements: '<p>Carlsberg green (Pantone 356C), wordmark only.</p>',
    notes: '<p>Palletised delivery to Carlsberg logistics gate, Ny Carlsberg Vej 100.</p>',
    status: 'Sent to Procurement',
    createdBy: 'Peter Sørensen',
    createdAt: '2026-02-07 14:00',
    attachments: [],
    country: 'Denmark',
    nextStep: 'Awaiting supplier pricing',
    archived: false,
    items: [
      {
        id: 'ITEM-carlsberg-cooler',
        productId: 4,
        productName: 'Mugs',
        specifications: '330ml ceramic, dishwasher safe',
        productCategory: 'Drinkware',
        quantity: 500,
        brandingDescription: 'Full-colour logo print, wraparound, Carlsberg logo',
        deliveryRequirements: 'Individual gift box, 24 per carton',
        sourcingStatus: 'Quote Created',
        selectedSuppliers: [
          { supplierId: 3, supplierName: 'CeramicWorks DK' },
          { supplierId: 8, supplierName: 'PromoGoods DK' },
        ],
        files: [
          {
            uid: 'inq-carlsberg-cooler-mockup',
            name: 'Carlsberg_cooler_bag_mockup.jpg',
            status: 'done',
            url: 'https://picsum.photos/seed/carlsberg-cooler/300/300',
          },
        ],
      },
      {
        id: 'ITEM-carlsberg-rainjacket',
        productId: null,
        isNewProduct: true,
        productName: 'Branded Polo Rain Jacket',
        specifications: 'Waterproof shell, taped seams, embroidered chest logo',
        productCategory: 'Textiles',
        quantity: 150,
        brandingDescription: 'Embroidered chest logo, Pantone 356C',
        deliveryRequirements: 'Size run: S×30, M×50, L×40, XL×20, XXL×10',
        sourcingStatus: 'Quote Created',
        selectedSuppliers: [{ supplierId: 8, supplierName: 'PromoGoods DK' }],
        files: [],
      },
    ],
  },
  {
    id: 'INQ-L008-20260126',
    key: 'INQ-L008-20260126',
    type: 'Inquiry',
    clientId: 'L008',
    client: 'Bestseller A/S',
    date: '2026-01-26',
    requestedBy: 'Sofie Larsen',
    priority: 'Medium',
    neededBefore: '2026-03-15',
    requestedDeliveryDate: '2026-03-22',
    productCategoryId: 1,
    productCategoryName: 'Textiles',
    requestedQuantity: 1200,
    budgetTargetPrice: 60000,
    printingDecorationType: 'Embroidery',
    urgent: false,
    reorder: true,
    specialRequirements: '<p>Bestseller wordmark, Pantone Black C, embroidered chest logo only.</p>',
    notes: '<p>Split delivery across 50 retail stores, store list and quantities to follow.</p>',
    status: 'New Inquiry',
    createdBy: 'Emma Andersen',
    createdAt: '2026-01-26 14:20',
    attachments: [],
    country: 'Denmark',
    nextStep: 'Review details',
    archived: false,
    items: [
      {
        id: 'ITEM-bestseller-tshirt',
        productId: 3,
        productName: 'Bags',
        specifications: '280gsm cotton canvas, 38 × 42 cm, long handles',
        productCategory: 'Bags',
        quantity: 800,
        brandingDescription: 'Front panel logo print, single colour, Bestseller wordmark',
        deliveryRequirements: 'Split delivery across 50 retail stores',
        sourcingStatus: 'Quote Created',
        selectedSuppliers: [
          { supplierId: 1, supplierName: 'Nordic Textiles A/S' },
          { supplierId: 2, supplierName: 'PrintPartner ApS' },
        ],
        files: [],
      },
      {
        id: 'ITEM-bestseller-cap',
        productId: 12,
        productName: 'Umbrellas',
        specifications: '130cm diameter, fibreglass ribs, auto-open',
        productCategory: 'Outdoor',
        quantity: 400,
        brandingDescription: 'Panel print logo, single colour',
        deliveryRequirements: 'Same delivery as tote bags',
        sourcingStatus: 'Pending',
        selectedSuppliers: [],
        files: [],
      },
    ],
  },
  {
    id: 'INQ-C004-20260128',
    key: 'INQ-C004-20260128',
    type: 'Inquiry',
    clientId: 'C004',
    client: 'Danish Crown A/S',
    date: '2026-01-28',
    requestedBy: 'Mette Hansen',
    priority: 'High',
    neededBefore: '2026-03-10',
    requestedDeliveryDate: '2026-03-17',
    productCategoryId: 1,
    productCategoryName: 'Textiles',
    requestedQuantity: 850,
    budgetTargetPrice: 70000,
    printingDecorationType: 'Embroidery & print',
    urgent: true,
    reorder: false,
    specialRequirements: '<p>Danish Crown logo, white on red, left chest placement on garments.</p>',
    notes: '<p>Deliver to Randers production facility, attention HR department, Marsvej 43.</p>',
    status: 'Sent to Procurement',
    createdBy: 'Maria Kristensen',
    createdAt: '2026-01-28 11:30',
    attachments: [],
    country: 'Denmark',
    nextStep: 'Quotation in process',
    archived: false,
    items: [
      {
        id: 'ITEM-dancrown-polo',
        productId: 8,
        productName: 'Bottles',
        specifications: '750ml stainless steel, double-wall insulated',
        productCategory: 'Drinkware',
        quantity: 300,
        brandingDescription: 'Laser-engraved Danish Crown logo, single position',
        deliveryRequirements: 'Sample approved by HR before full production',
        sourcingStatus: 'Quote Created',
        selectedSuppliers: [
          { supplierId: 6, supplierName: 'MetalGoods Scandinavia' },
          { supplierId: 4, supplierName: 'TechGifts EU' },
        ],
        files: [
          {
            uid: 'inq-dancrown-polo-sample',
            name: 'Danish_Crown_bottle_sample.jpg',
            status: 'done',
            url: 'https://picsum.photos/seed/dancrown-polo/300/300',
          },
        ],
      },
      {
        id: 'ITEM-dancrown-cap',
        productId: 16,
        productName: 'Power Banks',
        specifications: '5000mAh, USB-A + USB-C output',
        productCategory: 'Electronics',
        quantity: 300,
        brandingDescription: 'Laser-engraved logo, top panel',
        deliveryRequirements: 'Same delivery as water bottles',
        sourcingStatus: 'Quote Created',
        selectedSuppliers: [
          { supplierId: 4, supplierName: 'TechGifts EU' },
          { supplierId: 8, supplierName: 'PromoGoods DK' },
        ],
        files: [],
      },
      {
        id: 'ITEM-dancrown-vest',
        productId: null,
        isNewProduct: true,
        productName: 'Hi-Vis Branded Safety Vests',
        specifications: 'EN ISO 20471 compliant, reflective strips, printed logo',
        productCategory: 'Textiles',
        quantity: 250,
        brandingDescription: 'Reflective strips, printed Danish Crown logo on back panel',
        deliveryRequirements: 'EN ISO 20471 compliant, size run S–XXL enclosed',
        sourcingStatus: 'Quote Created',
        selectedSuppliers: [{ supplierId: 8, supplierName: 'PromoGoods DK' }],
        files: [],
      },
    ],
  },
  {
    id: 'INQ-L011-20260112',
    key: 'INQ-L011-20260112',
    type: 'Inquiry',
    clientId: 'L011',
    client: 'Copenhagen Business School',
    date: '2026-01-12',
    requestedBy: 'Michael Thomsen',
    priority: 'Medium',
    neededBefore: '2026-08-01',
    requestedDeliveryDate: '2026-08-10',
    productCategoryId: 4,
    productCategoryName: 'Office & Giveaways',
    requestedQuantity: 2400,
    budgetTargetPrice: 90000,
    printingDecorationType: 'Debossing & print',
    urgent: false,
    reorder: true,
    specialRequirements: '<p>CBS crest, navy on natural/white, debossed or printed depending on item.</p>',
    notes: '<p>Deliver to campus warehouse, Solbjerg Plads 3, 2000 Frederiksberg.</p>',
    status: 'Sent to Procurement',
    createdBy: 'Mads Mark-Buur',
    createdAt: '2026-01-12 09:45',
    attachments: [],
    country: 'Denmark',
    nextStep: 'Quotation in process',
    archived: false,
    items: [
      {
        id: 'ITEM-cbs-notebook',
        productId: 4,
        productName: 'Mugs',
        specifications: '330ml ceramic, dishwasher safe',
        productCategory: 'Drinkware',
        quantity: 800,
        brandingDescription: 'CBS crest full-colour print, navy',
        deliveryRequirements: 'Deliver to campus warehouse, attention facilities team',
        sourcingStatus: 'Quote Created',
        selectedSuppliers: [
          { supplierId: 3, supplierName: 'CeramicWorks DK' },
          { supplierId: 8, supplierName: 'PromoGoods DK' },
        ],
        files: [
          {
            uid: 'inq-cbs-notebook-cover',
            name: 'CBS_notebook_cover_proof.jpg',
            status: 'done',
            url: 'https://picsum.photos/seed/cbs-notebook/300/300',
          },
        ],
      },
      {
        id: 'ITEM-cbs-pen',
        productId: 12,
        productName: 'Umbrellas',
        specifications: '130cm diameter, fibreglass ribs, auto-open',
        productCategory: 'Outdoor',
        quantity: 800,
        brandingDescription: 'CBS crest panel print, navy and white',
        deliveryRequirements: 'Same delivery as coffee mugs',
        sourcingStatus: 'Quote Created',
        selectedSuppliers: [{ supplierId: 1, supplierName: 'Nordic Textiles A/S' }],
        files: [],
      },
      {
        id: 'ITEM-cbs-tote',
        productId: 3,
        productName: 'Bags',
        specifications: 'Natural canvas, one colour print',
        productCategory: 'Bags & Outdoor',
        quantity: 800,
        brandingDescription: 'CBS crest print, one colour, natural canvas',
        deliveryRequirements: 'Same delivery as notebooks',
        sourcingStatus: 'Quote Created',
        selectedSuppliers: [
          { supplierId: 1, supplierName: 'Nordic Textiles A/S' },
          { supplierId: 2, supplierName: 'PrintPartner ApS' },
        ],
        files: [],
      },
    ],
  },
  {
    id: 'INQ-L003-20260622',
    key: 'INQ-L003-20260622',
    type: 'Inquiry',
    clientId: 'L003',
    client: 'TDC NET A/S',
    date: '2026-06-22',
    requestedBy: 'Anders Petersen',
    priority: 'High',
    neededBefore: '2026-08-15',
    requestedDeliveryDate: '2026-08-22',
    productCategoryId: 2,
    productCategoryName: 'Drinkware & Tech',
    requestedQuantity: 600,
    budgetTargetPrice: 52000,
    printingDecorationType: 'Laser engraving',
    urgent: false,
    reorder: false,
    specialRequirements: '<p>TDC NET logo laser engraved, no colour print — brand guideline requires single-tone engraving only.</p>',
    notes: '<p>Deliver to TDC NET head office, Teglholmsgade 1, 2450 Copenhagen. Attention: Anders Petersen.</p>',
    status: 'New Inquiry',
    createdBy: 'Mads Mark-Buur',
    createdAt: '2026-06-22 10:20',
    attachments: [],
    country: 'Denmark',
    nextStep: 'Awaiting supplier selection',
    archived: false,
    items: [
      {
        id: 'ITEM-tdc-powerbank',
        productId: 16,
        productName: 'Power Banks',
        specifications: '5000mAh, USB-C input, matte black housing',
        productCategory: 'Drinkware & Tech',
        quantity: 300,
        brandingDescription: 'TDC NET logo laser engraved on back panel',
        deliveryRequirements: 'Bulk packaging, palletised delivery to HQ',
        sourcingStatus: 'Pending',
        selectedSuppliers: [],
        files: [],
      },
      {
        id: 'ITEM-tdc-bottle',
        productId: 8,
        productName: 'Bottles',
        specifications: 'Stainless steel, 500ml, brushed finish',
        productCategory: 'Drinkware & Tech',
        quantity: 300,
        brandingDescription: 'TDC NET logo laser engraved on body',
        deliveryRequirements: 'Same delivery as power banks',
        sourcingStatus: 'Pending',
        selectedSuppliers: [],
        files: [],
      },
    ],
  },
  {
    id: 'INQ-L005-20260623',
    key: 'INQ-L005-20260623',
    type: 'Inquiry',
    clientId: 'L005',
    client: 'Danske Bank A/S',
    date: '2026-06-23',
    requestedBy: 'Thomas Christiansen',
    priority: 'Medium',
    neededBefore: '2026-09-01',
    requestedDeliveryDate: '2026-09-08',
    productCategoryId: 4,
    productCategoryName: 'Office & Giveaways',
    requestedQuantity: 650,
    budgetTargetPrice: 105500,
    printingDecorationType: 'Embossing & foil print',
    urgent: false,
    reorder: false,
    specialRequirements: '<p>Premium gift sets for VIP clients — Danske Bank wordmark embossed, navy and gold foil only.</p>',
    notes: '<p>Deliver to Holmens Kanal 2-12, 1092 Copenhagen. Attention: Thomas Christiansen, executive office.</p>',
    status: 'New Inquiry',
    createdBy: 'Mads Mark-Buur',
    createdAt: '2026-06-23 09:05',
    attachments: [
      {
        uid: 'inq-danskebank-logo',
        name: 'Danske_Bank_logo.png',
        status: 'done',
        url: '/mock-files/Danske_Bank_logo.png',
      },
    ],
    country: 'Denmark',
    nextStep: 'Awaiting supplier selection',
    archived: false,
    items: [
      {
        id: 'ITEM-danskebank-cardholder',
        productId: 3,
        productName: 'Bags',
        specifications: '280gsm cotton canvas, 38 × 42 cm, long handles',
        productCategory: 'Bags',
        quantity: 250,
        targetPrice: 180,
        brandingDescription: 'Danske Bank wordmark, front panel print',
        deliveryRequirements: 'Individually boxed, gift-ready presentation',
        sourcingStatus: 'Pending',
        selectedSuppliers: [],
        files: [
          {
            uid: 'inq-danskebank-logo-cardholder',
            name: 'Danske_Bank_logo.png',
            status: 'done',
            url: '/mock-files/Danske_Bank_logo.png',
          },
        ],
      },
      {
        id: 'ITEM-danskebank-notebook',
        productId: 8,
        productName: 'Bottles',
        specifications: '750ml stainless steel, double-wall insulated',
        productCategory: 'Drinkware',
        quantity: 250,
        targetPrice: 92,
        brandingDescription: 'Danske Bank wordmark, laser-engraved',
        deliveryRequirements: 'Same delivery as tote bags',
        sourcingStatus: 'Pending',
        selectedSuppliers: [],
        files: [
          {
            uid: 'inq-danskebank-logo-notebook',
            name: 'Danske_Bank_logo.png',
            status: 'done',
            url: '/mock-files/Danske_Bank_logo.png',
          },
        ],
      },
      {
        id: 'ITEM-danskebank-powerbank',
        productId: 16,
        productName: 'Power Banks',
        specifications: '5000mAh, USB-C input, matte navy housing',
        productCategory: 'Drinkware & Tech',
        quantity: 150,
        targetPrice: 250,
        brandingDescription: 'Danske Bank wordmark laser engraved, gold finish',
        deliveryRequirements: 'Individually boxed, gift-ready presentation',
        sourcingStatus: 'Pending',
        selectedSuppliers: [],
        files: [],
      },
    ],
  },
  {
    id: 'INQ-L007-20260623',
    key: 'INQ-L007-20260623',
    type: 'Inquiry',
    clientId: 'L007',
    client: 'Rambøll Group A/S',
    date: '2026-06-23',
    requestedBy: 'Henrik Sørensen',
    priority: 'Medium',
    neededBefore: '2026-09-15',
    requestedDeliveryDate: '2026-09-22',
    productCategoryId: 3,
    productCategoryName: 'Bags & Outdoor',
    requestedQuantity: 1200,
    budgetTargetPrice: 75000,
    printingDecorationType: 'Screen print',
    urgent: false,
    reorder: false,
    specialRequirements: '<p>Employee anniversary event — Rambøll logo, one colour, sustainable materials preferred.</p>',
    notes: '<p>Deliver to Hannemanns Allé 53, 2300 Copenhagen. Split shipment across 3 office locations, details to follow.</p>',
    status: 'Sent to Procurement',
    createdBy: 'Mads Mark-Buur',
    createdAt: '2026-06-23 13:40',
    attachments: [],
    country: 'Denmark',
    nextStep: 'Awaiting procurement pricing',
    archived: false,
    items: [
      {
        id: 'ITEM-ramboll-tote',
        productId: 3,
        productName: 'Bags',
        specifications: 'Recycled cotton canvas, one colour print',
        productCategory: 'Bags & Outdoor',
        quantity: 600,
        brandingDescription: 'Rambøll logo, one colour, centred front print',
        deliveryRequirements: 'Split delivery across 3 office locations',
        sourcingStatus: 'Suppliers Selected',
        selectedSuppliers: [
          { supplierId: 1, supplierName: 'Nordic Textiles A/S' },
        ],
        files: [],
      },
      {
        id: 'ITEM-ramboll-cooler',
        productId: 16,
        productName: 'Power Banks',
        specifications: '5000mAh, USB-A + USB-C output',
        productCategory: 'Electronics',
        quantity: 600,
        brandingDescription: 'Rambøll logo, laser-engraved, top panel',
        deliveryRequirements: 'Same delivery as tote bags',
        sourcingStatus: 'Suppliers Selected',
        selectedSuppliers: [
          { supplierId: 4, supplierName: 'TechGifts EU' },
        ],
        files: [],
      },
    ],
  },
  {
    id: 'INQ-L012-20260624',
    key: 'INQ-L012-20260624',
    type: 'Inquiry',
    clientId: 'L012',
    client: 'Saxo Bank A/S',
    date: '2026-06-24',
    requestedBy: 'Julie Andersen',
    priority: 'High',
    neededBefore: '2026-08-01',
    requestedDeliveryDate: '2026-08-08',
    productCategoryId: 4,
    productCategoryName: 'Office & Giveaways',
    requestedQuantity: 150,
    budgetTargetPrice: 42000,
    printingDecorationType: 'Embossing',
    urgent: true,
    reorder: false,
    specialRequirements: '<p>VIP client onboarding gift sets — premium leather and metal items only, understated branding.</p>',
    notes: '<p>Deliver to Philip Heymans Allé 15, 2900 Hellerup. Attention: Julie Andersen, private banking.</p>',
    status: 'New Inquiry',
    createdBy: 'Peter Sørensen',
    createdAt: '2026-06-24 08:30',
    attachments: [],
    country: 'Denmark',
    nextStep: 'Awaiting supplier selection',
    archived: false,
    items: [
      {
        id: 'ITEM-saxo-cardholder',
        productId: 4,
        productName: 'Mugs',
        specifications: '330ml ceramic, dishwasher safe',
        productCategory: 'Drinkware',
        quantity: 150,
        brandingDescription: 'Saxo Bank monogram, full-colour print',
        deliveryRequirements: 'Individually gift-boxed, presentation ribbon',
        sourcingStatus: 'Pending',
        selectedSuppliers: [],
        files: [],
      },
    ],
  },
  // Source Inquiries for the legacy standalone Offers below (TDC-20260124/-129,
  // DAN-20260126/-119, CAR-20260123, NOV-20251215, RAM-20260201, SAX-20260105) —
  // those Offers previously had no Inquiry/Quote backing at all.
  {
    id: 'INQ-L003-20260115',
    key: 'INQ-L003-20260115',
    type: 'Inquiry',
    clientId: 'L003',
    client: 'TDC NET A/S',
    date: '2026-01-15',
    requestedBy: 'Anders Petersen',
    priority: 'Medium',
    neededBefore: '2026-02-20',
    requestedDeliveryDate: '2026-02-27',
    productCategoryId: 2,
    productCategoryName: 'Drinkware & Tech',
    requestedQuantity: 600,
    budgetTargetPrice: 78000,
    printingDecorationType: 'Laser engraving',
    urgent: false,
    reorder: false,
    specialRequirements: '<p>TDC NET logo laser engraved on USB drives for the employee gift programme.</p>',
    notes: '<p>Deliver to TDC NET head office, Teglholmsgade 1, 2450 Copenhagen. Attention: Anders Petersen.</p>',
    status: 'Sent to Procurement',
    createdBy: 'Mads Mark-Buur',
    createdAt: '2026-01-15 10:00',
    attachments: [],
    country: 'Denmark',
    nextStep: 'Awaiting procurement pricing',
    archived: false,
    items: [
      {
        id: 'ITEM-tdc-usb',
        productId: 3,
        productName: 'Bags',
        specifications: '280gsm cotton canvas, 38 × 42 cm, long handles',
        productCategory: 'Bags',
        quantity: 600,
        brandingDescription: 'TDC NET logo, front panel print',
        deliveryRequirements: 'Bulk packaging, delivery to HQ',
        sourcingStatus: 'Quote Created',
        selectedSuppliers: [{ supplierId: 1, supplierName: 'Nordic Textiles A/S' }],
        files: [],
      },
    ],
  },
  {
    id: 'INQ-L003-20260120',
    key: 'INQ-L003-20260120',
    type: 'Inquiry',
    clientId: 'L003',
    client: 'TDC NET A/S',
    date: '2026-01-20',
    requestedBy: 'Anders Petersen',
    priority: 'Medium',
    neededBefore: '2026-02-25',
    requestedDeliveryDate: '2026-03-04',
    productCategoryId: 2,
    productCategoryName: 'Drinkware & Tech',
    requestedQuantity: 600,
    budgetTargetPrice: 128000,
    printingDecorationType: 'Laser engraving',
    urgent: false,
    reorder: false,
    specialRequirements: '<p>TDC NET logo laser engraved, matte black housing only.</p>',
    notes: '<p>Deliver to TDC NET head office, Teglholmsgade 1, 2450 Copenhagen. Attention: Anders Petersen.</p>',
    status: 'Sent to Procurement',
    createdBy: 'Mads Mark-Buur',
    createdAt: '2026-01-20 10:00',
    attachments: [],
    country: 'Denmark',
    nextStep: 'Awaiting procurement pricing',
    archived: false,
    items: [
      {
        id: 'ITEM-tdc-powerbank-2',
        productId: 16,
        productName: 'Power Banks',
        specifications: '5000mAh, USB-C input, matte black housing',
        productCategory: 'Drinkware & Tech',
        quantity: 600,
        brandingDescription: 'TDC NET logo laser engraved on back panel',
        deliveryRequirements: 'Bulk packaging, palletised delivery to HQ',
        sourcingStatus: 'Quote Created',
        selectedSuppliers: [
          { supplierId: 4, supplierName: 'TechGifts EU' },
          { supplierId: 8, supplierName: 'PromoGoods DK' },
        ],
        files: [],
      },
    ],
  },
  {
    id: 'INQ-L005-20260112',
    key: 'INQ-L005-20260112',
    type: 'Inquiry',
    clientId: 'L005',
    client: 'Danske Bank A/S',
    date: '2026-01-12',
    requestedBy: 'Thomas Christiansen',
    priority: 'High',
    neededBefore: '2026-02-15',
    requestedDeliveryDate: '2026-02-22',
    productCategoryId: 4,
    productCategoryName: 'Office & Giveaways',
    requestedQuantity: 500,
    budgetTargetPrice: 150000,
    printingDecorationType: 'Embossing',
    urgent: false,
    reorder: false,
    specialRequirements: '<p>Premium gift sets for VIP clients — Danske Bank wordmark embossed, navy and gold foil only.</p>',
    notes: '<p>Deliver to Holmens Kanal 2-12, 1092 Copenhagen. Attention: Thomas Christiansen, executive office.</p>',
    status: 'Sent to Procurement',
    createdBy: 'Mads Mark-Buur',
    createdAt: '2026-01-12 09:30',
    attachments: [],
    country: 'Denmark',
    nextStep: 'Awaiting procurement pricing',
    archived: false,
    items: [
      {
        id: 'ITEM-danskebank-cardholder-2',
        productId: 8,
        productName: 'Bottles',
        specifications: '750ml stainless steel, double-wall insulated',
        productCategory: 'Drinkware',
        quantity: 500,
        brandingDescription: 'Danske Bank wordmark, laser-engraved',
        deliveryRequirements: 'Individually boxed, gift-ready presentation',
        sourcingStatus: 'Quote Created',
        selectedSuppliers: [
          { supplierId: 6, supplierName: 'MetalGoods Scandinavia' },
          { supplierId: 4, supplierName: 'TechGifts EU' },
        ],
        files: [],
      },
    ],
  },
  {
    id: 'INQ-L005-20260117',
    key: 'INQ-L005-20260117',
    type: 'Inquiry',
    clientId: 'L005',
    client: 'Danske Bank A/S',
    date: '2026-01-17',
    requestedBy: 'Thomas Christiansen',
    priority: 'Medium',
    neededBefore: '2026-02-20',
    requestedDeliveryDate: '2026-02-27',
    productCategoryId: 4,
    productCategoryName: 'Office & Giveaways',
    requestedQuantity: 500,
    budgetTargetPrice: 90000,
    printingDecorationType: 'Debossing & foil print',
    urgent: false,
    reorder: false,
    specialRequirements: '<p>Premium gift sets for VIP clients — Danske Bank wordmark debossed, navy and gold foil only.</p>',
    notes: '<p>Deliver to Holmens Kanal 2-12, 1092 Copenhagen. Attention: Thomas Christiansen, executive office.</p>',
    status: 'Sent to Procurement',
    createdBy: 'Mads Mark-Buur',
    createdAt: '2026-01-17 09:30',
    attachments: [],
    country: 'Denmark',
    nextStep: 'Awaiting procurement pricing',
    archived: false,
    items: [
      {
        id: 'ITEM-danskebank-notebook-2',
        productId: 16,
        productName: 'Power Banks',
        specifications: '5000mAh, USB-A + USB-C output',
        productCategory: 'Electronics',
        quantity: 500,
        brandingDescription: 'Danske Bank wordmark, laser-engraved, top panel',
        deliveryRequirements: 'Individually boxed, gift-ready presentation',
        sourcingStatus: 'Quote Created',
        selectedSuppliers: [
          { supplierId: 4, supplierName: 'TechGifts EU' },
          { supplierId: 8, supplierName: 'PromoGoods DK' },
        ],
        files: [],
      },
    ],
  },
  {
    id: 'INQ-C002-20260118',
    key: 'INQ-C002-20260118',
    type: 'Inquiry',
    clientId: 'C002',
    client: 'Carlsberg Group',
    date: '2026-01-18',
    requestedBy: 'Maria Nielsen',
    priority: 'High',
    neededBefore: '2026-02-20',
    requestedDeliveryDate: '2026-02-27',
    productCategoryId: 1,
    productCategoryName: 'Textiles',
    requestedQuantity: 15000,
    budgetTargetPrice: 70000,
    printingDecorationType: 'Print',
    urgent: false,
    reorder: false,
    specialRequirements: '<p>Carlsberg red print on t-shirts, full-bleed print on coasters.</p>',
    notes: '<p>Deliver to Ny Carlsberg Vej 100, 1799 Copenhagen. Attention: Maria Nielsen.</p>',
    status: 'Sent to Procurement',
    createdBy: 'Peter Sørensen',
    createdAt: '2026-01-18 13:00',
    attachments: [],
    country: 'Denmark',
    nextStep: 'Awaiting procurement pricing',
    archived: false,
    items: [
      {
        id: 'ITEM-carlsberg-tshirt',
        productId: 4,
        productName: 'Mugs',
        specifications: '330ml ceramic, dishwasher safe',
        productCategory: 'Drinkware',
        quantity: 5000,
        brandingDescription: 'Carlsberg red print, wraparound',
        deliveryRequirements: 'Palletised delivery, 24 per carton',
        sourcingStatus: 'Quote Created',
        selectedSuppliers: [
          { supplierId: 3, supplierName: 'CeramicWorks DK' },
          { supplierId: 8, supplierName: 'PromoGoods DK' },
        ],
        files: [],
      },
      {
        id: 'ITEM-carlsberg-coaster',
        productId: null,
        isNewProduct: true,
        productName: 'Branded Beer Coasters',
        specifications: 'Full bleed print 100mm, matte finish both sides',
        productCategory: 'Textiles',
        quantity: 10000,
        brandingDescription: 'Carlsberg logo, full bleed print',
        deliveryRequirements: 'Packed in sleeves of 100',
        sourcingStatus: 'Quote Created',
        selectedSuppliers: [{ supplierId: 2, supplierName: 'PrintPartner ApS' }],
        files: [],
      },
    ],
  },
  {
    id: 'INQ-L001-20251205',
    key: 'INQ-L001-20251205',
    type: 'Inquiry',
    clientId: 'L001',
    client: 'Novo Nordisk A/S',
    date: '2025-12-05',
    requestedBy: 'Lars Jensen',
    priority: 'Medium',
    neededBefore: '2025-12-30',
    requestedDeliveryDate: '2026-01-06',
    productCategoryId: 4,
    productCategoryName: 'Office & Giveaways',
    requestedQuantity: 1100,
    budgetTargetPrice: 35000,
    printingDecorationType: 'Print & engraving',
    urgent: false,
    reorder: false,
    specialRequirements: '<p>Conference giveaway items, Novo Nordisk logo in Pantone 300C blue.</p>',
    notes: '<p>Deliver to HQ reception, Novo Allé, 2880 Bagsværd. Attention: Lars Jensen.</p>',
    status: 'Sent to Procurement',
    createdBy: 'Mads Mark-Buur',
    createdAt: '2025-12-05 09:00',
    attachments: [],
    country: 'Denmark',
    nextStep: 'Awaiting procurement pricing',
    archived: false,
    items: [
      {
        id: 'ITEM-novo-pen',
        productId: 3,
        productName: 'Bags',
        specifications: '280gsm cotton canvas, 38 × 42 cm, long handles',
        productCategory: 'Bags',
        quantity: 800,
        brandingDescription: 'Novo Nordisk logo, Pantone 300C blue',
        deliveryRequirements: 'Bulk packaging for conference handouts',
        sourcingStatus: 'Quote Created',
        selectedSuppliers: [
          { supplierId: 1, supplierName: 'Nordic Textiles A/S' },
          { supplierId: 2, supplierName: 'PrintPartner ApS' },
        ],
        files: [],
      },
      {
        id: 'ITEM-novo-usb',
        productId: 12,
        productName: 'Umbrellas',
        specifications: '130cm diameter, fibreglass ribs, auto-open',
        productCategory: 'Outdoor',
        quantity: 300,
        brandingDescription: 'Novo Nordisk logo, panel print',
        deliveryRequirements: 'Individually boxed for conference gifting',
        sourcingStatus: 'Quote Created',
        selectedSuppliers: [{ supplierId: 1, supplierName: 'Nordic Textiles A/S' }],
        files: [],
      },
    ],
  },
  {
    id: 'INQ-L007-20260123',
    key: 'INQ-L007-20260123',
    type: 'Inquiry',
    clientId: 'L007',
    client: 'Rambøll Group A/S',
    date: '2026-01-23',
    requestedBy: 'Henrik Sørensen',
    priority: 'Medium',
    neededBefore: '2026-02-28',
    requestedDeliveryDate: '2026-03-07',
    productCategoryId: 3,
    productCategoryName: 'Bags & Outdoor',
    requestedQuantity: 600,
    budgetTargetPrice: 100000,
    printingDecorationType: 'Print',
    urgent: false,
    reorder: false,
    specialRequirements: '<p>Employee anniversary event giveaways, Rambøll logo full-color print.</p>',
    notes: '<p>Deliver to Hannemanns Allé 53, 2300 Copenhagen. Attention: Henrik Sørensen.</p>',
    status: 'Sent to Procurement',
    createdBy: 'Mads Mark-Buur',
    createdAt: '2026-01-23 11:15',
    attachments: [],
    country: 'Denmark',
    nextStep: 'Awaiting procurement pricing',
    archived: false,
    items: [
      {
        id: 'ITEM-ramboll-tote',
        productId: 3,
        productName: 'Bags',
        specifications: 'Reusable shopping bags with custom print',
        productCategory: 'Bags & Outdoor',
        quantity: 600,
        brandingDescription: 'Rambøll logo, full-color one-side print',
        deliveryRequirements: 'Delivery for 200+ employee anniversary event',
        sourcingStatus: 'Quote Created',
        selectedSuppliers: [
          { supplierId: 1, supplierName: 'Nordic Textiles A/S' },
          { supplierId: 2, supplierName: 'PrintPartner ApS' },
        ],
        files: [],
      },
    ],
  },
  {
    id: 'INQ-L012-20251228',
    key: 'INQ-L012-20251228',
    type: 'Inquiry',
    clientId: 'L012',
    client: 'Saxo Bank A/S',
    date: '2025-12-28',
    requestedBy: 'Julie Andersen',
    priority: 'Medium',
    neededBefore: '2026-02-01',
    requestedDeliveryDate: '2026-02-08',
    productCategoryId: 4,
    productCategoryName: 'Office & Giveaways',
    requestedQuantity: 200,
    budgetTargetPrice: 40000,
    printingDecorationType: 'Embossing',
    urgent: false,
    reorder: false,
    specialRequirements: '<p>VIP client gift concept — premium leather card holders, understated branding.</p>',
    notes: '<p>Deliver to Philip Heymans Allé 15, 2900 Hellerup. Attention: Julie Andersen, private banking.</p>',
    status: 'Sent to Procurement',
    createdBy: 'Peter Sørensen',
    createdAt: '2025-12-28 10:00',
    attachments: [],
    country: 'Denmark',
    nextStep: 'Awaiting procurement pricing',
    archived: false,
    items: [
      {
        id: 'ITEM-saxo-cardholder-concept',
        productId: 8,
        productName: 'Bottles',
        specifications: '750ml stainless steel, double-wall insulated',
        productCategory: 'Drinkware',
        quantity: 200,
        brandingDescription: 'Saxo Bank monogram, laser-engraved',
        deliveryRequirements: 'Individually gift-boxed, presentation ribbon',
        sourcingStatus: 'Quote Created',
        selectedSuppliers: [
          { supplierId: 6, supplierName: 'MetalGoods Scandinavia' },
          { supplierId: 4, supplierName: 'TechGifts EU' },
        ],
        files: [],
      },
    ],
  },
];

// ── Inquiry_Item migration (inquiry-module-redesign, Requirement 2.6) ──────
// RAW_INQUIRIES above predates the wizard-based Inquiry_Item shape (its items
// carry the old productId/productName/specifications/selectedSuppliers
// fields). Rather than hand-edit ~28 literal item entries, each item is
// rewritten here into the new { categoryId, categoryName, quantity, reorder,
// sourcingStatus, wizardResult } shape: the item's old `productCategory`
// string is matched against initialProductCategories, and any legacy
// `selectedSuppliers` become the wizard's Manufacturing-role selection (the
// closest fit — the old data didn't distinguish product/service/logistics
// suppliers per item). An item only ever reached 'Quote Created' by
// completing all three Selection_Wizard steps (the Save gate requires
// Products, Services, AND a non-blank Logistics note), so a Service
// selection and a Logistics note are backfilled too — the old data didn't
// record which Service, so the first qualifying catalogue entry for that
// category stands in for it.
const findCategoryForLegacyName = (name = '') => (
  initialProductCategories.find((c) => name.toLowerCase().includes(c.name.toLowerCase())) || null
);

// Picks a real Supplier_Product row for the given (legacy) supplier + category
// so the migrated ref's `supplierProductId` resolves to an actual Products
// step row (the Selection Wizard matches selections by that id — a null or
// made-up id shows as "nothing selected" when the wizard reopens even
// though the Inquiry item list's Sourcing column, which only reads
// supplierId/supplierName, displays it fine). Falls back to any qualifying
// record in the category if this exact supplier doesn't carry one.
const pickProductRef = (supplierId, categoryId) => {
  const bySupplier = initialSupplierProducts.find((sp) => (
    sp.supplierId === supplierId && sp.categoryId === categoryId && sp.avgItemPrice != null
  ));
  const record = bySupplier || initialSupplierProducts.find((sp) => sp.categoryId === categoryId && sp.avgItemPrice != null);
  return record ? toSupplierProductRef(record, initialSuppliers) : null;
};

// Same idea for Services, but sourced from its own catalogue
// (initialServiceItems) — a different id space from Supplier_Product —
// since that's what the wizard's Services step actually matches selections
// against.
const pickCatalogRef = (catalogItems) => {
  const entry = catalogItems.find((item) => item.status !== 'Inactive') || catalogItems[0];
  return entry ? [toCatalogItemRef(entry, initialSuppliers)] : [];
};

// Logistics is a free-text note on the wizard (not a catalogue selection) —
// stand in with a generic note referencing the first active Logistics
// catalogue entry, since the legacy data didn't record shipping details.
const legacyLogisticsNote = () => {
  const entry = initialLogisticsItems.find((item) => item.status !== 'Inactive') || initialLogisticsItems[0];
  return entry ? `Standard ${entry.name}.` : '';
};

const migrateLegacyInquiryItem = (item) => {
  const category = findCategoryForLegacyName(item.productCategory || item.categoryName || '');
  const legacySuppliers = item.selectedSuppliers || [];
  const wizardResult = legacySuppliers.length > 0 ? {
    selectedProducts: legacySuppliers
      .map((supplier) => pickProductRef(supplier.supplierId, category?.id))
      .filter(Boolean),
    selectedServices: pickCatalogRef(initialServiceItems),
    logisticsNotes: legacyLogisticsNote(),
  } : null;

  return {
    id: item.id,
    categoryId: category?.id ?? null,
    categoryName: category?.name || item.productCategory || '',
    quantity: item.quantity ?? 1,
    specifications: item.specifications || '',
    targetPrice: item.targetPrice ?? null,
    brandingDescription: item.brandingDescription || '',
    deliveryRequirements: item.deliveryRequirements || '',
    reorder: Boolean(item.reorder),
    // 'Quote Created' is a real downstream state (a Quote already exists);
    // any other legacy status ('Suppliers Selected', etc.) collapses to the
    // two states the redesign actually has: 'Sourced' once wizardResult is
    // complete, otherwise 'Pending'.
    sourcingStatus: item.sourcingStatus === 'Quote Created' ? 'Quote Created' : (wizardResult ? 'Sourced' : 'Pending'),
    wizardResult,
    files: item.files || [],
  };
};

export const initialInquiries = RAW_INQUIRIES.map((inquiry) => ({
  ...inquiry,
  items: (inquiry.items || []).map(migrateLegacyInquiryItem),
}));

// Quotes are created from an Inquiry once it is sent to Procurement — they carry
// the Inquiry's header information plus the product/supplier quotation work.
// Every Quote follows the one-product-per-Quote pipeline: a multi-product
// Inquiry produces one Quote per Inquiry_Item (see buildQuotesFromInquiry),
// each carrying its own `suppliers` (sourced from the Inquiry item) and
// `supplierQuotes` cost entries. A legacy multi-item Inquiry that already had
// several quoted products therefore now has several sibling Quotes, one per
// product — the first keeps the original Quote ID, the rest are suffixed
// (`-2`, `-3`, ...) so existing comment/notification/offer references that
// point at the original ID keep resolving to the right product line.
const initialQuotesRaw = [
  {
    id: 'QUO-L001-20260205',
    key: 'QUO-L001-20260205',
    type: 'Quote',
    inquiryId: 'INQ-L001-20260205',
    inquiryItemId: 'ITEM-novo-tshirt',
    clientId: 'L001',
    client: 'Novo Nordisk A/S',
    requestedBy: 'Lars Jensen',
    priority: 'High',
    neededBefore: '2026-03-01',
    requestedDeliveryDate: '2026-03-08',
    productId: 8,
    productName: 'Bottles',
    specifications: '750ml stainless steel, double-wall insulated',
    productCategory: 'Drinkware',
    quantity: 200,
    brandingDescription: 'Laser-engraved logo, single position',
    deliveryRequirements: 'Individual gift box, 24 per carton',
    urgent: true,
    reorder: false,
    status: 'Sent to Sales Quote',
    country: 'Denmark',
    nextStep: 'Converted to offer',
    archived: false,
    createdBy: 'Mads Mark-Buur',
    createdAt: '2026-02-05 11:30',
    convertedOfferId: 'NOV-20260201',
    files: [
      {
        uid: 'inq-novo-shirt-logo',
        name: 'Novo_bottle_logo_reference.jpg',
        status: 'done',
        url: 'https://picsum.photos/seed/novo-tshirt/300/300',
      },
    ],
    suppliers: [
      { supplierId: 6, supplierName: 'MetalGoods Scandinavia' },
      { supplierId: 4, supplierName: 'TechGifts EU' },
    ],
    supplierQuotes: {
      6: {
        status: 'accepted',
        lines: [{
          id: 'QUO-L001-20260205-1-line-1',
          quantity: 200,
          productDetails: '750ml stainless steel, double-wall insulated',
          unitPrice: 66,
          brandingDecoration: 'Laser-engraved logo, single position',
          productionTime: 14,
          shippingTime: 7,
          shippingCost: 3,
          validUntil: '2026-03-05',
          files: [],
          comments: 'Confirmed stock availability for full quantity',
        }],
      },
      4: {
        status: 'rejected',
        lines: [{
          id: 'QUO-L001-20260205-2-line-1',
          quantity: 200,
          productDetails: '750ml stainless steel, double-wall insulated',
          unitPrice: 72,
          brandingDecoration: 'Laser-engraved logo, single position',
          productionTime: 16,
          shippingTime: 8,
          shippingCost: 4,
          validUntil: '2026-03-05',
          files: [],
          comments: 'Higher price and longer lead time than MetalGoods Scandinavia — not selected.',
        }],
      },
    },
  },
  {
    id: 'QUO-L001-20260205-2',
    key: 'QUO-L001-20260205-2',
    type: 'Quote',
    inquiryId: 'INQ-L001-20260205',
    inquiryItemId: 'ITEM-novo-notebook',
    clientId: 'L001',
    client: 'Novo Nordisk A/S',
    requestedBy: 'Lars Jensen',
    priority: 'High',
    neededBefore: '2026-03-01',
    requestedDeliveryDate: '2026-03-08',
    productId: 16,
    productName: 'Power Banks',
    specifications: '5000mAh, USB-A + USB-C output',
    productCategory: 'Electronics',
    quantity: 200,
    brandingDescription: 'Laser-engraved logo, top panel',
    deliveryRequirements: 'Same delivery as water bottles',
    urgent: true,
    reorder: false,
    status: 'Sent to Sales Quote',
    country: 'Denmark',
    nextStep: 'Converted to offer',
    archived: false,
    createdBy: 'Mads Mark-Buur',
    createdAt: '2026-02-05 11:30',
    convertedOfferId: 'NOV-20260201',
    suppliers: [
      { supplierId: 4, supplierName: 'TechGifts EU' },
      { supplierId: 8, supplierName: 'PromoGoods DK' },
    ],
    supplierQuotes: {
      4: {
        status: 'accepted',
        lines: [{
          id: 'QUO-L001-20260205-2-5-line-1',
          quantity: 200,
          productDetails: '5000mAh, USB-A + USB-C output',
          unitPrice: 32,
          brandingDecoration: 'Laser-engraved logo, top panel',
          productionTime: 7,
          shippingTime: 7,
          shippingCost: 2,
          validUntil: '2026-03-05',
          files: [],
          comments: 'Confirmed component stock and engraving capacity.',
        }],
      },
      8: {
        status: 'rejected',
        lines: [{
          id: 'QUO-L001-20260205-2-2-line-1',
          quantity: 200,
          productDetails: '5000mAh, USB-A + USB-C output',
          unitPrice: 36,
          brandingDecoration: 'Laser-engraved logo, top panel',
          productionTime: 9,
          shippingTime: 7,
          shippingCost: 3,
          validUntil: '2026-03-05',
          files: [],
          comments: 'More expensive than TechGifts EU for the same spec — not selected.',
        }],
      },
    },
  },
  {
    id: 'QUO-C002-20260207',
    key: 'QUO-C002-20260207',
    type: 'Quote',
    inquiryId: 'INQ-C002-20260207',
    inquiryItemId: 'ITEM-carlsberg-cooler',
    clientId: 'C002',
    client: 'Carlsberg Group',
    requestedBy: 'Maria Nielsen',
    priority: 'Medium',
    neededBefore: '2026-04-01',
    requestedDeliveryDate: '2026-04-10',
    productId: 4,
    productName: 'Mugs',
    specifications: '330ml ceramic, dishwasher safe',
    productCategory: 'Drinkware',
    quantity: 500,
    brandingDescription: 'Full-colour logo print, wraparound, Carlsberg logo',
    deliveryRequirements: 'Individual gift box, 24 per carton',
    urgent: false,
    reorder: false,
    status: 'Ready for Procurement',
    country: 'Denmark',
    nextStep: 'Awaiting supplier pricing',
    archived: false,
    createdBy: 'Peter Sørensen',
    createdAt: '2026-02-07 15:10',
    convertedOfferId: null,
    files: [
      {
        uid: 'inq-carlsberg-cooler-mockup',
        name: 'Carlsberg_cooler_bag_mockup.jpg',
        status: 'done',
        url: 'https://picsum.photos/seed/carlsberg-cooler/300/300',
      },
    ],
    suppliers: [
      { supplierId: 3, supplierName: 'CeramicWorks DK' },
      { supplierId: 8, supplierName: 'PromoGoods DK' },
    ],
    supplierQuotes: {},
  },
  {
    id: 'QUO-C002-20260207-2',
    key: 'QUO-C002-20260207-2',
    type: 'Quote',
    inquiryId: 'INQ-C002-20260207',
    inquiryItemId: 'ITEM-carlsberg-rainjacket',
    clientId: 'C002',
    client: 'Carlsberg Group',
    requestedBy: 'Maria Nielsen',
    priority: 'Medium',
    neededBefore: '2026-04-01',
    requestedDeliveryDate: '2026-04-10',
    productId: null,
    productName: 'Branded Polo Rain Jacket',
    specifications: 'Waterproof shell, taped seams, embroidered chest logo',
    productCategory: 'Textiles',
    quantity: 150,
    brandingDescription: 'Embroidered chest logo, Pantone 356C',
    deliveryRequirements: 'Size run: S×30, M×50, L×40, XL×20, XXL×10',
    urgent: false,
    reorder: false,
    status: 'Ready for Procurement',
    country: 'Denmark',
    nextStep: 'Awaiting supplier pricing',
    archived: false,
    createdBy: 'Peter Sørensen',
    createdAt: '2026-02-07 15:10',
    convertedOfferId: null,
    suppliers: [
      { supplierId: 8, supplierName: 'PromoGoods DK' },
      { supplierId: 1, supplierName: 'Nordic Textiles A/S' },
    ],
    supplierQuotes: {},
  },
  {
    id: 'QUO-C004-20260128',
    key: 'QUO-C004-20260128',
    type: 'Quote',
    inquiryId: 'INQ-C004-20260128',
    inquiryItemId: 'ITEM-dancrown-polo',
    clientId: 'C004',
    client: 'Danish Crown A/S',
    requestedBy: 'Mette Hansen',
    priority: 'High',
    neededBefore: '2026-03-10',
    requestedDeliveryDate: '2026-03-17',
    productId: 8,
    productName: 'Bottles',
    specifications: '750ml stainless steel, double-wall insulated',
    productCategory: 'Drinkware',
    quantity: 300,
    brandingDescription: 'Laser-engraved Danish Crown logo, single position',
    deliveryRequirements: 'Sample approved by HR before full production',
    urgent: true,
    reorder: false,
    status: 'Sent to Sales Quote',
    country: 'Denmark',
    nextStep: 'Converted to offer',
    archived: false,
    createdBy: 'Maria Kristensen',
    createdAt: '2026-01-28 13:45',
    convertedOfferId: 'DAN-20260128',
    files: [
      {
        uid: 'inq-dancrown-polo-sample',
        name: 'Danish_Crown_polo_sample.jpg',
        status: 'done',
        url: 'https://picsum.photos/seed/dancrown-polo/300/300',
      },
    ],
    suppliers: [
      { supplierId: 6, supplierName: 'MetalGoods Scandinavia' },
      { supplierId: 4, supplierName: 'TechGifts EU' },
    ],
    supplierQuotes: {
      6: {
        supplierId: 6,
        productDetails: '750ml stainless steel, double-wall insulated',
        unitPrice: 81,
        brandingDecoration: 'Laser-engraved Danish Crown logo, single position',
        productionTime: 14,
        shippingTime: 7,
        shippingCost: 4,
        validUntil: '2026-02-27',
        files: [],
        comments: 'Sample approved by HR',
      },
    },
  },
  {
    id: 'QUO-C004-20260128-2',
    key: 'QUO-C004-20260128-2',
    type: 'Quote',
    inquiryId: 'INQ-C004-20260128',
    inquiryItemId: 'ITEM-dancrown-cap',
    clientId: 'C004',
    client: 'Danish Crown A/S',
    requestedBy: 'Mette Hansen',
    priority: 'High',
    neededBefore: '2026-03-10',
    requestedDeliveryDate: '2026-03-17',
    productId: 16,
    productName: 'Power Banks',
    specifications: '5000mAh, USB-A + USB-C output',
    productCategory: 'Electronics',
    quantity: 300,
    brandingDescription: 'Laser-engraved logo, top panel',
    deliveryRequirements: 'Same delivery as water bottles',
    urgent: true,
    reorder: false,
    status: 'Sent to Sales Quote',
    country: 'Denmark',
    nextStep: 'Converted to offer',
    archived: false,
    createdBy: 'Maria Kristensen',
    createdAt: '2026-01-28 13:45',
    convertedOfferId: 'DAN-20260128',
    suppliers: [
      { supplierId: 4, supplierName: 'TechGifts EU' },
      { supplierId: 8, supplierName: 'PromoGoods DK' },
    ],
    supplierQuotes: {
      4: {
        supplierId: 4,
        productDetails: '5000mAh, USB-A + USB-C output',
        unitPrice: 54,
        brandingDecoration: 'Laser-engraved logo, top panel',
        productionTime: 7,
        shippingTime: 7,
        shippingCost: 3,
        validUntil: '2026-02-27',
        files: [],
        comments: '',
      },
    },
  },
  {
    id: 'QUO-C004-20260128-3',
    key: 'QUO-C004-20260128-3',
    type: 'Quote',
    inquiryId: 'INQ-C004-20260128',
    inquiryItemId: 'ITEM-dancrown-vest',
    clientId: 'C004',
    client: 'Danish Crown A/S',
    requestedBy: 'Mette Hansen',
    priority: 'High',
    neededBefore: '2026-03-10',
    requestedDeliveryDate: '2026-03-17',
    productId: null,
    productName: 'Hi-Vis Branded Safety Vests',
    specifications: 'EN ISO 20471 compliant, reflective strips, printed logo',
    productCategory: 'Textiles',
    quantity: 250,
    brandingDescription: 'Reflective strips, printed Danish Crown logo on back panel',
    deliveryRequirements: 'EN ISO 20471 compliant, size run S–XXL enclosed',
    urgent: true,
    reorder: false,
    status: 'Quotation in Process',
    country: 'Denmark',
    nextStep: 'Pending compliance certificate from supplier',
    archived: false,
    createdBy: 'Maria Kristensen',
    createdAt: '2026-01-28 13:45',
    convertedOfferId: null,
    suppliers: [
      { supplierId: 8, supplierName: 'PromoGoods DK' },
      { supplierId: 1, supplierName: 'Nordic Textiles A/S' },
    ],
    supplierQuotes: {
      8: {
        supplierId: 8,
        productDetails: 'EN ISO 20471 compliant, reflective strips, printed logo',
        unitPrice: 48,
        brandingDecoration: 'Reflective strips, printed Danish Crown logo on back panel',
        productionTime: 21,
        shippingTime: 7,
        shippingCost: 2,
        validUntil: '2026-02-27',
        files: [],
        comments: 'New supplier quote pending compliance certificate',
      },
    },
  },
  {
    id: 'QUO-L011-20260112',
    key: 'QUO-L011-20260112',
    type: 'Quote',
    inquiryId: 'INQ-L011-20260112',
    inquiryItemId: 'ITEM-cbs-notebook',
    clientId: 'L011',
    client: 'Copenhagen Business School',
    requestedBy: 'Michael Thomsen',
    priority: 'Medium',
    neededBefore: '2026-08-01',
    requestedDeliveryDate: '2026-08-10',
    productId: 4,
    productName: 'Mugs',
    specifications: '330ml ceramic, dishwasher safe',
    productCategory: 'Drinkware',
    quantity: 800,
    brandingDescription: 'CBS crest full-colour print, navy',
    deliveryRequirements: 'Deliver to campus warehouse, attention facilities team',
    urgent: false,
    reorder: true,
    status: 'Sent to Sales Quote',
    country: 'Denmark',
    nextStep: 'Converted to offer',
    archived: false,
    createdBy: 'Mads Mark-Buur',
    createdAt: '2026-01-12 10:30',
    convertedOfferId: 'COP-20260112',
    files: [
      {
        uid: 'inq-cbs-notebook-cover',
        name: 'CBS_notebook_cover_proof.jpg',
        status: 'done',
        url: 'https://picsum.photos/seed/cbs-notebook/300/300',
      },
    ],
    suppliers: [
      { supplierId: 3, supplierName: 'CeramicWorks DK' },
      { supplierId: 8, supplierName: 'PromoGoods DK' },
    ],
    supplierQuotes: {
      3: {
        supplierId: 3,
        productDetails: '330ml ceramic, dishwasher safe',
        unitPrice: 32,
        brandingDecoration: 'CBS crest full-colour print, navy',
        productionTime: 7,
        shippingTime: 7,
        shippingCost: 2,
        validUntil: '2026-02-11',
        files: [],
        comments: 'Confirmed ceramic stock availability',
        selected: true,
      },
    },
  },
  {
    id: 'QUO-L011-20260112-2',
    key: 'QUO-L011-20260112-2',
    type: 'Quote',
    inquiryId: 'INQ-L011-20260112',
    inquiryItemId: 'ITEM-cbs-pen',
    clientId: 'L011',
    client: 'Copenhagen Business School',
    requestedBy: 'Michael Thomsen',
    priority: 'Medium',
    neededBefore: '2026-08-01',
    requestedDeliveryDate: '2026-08-10',
    productId: 12,
    productName: 'Umbrellas',
    specifications: '130cm diameter, fibreglass ribs, auto-open',
    productCategory: 'Outdoor',
    quantity: 800,
    brandingDescription: 'CBS crest panel print, navy and white',
    deliveryRequirements: 'Same delivery as coffee mugs',
    urgent: false,
    reorder: true,
    status: 'Ready for Procurement',
    country: 'Denmark',
    nextStep: 'Awaiting supplier pricing',
    archived: false,
    createdBy: 'Mads Mark-Buur',
    createdAt: '2026-01-12 10:30',
    convertedOfferId: null,
    suppliers: [
      { supplierId: 1, supplierName: 'Nordic Textiles A/S' },
    ],
    supplierQuotes: {},
  },
  {
    id: 'QUO-L011-20260112-3',
    key: 'QUO-L011-20260112-3',
    type: 'Quote',
    inquiryId: 'INQ-L011-20260112',
    inquiryItemId: 'ITEM-cbs-tote',
    clientId: 'L011',
    client: 'Copenhagen Business School',
    requestedBy: 'Michael Thomsen',
    priority: 'Medium',
    neededBefore: '2026-08-01',
    requestedDeliveryDate: '2026-08-10',
    productId: 3,
    productName: 'Bags',
    specifications: 'Natural canvas, one colour print',
    productCategory: 'Bags & Outdoor',
    quantity: 800,
    brandingDescription: 'CBS crest print, one colour, natural canvas',
    deliveryRequirements: 'Same delivery as notebooks',
    urgent: false,
    reorder: true,
    status: 'Ready for Procurement',
    country: 'Denmark',
    nextStep: 'Awaiting supplier pricing',
    archived: false,
    createdBy: 'Mads Mark-Buur',
    createdAt: '2026-01-12 10:30',
    convertedOfferId: null,
    suppliers: [
      { supplierId: 1, supplierName: 'Nordic Textiles A/S' },
      { supplierId: 2, supplierName: 'PrintPartner ApS' },
    ],
    supplierQuotes: {},
  },
  {
    id: 'QUO-L008-20260126',
    key: 'QUO-L008-20260126',
    type: 'Quote',
    inquiryId: 'INQ-L008-20260126',
    inquiryItemId: 'ITEM-bestseller-tshirt',
    clientId: 'L008',
    client: 'Bestseller A/S',
    requestedBy: 'Sofie Larsen',
    priority: 'Medium',
    neededBefore: '2026-03-15',
    requestedDeliveryDate: '2026-03-22',
    productId: 3,
    productName: 'Bags',
    specifications: '280gsm cotton canvas, 38 × 42 cm, long handles',
    productCategory: 'Bags',
    quantity: 800,
    brandingDescription: 'Front panel logo print, single colour, Bestseller wordmark',
    deliveryRequirements: 'Split delivery across 50 retail stores',
    urgent: false,
    reorder: true,
    status: 'Sent to Sales Quote',
    country: 'Denmark',
    nextStep: 'Converted to offer',
    archived: false,
    createdBy: 'Emma Andersen',
    createdAt: '2026-01-26 15:00',
    convertedOfferId: 'BES-20260126',
    suppliers: [
      { supplierId: 1, supplierName: 'Nordic Textiles A/S' },
      { supplierId: 2, supplierName: 'PrintPartner ApS' },
    ],
    supplierQuotes: {
      1: {
        supplierId: 1,
        productDetails: '280gsm cotton canvas, 38 × 42 cm, long handles',
        unitPrice: 62,
        brandingDecoration: 'Front panel logo print, single colour, Bestseller wordmark',
        productionTime: 14,
        shippingTime: 7,
        shippingCost: 5,
        validUntil: '2026-02-25',
        files: [],
        comments: 'Bulk discount applied for 800-unit retail rollout',
        selected: true,
      },
    },
  },
  // Quotes backing the legacy standalone Offers (see Inquiries above).
  {
    id: 'QUO-L003-20260115',
    key: 'QUO-L003-20260115',
    type: 'Quote',
    inquiryId: 'INQ-L003-20260115',
    inquiryItemId: 'ITEM-tdc-usb',
    clientId: 'L003',
    client: 'TDC NET A/S',
    requestedBy: 'Anders Petersen',
    priority: 'Medium',
    neededBefore: '2026-02-20',
    requestedDeliveryDate: '2026-02-27',
    productId: 3,
    productName: 'Bags',
    specifications: '280gsm cotton canvas, 38 × 42 cm, long handles',
    productCategory: 'Bags',
    quantity: 600,
    brandingDescription: 'TDC NET logo, front panel print',
    deliveryRequirements: 'Bulk packaging, delivery to HQ',
    urgent: false,
    reorder: false,
    status: 'Sent to Sales Quote',
    country: 'Denmark',
    nextStep: 'Converted to offer',
    archived: false,
    createdBy: 'Mads Mark-Buur',
    createdAt: '2026-01-15 11:00',
    convertedOfferId: 'TDC-20260124',
    suppliers: [{ supplierId: 1, supplierName: 'Nordic Textiles A/S' }],
    supplierQuotes: {
      1: {
        status: 'accepted',
        lines: [{
          id: 'QUO-L003-20260115-4-line-1',
          quantity: 600,
          productDetails: '280gsm cotton canvas, 38 × 42 cm, long handles',
          unitPrice: 128,
          brandingDecoration: 'TDC NET logo, front panel print',
          productionTime: 10,
          shippingTime: 5,
          shippingCost: 4,
          validUntil: '2026-02-15',
          files: [],
          comments: 'Confirmed production capacity for 600-unit run.',
        }],
      },
    },
  },
  {
    id: 'QUO-L003-20260120',
    key: 'QUO-L003-20260120',
    type: 'Quote',
    inquiryId: 'INQ-L003-20260120',
    inquiryItemId: 'ITEM-tdc-powerbank-2',
    clientId: 'L003',
    client: 'TDC NET A/S',
    requestedBy: 'Anders Petersen',
    priority: 'Medium',
    neededBefore: '2026-02-25',
    requestedDeliveryDate: '2026-03-04',
    productId: 16,
    productName: 'Power Banks',
    specifications: '5000mAh, USB-C input, matte black housing',
    productCategory: 'Drinkware & Tech',
    quantity: 600,
    brandingDescription: 'TDC NET logo laser engraved on back panel',
    deliveryRequirements: 'Bulk packaging, palletised delivery to HQ',
    urgent: false,
    reorder: false,
    status: 'Sent to Sales Quote',
    country: 'Denmark',
    nextStep: 'Converted to offer',
    archived: false,
    createdBy: 'Mads Mark-Buur',
    createdAt: '2026-01-20 11:00',
    convertedOfferId: 'TDC-20260129',
    suppliers: [
      { supplierId: 4, supplierName: 'TechGifts EU' },
      { supplierId: 8, supplierName: 'PromoGoods DK' },
    ],
    supplierQuotes: {
      4: {
        status: 'accepted',
        lines: [{
          id: 'QUO-L003-20260120-4-line-1',
          quantity: 600,
          productDetails: '5000mAh, USB-C input, matte black housing',
          unitPrice: 209,
          brandingDecoration: 'TDC NET logo laser engraved on back panel',
          productionTime: 12,
          shippingTime: 6,
          shippingCost: 5,
          validUntil: '2026-02-20',
          files: [],
          comments: 'Confirmed stock for matte black housing in required volume.',
        }],
      },
      8: {
        status: 'rejected',
        lines: [{
          id: 'QUO-L003-20260120-8-line-1',
          quantity: 600,
          productDetails: '5000mAh, USB-C input, matte black housing',
          unitPrice: 224,
          brandingDecoration: 'TDC NET logo laser engraved on back panel',
          productionTime: 15,
          shippingTime: 8,
          shippingCost: 6,
          validUntil: '2026-02-20',
          files: [],
          comments: 'Higher price and longer lead time than TechGifts EU — not selected.',
        }],
      },
    },
  },
  {
    id: 'QUO-L005-20260112',
    key: 'QUO-L005-20260112',
    type: 'Quote',
    inquiryId: 'INQ-L005-20260112',
    inquiryItemId: 'ITEM-danskebank-cardholder-2',
    clientId: 'L005',
    client: 'Danske Bank A/S',
    requestedBy: 'Thomas Christiansen',
    priority: 'High',
    neededBefore: '2026-02-15',
    requestedDeliveryDate: '2026-02-22',
    productId: 8,
    productName: 'Bottles',
    specifications: '750ml stainless steel, double-wall insulated',
    productCategory: 'Drinkware',
    quantity: 500,
    brandingDescription: 'Danske Bank wordmark, laser-engraved',
    deliveryRequirements: 'Individually boxed, gift-ready presentation',
    urgent: false,
    reorder: false,
    status: 'Sent to Sales Quote',
    country: 'Denmark',
    nextStep: 'Converted to offer',
    archived: false,
    createdBy: 'Mads Mark-Buur',
    createdAt: '2026-01-12 10:00',
    convertedOfferId: 'DAN-20260119',
    suppliers: [
      { supplierId: 6, supplierName: 'MetalGoods Scandinavia' },
      { supplierId: 4, supplierName: 'TechGifts EU' },
    ],
    supplierQuotes: {
      6: {
        status: 'accepted',
        lines: [{
          id: 'QUO-L005-20260112-5-line-1',
          quantity: 500,
          productDetails: '750ml stainless steel, double-wall insulated',
          unitPrice: 165,
          brandingDecoration: 'Danske Bank wordmark, laser-engraved',
          productionTime: 12,
          shippingTime: 6,
          shippingCost: 4,
          validUntil: '2026-02-05',
          files: [],
          comments: 'Confirmed steel stock and engraving capacity.',
        }],
      },
      4: {
        status: 'rejected',
        lines: [{
          id: 'QUO-L005-20260112-2-line-1',
          quantity: 500,
          productDetails: '750ml stainless steel, double-wall insulated',
          unitPrice: 178,
          brandingDecoration: 'Danske Bank wordmark, laser-engraved',
          productionTime: 15,
          shippingTime: 7,
          shippingCost: 5,
          validUntil: '2026-02-05',
          files: [],
          comments: 'More expensive than MetalGoods Scandinavia for the same spec — not selected.',
        }],
      },
    },
  },
  {
    id: 'QUO-L005-20260117',
    key: 'QUO-L005-20260117',
    type: 'Quote',
    inquiryId: 'INQ-L005-20260117',
    inquiryItemId: 'ITEM-danskebank-notebook-2',
    clientId: 'L005',
    client: 'Danske Bank A/S',
    requestedBy: 'Thomas Christiansen',
    priority: 'Medium',
    neededBefore: '2026-02-20',
    requestedDeliveryDate: '2026-02-27',
    productId: 16,
    productName: 'Power Banks',
    specifications: '5000mAh, USB-A + USB-C output',
    productCategory: 'Electronics',
    quantity: 500,
    brandingDescription: 'Danske Bank wordmark, laser-engraved, top panel',
    deliveryRequirements: 'Individually boxed, gift-ready presentation',
    urgent: false,
    reorder: false,
    status: 'Sent to Sales Quote',
    country: 'Denmark',
    nextStep: 'Converted to offer',
    archived: false,
    createdBy: 'Mads Mark-Buur',
    createdAt: '2026-01-17 10:00',
    convertedOfferId: 'DAN-20260126',
    suppliers: [
      { supplierId: 4, supplierName: 'TechGifts EU' },
      { supplierId: 8, supplierName: 'PromoGoods DK' },
    ],
    supplierQuotes: {
      4: {
        status: 'accepted',
        lines: [{
          id: 'QUO-L005-20260117-5-line-1',
          quantity: 500,
          productDetails: '5000mAh, USB-A + USB-C output',
          unitPrice: 95,
          brandingDecoration: 'Danske Bank wordmark, laser-engraved, top panel',
          productionTime: 10,
          shippingTime: 5,
          shippingCost: 3,
          validUntil: '2026-02-10',
          files: [],
          comments: 'Confirmed component stock and engraving capacity.',
        }],
      },
      8: {
        status: 'rejected',
        lines: [{
          id: 'QUO-L005-20260117-2-line-1',
          quantity: 500,
          productDetails: '5000mAh, USB-A + USB-C output',
          unitPrice: 104,
          brandingDecoration: 'Danske Bank wordmark, laser-engraved, top panel',
          productionTime: 13,
          shippingTime: 6,
          shippingCost: 4,
          validUntil: '2026-02-10',
          files: [],
          comments: 'More expensive than TechGifts EU for the same spec — not selected.',
        }],
      },
    },
  },
  {
    id: 'QUO-C002-20260118',
    key: 'QUO-C002-20260118',
    type: 'Quote',
    inquiryId: 'INQ-C002-20260118',
    inquiryItemId: 'ITEM-carlsberg-tshirt',
    clientId: 'C002',
    client: 'Carlsberg Group',
    requestedBy: 'Maria Nielsen',
    priority: 'High',
    neededBefore: '2026-02-20',
    requestedDeliveryDate: '2026-02-27',
    productId: 4,
    productName: 'Mugs',
    specifications: '330ml ceramic, dishwasher safe',
    productCategory: 'Drinkware',
    quantity: 5000,
    brandingDescription: 'Carlsberg red print, wraparound',
    deliveryRequirements: 'Palletised delivery, 24 per carton',
    urgent: false,
    reorder: false,
    status: 'Sent to Sales Quote',
    country: 'Denmark',
    nextStep: 'Converted to offer',
    archived: false,
    createdBy: 'Peter Sørensen',
    createdAt: '2026-01-18 14:00',
    convertedOfferId: 'CAR-20260123',
    suppliers: [
      { supplierId: 3, supplierName: 'CeramicWorks DK' },
      { supplierId: 8, supplierName: 'PromoGoods DK' },
    ],
    supplierQuotes: {
      3: {
        status: 'accepted',
        lines: [{
          id: 'QUO-C002-20260118-1-line-1',
          quantity: 5000,
          productDetails: '330ml ceramic, dishwasher safe',
          unitPrice: 12,
          brandingDecoration: 'Carlsberg red print, wraparound',
          productionTime: 16,
          shippingTime: 7,
          shippingCost: 1,
          validUntil: '2026-02-05',
          files: [],
          comments: 'Confirmed stock availability for the full quantity.',
        }],
      },
      8: {
        status: 'rejected',
        lines: [{
          id: 'QUO-C002-20260118-8-line-1',
          quantity: 5000,
          productDetails: '330ml ceramic, dishwasher safe',
          unitPrice: 14,
          brandingDecoration: 'Carlsberg red print, wraparound',
          productionTime: 18,
          shippingTime: 9,
          shippingCost: 2,
          validUntil: '2026-02-05',
          files: [],
          comments: 'Higher price and longer lead time than CeramicWorks DK — not selected.',
        }],
      },
    },
  },
  {
    id: 'QUO-C002-20260118-2',
    key: 'QUO-C002-20260118-2',
    type: 'Quote',
    inquiryId: 'INQ-C002-20260118',
    inquiryItemId: 'ITEM-carlsberg-coaster',
    clientId: 'C002',
    client: 'Carlsberg Group',
    requestedBy: 'Maria Nielsen',
    priority: 'High',
    neededBefore: '2026-02-20',
    requestedDeliveryDate: '2026-02-27',
    productId: null,
    productName: 'Branded Beer Coasters',
    specifications: 'Full bleed print 100mm, matte finish both sides',
    productCategory: 'Textiles',
    quantity: 10000,
    brandingDescription: 'Carlsberg logo, full bleed print',
    deliveryRequirements: 'Packed in sleeves of 100',
    urgent: false,
    reorder: false,
    status: 'Sent to Sales Quote',
    country: 'Denmark',
    nextStep: 'Converted to offer',
    archived: false,
    createdBy: 'Peter Sørensen',
    createdAt: '2026-01-18 14:00',
    convertedOfferId: 'CAR-20260123',
    suppliers: [{ supplierId: 2, supplierName: 'PrintPartner ApS' }],
    supplierQuotes: {
      2: {
        status: 'accepted',
        lines: [{
          id: 'QUO-C002-20260118-2-2-line-1',
          quantity: 10000,
          productDetails: 'Full bleed print 100mm, matte finish both sides',
          unitPrice: 0.75,
          brandingDecoration: 'Carlsberg logo, full bleed print',
          productionTime: 10,
          shippingTime: 5,
          shippingCost: 1,
          validUntil: '2026-02-05',
          files: [],
          comments: 'Confirmed matte finish stock for full quantity.',
        }],
      },
    },
  },
  {
    id: 'QUO-L001-20251205',
    key: 'QUO-L001-20251205',
    type: 'Quote',
    inquiryId: 'INQ-L001-20251205',
    inquiryItemId: 'ITEM-novo-pen',
    clientId: 'L001',
    client: 'Novo Nordisk A/S',
    requestedBy: 'Lars Jensen',
    priority: 'Medium',
    neededBefore: '2025-12-30',
    requestedDeliveryDate: '2026-01-06',
    productId: 3,
    productName: 'Bags',
    specifications: '280gsm cotton canvas, 38 × 42 cm, long handles',
    productCategory: 'Bags',
    quantity: 800,
    brandingDescription: 'Novo Nordisk logo, Pantone 300C blue',
    deliveryRequirements: 'Bulk packaging for conference handouts',
    urgent: false,
    reorder: false,
    status: 'Sent to Sales Quote',
    country: 'Denmark',
    nextStep: 'Converted to offer',
    archived: false,
    createdBy: 'Mads Mark-Buur',
    createdAt: '2025-12-05 10:00',
    convertedOfferId: 'NOV-20251215',
    suppliers: [
      { supplierId: 2, supplierName: 'PrintPartner ApS' },
      { supplierId: 7, supplierName: 'EventSupply Co.' },
    ],
    supplierQuotes: {
      2: {
        status: 'accepted',
        lines: [{
          id: 'QUO-L001-20251205-2-line-1',
          quantity: 800,
          productDetails: '280gsm cotton canvas, 38 × 42 cm, long handles',
          unitPrice: 6,
          brandingDecoration: 'Novo Nordisk logo, Pantone 300C blue',
          productionTime: 8,
          shippingTime: 4,
          shippingCost: 1,
          validUntil: '2025-12-20',
          files: [],
          comments: 'Confirmed Pantone match and stock for conference deadline.',
        }],
      },
      7: {
        status: 'rejected',
        lines: [{
          id: 'QUO-L001-20251205-7-line-1',
          quantity: 800,
          productDetails: '280gsm cotton canvas, 38 × 42 cm, long handles',
          unitPrice: 7,
          brandingDecoration: 'Novo Nordisk logo, Pantone 300C blue',
          productionTime: 10,
          shippingTime: 5,
          shippingCost: 1,
          validUntil: '2025-12-20',
          files: [],
          comments: 'Slightly higher price than PrintPartner — not selected.',
        }],
      },
    },
  },
  {
    id: 'QUO-L001-20251205-2',
    key: 'QUO-L001-20251205-2',
    type: 'Quote',
    inquiryId: 'INQ-L001-20251205',
    inquiryItemId: 'ITEM-novo-usb',
    clientId: 'L001',
    client: 'Novo Nordisk A/S',
    requestedBy: 'Lars Jensen',
    priority: 'Medium',
    neededBefore: '2025-12-30',
    requestedDeliveryDate: '2026-01-06',
    productId: 12,
    productName: 'Umbrellas',
    specifications: '130cm diameter, fibreglass ribs, auto-open',
    productCategory: 'Outdoor',
    quantity: 300,
    brandingDescription: 'Novo Nordisk logo, panel print',
    deliveryRequirements: 'Individually boxed for conference gifting',
    urgent: false,
    reorder: false,
    status: 'Sent to Sales Quote',
    country: 'Denmark',
    nextStep: 'Converted to offer',
    archived: false,
    createdBy: 'Mads Mark-Buur',
    createdAt: '2025-12-05 10:00',
    convertedOfferId: 'NOV-20251215',
    suppliers: [{ supplierId: 1, supplierName: 'Nordic Textiles A/S' }],
    supplierQuotes: {
      1: {
        status: 'accepted',
        lines: [{
          id: 'QUO-L001-20251205-2-4-line-1',
          quantity: 300,
          productDetails: '130cm diameter, fibreglass ribs, auto-open',
          unitPrice: 34,
          brandingDecoration: 'Novo Nordisk logo, panel print',
          productionTime: 9,
          shippingTime: 4,
          shippingCost: 1,
          validUntil: '2025-12-20',
          files: [],
          comments: 'Confirmed production slot for conference deadline.',
        }],
      },
    },
  },
  {
    id: 'QUO-L007-20260123',
    key: 'QUO-L007-20260123',
    type: 'Quote',
    inquiryId: 'INQ-L007-20260123',
    inquiryItemId: 'ITEM-ramboll-tote',
    clientId: 'L007',
    client: 'Rambøll Group A/S',
    requestedBy: 'Henrik Sørensen',
    priority: 'Medium',
    neededBefore: '2026-02-28',
    requestedDeliveryDate: '2026-03-07',
    productId: 3,
    productName: 'Bags',
    specifications: 'Reusable shopping bags with custom print',
    productCategory: 'Bags & Outdoor',
    quantity: 600,
    brandingDescription: 'Rambøll logo, full-color one-side print',
    deliveryRequirements: 'Delivery for 200+ employee anniversary event',
    urgent: false,
    reorder: false,
    status: 'Sent to Sales Quote',
    country: 'Denmark',
    nextStep: 'Converted to offer',
    archived: false,
    createdBy: 'Mads Mark-Buur',
    createdAt: '2026-01-23 12:00',
    convertedOfferId: 'RAM-20260201',
    suppliers: [
      { supplierId: 1, supplierName: 'Nordic Textiles A/S' },
      { supplierId: 2, supplierName: 'PrintPartner ApS' },
    ],
    supplierQuotes: {
      1: {
        status: 'accepted',
        lines: [{
          id: 'QUO-L007-20260123-1-line-1',
          quantity: 600,
          productDetails: 'Reusable shopping bags with custom print',
          unitPrice: 28,
          brandingDecoration: 'Rambøll logo, full-color one-side print',
          productionTime: 10,
          shippingTime: 5,
          shippingCost: 2,
          validUntil: '2026-02-15',
          files: [],
          comments: 'Confirmed natural cotton stock for full quantity.',
        }],
      },
      2: {
        status: 'rejected',
        lines: [{
          id: 'QUO-L007-20260123-2-line-1',
          quantity: 600,
          productDetails: 'Reusable shopping bags with custom print',
          unitPrice: 31,
          brandingDecoration: 'Rambøll logo, full-color one-side print',
          productionTime: 12,
          shippingTime: 6,
          shippingCost: 3,
          validUntil: '2026-02-15',
          files: [],
          comments: 'Higher price than Nordic Textiles for the same spec — not selected.',
        }],
      },
    },
  },
  {
    id: 'QUO-L012-20251228',
    key: 'QUO-L012-20251228',
    type: 'Quote',
    inquiryId: 'INQ-L012-20251228',
    inquiryItemId: 'ITEM-saxo-cardholder-concept',
    clientId: 'L012',
    client: 'Saxo Bank A/S',
    requestedBy: 'Julie Andersen',
    priority: 'Medium',
    neededBefore: '2026-02-01',
    requestedDeliveryDate: '2026-02-08',
    productId: 8,
    productName: 'Bottles',
    specifications: '750ml stainless steel, double-wall insulated',
    productCategory: 'Drinkware',
    quantity: 200,
    brandingDescription: 'Saxo Bank monogram, laser-engraved',
    deliveryRequirements: 'Individually gift-boxed, presentation ribbon',
    urgent: false,
    reorder: false,
    status: 'Sent for Pricing',
    country: 'Denmark',
    nextStep: 'VIP gift concept still being finalised — awaiting management sign-off before confirming a supplier',
    archived: false,
    createdBy: 'Peter Sørensen',
    createdAt: '2025-12-28 11:00',
    convertedOfferId: 'SAX-20260105',
    suppliers: [
      { supplierId: 6, supplierName: 'MetalGoods Scandinavia' },
      { supplierId: 4, supplierName: 'TechGifts EU' },
    ],
    supplierQuotes: {
      6: {
        status: null,
        lines: [{
          id: 'QUO-L012-20251228-5-line-1',
          quantity: 200,
          productDetails: '750ml stainless steel, double-wall insulated',
          unitPrice: 98,
          brandingDecoration: 'Saxo Bank monogram, laser-engraved',
          productionTime: 14,
          shippingTime: 6,
          shippingCost: 2,
          validUntil: '2026-01-25',
          files: [],
          comments: 'Indicative pricing only — awaiting concept sign-off before confirming.',
        }],
      },
      4: {
        status: null,
        lines: [{
          id: 'QUO-L012-20251228-2-line-1',
          quantity: 200,
          productDetails: '750ml stainless steel, double-wall insulated',
          unitPrice: 105,
          brandingDecoration: 'Saxo Bank monogram, laser-engraved',
          productionTime: 16,
          shippingTime: 7,
          shippingCost: 2,
          validUntil: '2026-01-25',
          files: [],
          comments: 'Indicative pricing only — awaiting concept sign-off before confirming.',
        }],
      },
    },
  },
  // Demonstrates a supplier holding all three Selection_Wizard roles at
  // once (Amsterdam Global Trade — see EXTRA_SUPPLIER_PRODUCTS above):
  // one Supplier Quote card, three independently-priced role sections.
  {
    id: 'QUO-L001-20260310',
    key: 'QUO-L001-20260310',
    type: 'Quote',
    inquiryId: null,
    inquiryItemId: null,
    clientId: 'L001',
    client: 'Novo Nordisk A/S',
    requestedBy: 'Lars Jensen',
    priority: 'Medium',
    neededBefore: '2026-04-15',
    requestedDeliveryDate: '2026-04-22',
    productId: 16,
    productName: 'Power Banks',
    specifications: '5000mAh, USB-A + USB-C output, EU-assembled',
    productCategory: 'Electronics',
    quantity: 300,
    brandingDescription: 'UV-printed logo, full colour, top panel',
    deliveryRequirements: 'Palletised delivery to Novo Allé, 2880 Bagsværd',
    urgent: false,
    reorder: false,
    status: 'Quotation in Process',
    country: 'Denmark',
    nextStep: 'Awaiting supplier pricing',
    archived: false,
    createdBy: 'Mads Mark-Buur',
    createdAt: '2026-03-10 10:00',
    convertedOfferId: null,
    files: [],
    suppliers: [
      { supplierId: 16, supplierName: 'Amsterdam Global Trade', roles: ['Manufacturing', 'Services', 'Logistics'] },
    ],
    supplierQuotes: {
      16: {
        status: null,
        lines: [
          {
            id: 'QUO-L001-20260310-mfg-1',
            role: 'Manufacturing',
            quantity: 300,
            productDetails: '5000mAh power bank, EU-assembled, USB-A + USB-C output',
            unitPrice: 79,
            productionTime: 12,
            shippingTime: 6,
            shippingCost: 2.5,
            validUntil: '2026-04-01',
            files: [],
            comments: 'Standard lead time confirmed for this quantity.',
          },
          {
            id: 'QUO-L001-20260310-svc-1',
            role: 'Services',
            quantity: 300,
            brandingDecoration: 'UV-printed logo, full colour, top panel',
            unitPrice: 5.4,
            productionTime: 3,
            validUntil: '2026-04-01',
            files: [],
            comments: 'In-house UV printing, no subcontracting.',
          },
          {
            id: 'QUO-L001-20260310-log-1',
            role: 'Logistics',
            quantity: 300,
            shippingCost: 340,
            shippingTime: 5,
            validUntil: '2026-04-01',
            files: [],
            comments: 'EU road freight, palletised delivery.',
          },
        ],
      },
    },
  },
];

const MOCK_ALTERNATE_SUPPLIERS = {
  1: { supplierId: 2, supplierName: 'PrintPartner ApS' },
  2: { supplierId: 1, supplierName: 'Nordic Textiles A/S' },
  5: { supplierId: 2, supplierName: 'PrintPartner ApS' },
  7: { supplierId: 2, supplierName: 'PrintPartner ApS' },
  8: { supplierId: 1, supplierName: 'Nordic Textiles A/S' },
};

// Every supplier referenced by the legacy Quote fixtures below predates the
// Selection_Wizard (product-module/inquiry-module-redesign) and only ever
// held one role. Rather than leave that implicit (relying on quoteHelpers'
// legacy-supplierType fallback at render time), each quote.suppliers[] entry
// and supplierQuotes[].lines[] entry is tagged here with the same role that
// fallback already resolves to — no behavior change, just explicit.
const LEGACY_QUOTE_SUPPLIER_TYPE_TO_ROLE = {
  Manufacturing: 'Manufacturing',
  Product: 'Manufacturing',
  Branding: 'Services',
  Customization: 'Services',
  Logistics: 'Logistics',
  Fulfilment: 'Logistics',
};

const legacyQuoteSupplierRole = (supplierId) => {
  const supplierType = initialSuppliers.find((s) => s.id === supplierId)?.supplierType;
  return LEGACY_QUOTE_SUPPLIER_TYPE_TO_ROLE[supplierType] || 'Manufacturing';
};

const buildMockSupplierQuote = (quote, supplier, index, source = {}) => {
  const quantity = Number(quote.quantity || 1);
  const basePrice = Number(source.unitPrice || Math.max(1, Math.round(Number(quote.targetPrice || quote.budgetTargetPrice || (quantity * 40)) / quantity)));
  const role = legacyQuoteSupplierRole(supplier.supplierId);
  return {
    ...source,
    supplierId: supplier.supplierId,
    role,
    // The actual per-line data QuoteDetailPage reads — tag each line with
    // the same role (idempotent if already tagged).
    lines: (source.lines || []).map((line) => (line.role ? line : { ...line, role })),
    productDetails: source.productDetails || quote.specifications || '',
    brandingDecoration: source.brandingDecoration || quote.brandingDescription || '',
    unitPrice: basePrice + (index * 3),
    productionTime: Number(source.productionTime || 14) + (index * 2),
    shippingTime: Number(source.shippingTime || 7),
    shippingCost: Number(source.shippingCost || 3) + index,
    validUntil: source.validUntil || '2026-12-31',
    files: source.files || [],
    comments: source.comments || `Pricing and capacity confirmed by ${supplier.supplierName}.`,
  };
};

// Every quote fixture carries at least two supplier options and a complete
// supplier-quote record for each option, so the Supplier Quotes card grid can
// always show a meaningful comparison.
export const initialQuotes = initialQuotesRaw.map((quote) => {
  const initialSuppliersForQuote = quote.suppliers || [];
  const firstSupplier = initialSuppliersForQuote[0];
  const alternateSupplier = firstSupplier && MOCK_ALTERNATE_SUPPLIERS[firstSupplier.supplierId];
  const suppliersForQuote = initialSuppliersForQuote.length >= 2 || !alternateSupplier
    ? initialSuppliersForQuote
    : [...initialSuppliersForQuote, alternateSupplier];
  const existingSupplierQuotes = quote.supplierQuotes || {};
  const fallbackSupplierQuote = Object.values(existingSupplierQuotes)[0] || {};
  const supplierQuotes = Object.fromEntries(suppliersForQuote.map((supplier, index) => [
    supplier.supplierId,
    buildMockSupplierQuote(
      quote,
      supplier,
      index,
      existingSupplierQuotes[supplier.supplierId] || fallbackSupplierQuote,
    ),
  ]));

  const linkedLead = initialLeads.find((lead) => lead.id === quote.clientId);
  return {
    ...quote,
    createdBy: quote.createdBy || 'Sales team',
    requestedBy: quote.requestedBy || linkedLead?.contact || 'Customer contact',
    deliveryAddress: quote.deliveryAddress || linkedLead?.address || 'Delivery address to be confirmed',
    specifications: quote.specifications || `Specification for ${quote.productName || 'the requested product'}.`,
    brandingDescription: quote.brandingDescription || 'Branding requirements to be confirmed with the customer.',
    suppliers: suppliersForQuote.map((supplier) => (
      supplier.roles ? supplier : { ...supplier, roles: [legacyQuoteSupplierRole(supplier.supplierId)] }
    )),
    supplierQuotes,
  };
});

export const initialNotifications = [
  { id: 1, targetUserId: 4, type: 'inquiry_assigned', entityType: 'Inquiry', entityId: 'INQ-L001-20260205', entityLabel: 'INQ-L001-20260205', message: 'New inquiry INQ-L001-20260205 has been assigned to Procurement', timestamp: '2026-02-05 11:35', read: false },
  { id: 2, targetUserId: 5, type: 'inquiry_assigned', entityType: 'Inquiry', entityId: 'INQ-C002-20260207', entityLabel: 'INQ-C002-20260207', message: 'New inquiry INQ-C002-20260207 has been assigned to Procurement', timestamp: '2026-02-07 15:15', read: false },
  { id: 3, targetUserId: 1, type: 'status_changed', entityType: 'Quote', entityId: 'QUO-L001-20260205', entityLabel: 'QUO-L001-20260205', message: 'Quote QUO-L001-20260205 is ready — an Offer can now be created', timestamp: '2026-02-06 09:05', read: false },
  { id: 4, targetUserId: 4, type: 'status_changed', entityType: 'Quote', entityId: 'QUO-C002-20260207', entityLabel: 'QUO-C002-20260207', message: 'Quote QUO-C002-20260207 has been returned to Procurement', timestamp: '2026-02-08 11:35', read: false },
  { id: 5, targetUserId: 1, type: 'status_changed', entityType: 'Offer', entityId: 'TDC-20260129', entityLabel: 'TDC-20260129', message: 'Offer TDC-20260129 status changed to Sent to the Customer', timestamp: '2026-01-29 16:00', read: false },
  { id: 6, targetUserId: 3, type: 'status_changed', entityType: 'Order', entityId: 'CAR-20260125', entityLabel: 'CAR-20260125', message: 'Order CAR-20260125 status changed to Accepted by Customer', timestamp: '2026-01-25 11:10', read: false },
  { id: 7, targetUserId: 1, type: 'document_converted', entityType: 'Offer', entityId: 'NOV-20260201', entityLabel: 'NOV-20260201', message: 'Quote QUO-L001-20260205 has been converted to Offer NOV-20260201', timestamp: '2026-02-06 09:10', read: false },
  { id: 8, targetUserId: 2, type: 'document_converted', entityType: 'Order', entityId: 'CAR-20260125', entityLabel: 'CAR-20260125', message: 'Offer CAR-20260123 has been converted to Order CAR-20260125', timestamp: '2026-01-25 10:05', read: false },
  { id: 9, targetUserId: 4, type: 'comment_added', entityType: 'Inquiry', entityId: 'INQ-C004-20260128', entityLabel: 'INQ-C004-20260128', message: 'Mads Mark-Buur left a comment on INQ-C004-20260128', timestamp: '2026-02-04 09:42', read: false },
  { id: 10, targetUserId: 1, type: 'comment_added', entityType: 'Quote', entityId: 'QUO-C004-20260128', entityLabel: 'QUO-C004-20260128', message: 'Maria Kristensen left a comment on QUO-C004-20260128', timestamp: '2026-02-04 09:52', read: false },
  { id: 11, targetUserId: 1, type: 'certificate_expiring', entityType: 'Certificate', entityId: 2, entityLabel: 'OEKO-TEX Standard 100', message: 'Certificate "OEKO-TEX Standard 100" for Nordic Textiles A/S expires soon.', timestamp: '2026-06-20 09:00', read: false },
];

// Base certificate data; `scope`/`inherited`/`verifiedBy`/`verifiedAt` are
// filled in below via the exported `initialCertificates` (product-module-
// analysis.md §6.5 / §10 — scope is derived from productId for existing
// records, `inherited` defaults to false except where set explicitly here).
const CERTIFICATE_BASE = [
  // Supplier 1 — Nordic Textiles A/S
  {
    id: 1,
    supplierId: 1,
    name: 'ISO 9001:2015 Quality Management',
    description: 'Third-party quality management system certification.',
    documentType: 'Quality Management',
    standard: 'ISO 9001',
    productId: null,
    validFrom: '2024-01-15',
    expiryDate: '2025-01-31',
    fileName: 'NT_ISO9001_2025.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Maria Kristensen',
    uploadDate: '2024-01-15 09:00',
  },
  {
    id: 2,
    supplierId: 1,
    name: 'OEKO-TEX Standard 100',
    description: 'Textile safety certification for cotton products.',
    documentType: 'Textile Safety',
    standard: 'OEKO-TEX 100',
    productId: 3,
    validFrom: '2025-07-01',
    expiryDate: '2026-07-15',
    fileName: 'NT_OEKOTEX_BAG.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Lars Nielsen',
    uploadDate: '2025-07-01 11:30',
  },
  // Supplier 2 — PrintPartner ApS
  {
    id: 3,
    supplierId: 2,
    name: 'FSC Chain of Custody',
    description: 'Forest Stewardship Council certification for paper-based packaging materials.',
    documentType: 'Forest Stewardship Council',
    standard: 'FSC',
    productId: null,
    validFrom: '2025-01-10',
    expiryDate: '2028-12-31',
    fileName: 'PP_FSC_PKG.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Maria Kristensen',
    uploadDate: '2025-01-10 14:00',
  },
  {
    id: 4,
    supplierId: 2,
    name: 'ISO 14001 Environmental Management',
    description: 'Environmental management system certification.',
    documentType: 'Environmental Management',
    standard: 'ISO 14001',
    productId: null,
    validFrom: '2023-03-01',
    expiryDate: '2025-02-28',
    fileName: 'PP_ISO14001.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Lars Nielsen',
    uploadDate: '2023-03-01 10:00',
  },
  // Supplier 3 — CeramicWorks DK
  {
    id: 5,
    supplierId: 3,
    name: 'EU Food Contact Compliance',
    description: 'Certifies mugs meet EU Regulation 1935/2004 for food contact materials.',
    documentType: 'Food Contact Safety',
    standard: 'EU 1935/2004',
    productId: 4,
    validFrom: '2025-03-20',
    expiryDate: '2028-03-15',
    fileName: 'CW_FoodContact_MUG.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Maria Kristensen',
    uploadDate: '2025-03-20 09:45',
  },
  {
    id: 6,
    supplierId: 3,
    name: 'REACH Compliance Declaration',
    description: 'Supplier declaration of compliance with EU REACH chemical regulation.',
    documentType: 'Chemical Compliance',
    standard: 'REACH',
    productId: null,
    validFrom: '2024-09-05',
    expiryDate: '2026-07-05',
    fileName: 'CW_REACH_Declaration.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Lars Nielsen',
    uploadDate: '2024-09-05 16:20',
  },
  // Supplier 4 — TechGifts EU
  {
    id: 7,
    supplierId: 4,
    name: 'RoHS Compliance Certificate',
    description: 'Restriction of Hazardous Substances compliance for electronic gadgets.',
    documentType: 'Electronics Safety',
    standard: 'RoHS',
    productId: null,
    validFrom: '2025-05-01',
    expiryDate: '2026-07-01',
    fileName: 'TG_RoHS_2026.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Maria Kristensen',
    uploadDate: '2025-05-01 10:00',
  },
  // Supplier 6 — MetalGoods Scandinavia
  {
    id: 8,
    supplierId: 6,
    name: 'CE Marking Declaration',
    description: 'Declaration of conformity for steel drinkware products.',
    documentType: 'Product Safety',
    standard: 'CE',
    productId: null,
    validFrom: '2024-04-01',
    expiryDate: '2026-04-01',
    fileName: 'MG_CEMarking_2024.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Lars Nielsen',
    uploadDate: '2024-04-01 11:00',
  },
  // Supplier 7 — EventSupply Co.
  {
    id: 9,
    supplierId: 7,
    name: 'ISO 45001 Certificate',
    description: 'Occupational health and safety management system certification.',
    documentType: 'Occupational Health & Safety',
    standard: 'ISO 45001',
    productId: null,
    validFrom: '2025-09-01',
    expiryDate: '2027-09-01',
    fileName: 'EV_ISO45001_2025.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Maria Kristensen',
    uploadDate: '2025-09-01 09:00',
  },
  // Supplier 9 — BrandEmboss Studio
  {
    id: 10,
    supplierId: 9,
    name: 'GOTS Certificate',
    description: 'Global Organic Textile Standard certification for embroidery materials.',
    documentType: 'Organic Textile',
    standard: 'GOTS',
    productId: null,
    validFrom: '2025-11-01',
    expiryDate: '2026-09-15',
    fileName: 'BE_GOTS_2025.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Lars Nielsen',
    uploadDate: '2025-11-01 14:30',
  },
  // Supplier 8 — PromoGoods DK
  {
    id: 11,
    supplierId: 8,
    name: 'EN ISO 20471 High-Visibility Clothing',
    description: 'Conformity certificate for the supplier’s high-visibility safety vest range.',
    documentType: 'Product Safety',
    standard: 'EN ISO 20471',
    productId: null,
    validFrom: '2026-01-15',
    expiryDate: '2027-01-14',
    fileName: 'PG_ENISO20471_2026.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Maria Kristensen',
    uploadDate: '2026-01-15 10:15',
  },
  // Supplier 5 — PaperCraft ApS
  {
    id: 12,
    supplierId: 5,
    name: 'FSC Chain of Custody',
    description: 'Forest Stewardship Council certification for paper stock sourcing.',
    documentType: 'Forest Stewardship Council',
    standard: 'FSC',
    productId: null,
    validFrom: '2025-02-01',
    expiryDate: '2028-01-31',
    fileName: 'PC_FSC_PKG.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Maria Kristensen',
    uploadDate: '2025-02-01 09:30',
  },
  {
    id: 13,
    supplierId: 5,
    name: 'ISO 9001:2015 Quality Management',
    description: 'Third-party quality management system certification.',
    documentType: 'Quality Management',
    standard: 'ISO 9001',
    productId: null,
    validFrom: '2023-06-01',
    expiryDate: '2026-07-10',
    fileName: 'PC_ISO9001_2026.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Lars Nielsen',
    uploadDate: '2023-06-01 11:15',
  },
  // Supplier 10 — NordicFreight Logistics
  {
    id: 14,
    supplierId: 10,
    name: 'AEO — Authorised Economic Operator',
    description: 'Customs authorisation confirming reliable, compliant cross-border freight handling.',
    documentType: 'Customs Compliance',
    standard: 'AEO',
    productId: null,
    validFrom: '2024-05-01',
    expiryDate: '2027-04-30',
    fileName: 'NF_AEO_2024.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Maria Kristensen',
    uploadDate: '2024-05-01 13:00',
  },
  {
    id: 15,
    supplierId: 10,
    name: 'ISO 39001 Road Traffic Safety Management',
    description: 'Management system certification for fleet and road traffic safety.',
    documentType: 'Road Safety Management',
    standard: 'ISO 39001',
    productId: null,
    validFrom: '2022-08-01',
    expiryDate: '2026-05-31',
    fileName: 'NF_ISO39001_2022.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Lars Nielsen',
    uploadDate: '2022-08-01 10:30',
  },
  // Supplier 11 — ColorCraft Decoration
  {
    id: 16,
    supplierId: 11,
    name: 'OEKO-TEX Standard 100',
    description: 'Textile safety certification covering decoration inks and print finishes.',
    documentType: 'Textile Safety',
    standard: 'OEKO-TEX 100',
    productId: null,
    validFrom: '2025-09-01',
    expiryDate: '2026-08-31',
    fileName: 'CC_OEKOTEX_2025.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Maria Kristensen',
    uploadDate: '2025-09-01 09:00',
  },
  {
    id: 17,
    supplierId: 11,
    name: 'BSCI Social Compliance Audit',
    description: 'Business Social Compliance Initiative audit covering factory labour standards.',
    documentType: 'Social Compliance',
    standard: 'BSCI',
    productId: null,
    validFrom: '2024-02-01',
    expiryDate: '2026-07-05',
    fileName: 'CC_BSCI_2024.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Lars Nielsen',
    uploadDate: '2024-02-01 14:45',
  },
  // Supplier 12 — SwiftCargo Express
  {
    id: 18,
    supplierId: 12,
    name: 'AEO — Authorised Economic Operator',
    description: 'Customs authorisation confirming reliable, compliant cross-border freight handling.',
    documentType: 'Customs Compliance',
    standard: 'AEO',
    productId: null,
    validFrom: '2023-10-01',
    expiryDate: '2026-09-30',
    fileName: 'SC_AEO_2023.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Maria Kristensen',
    uploadDate: '2023-10-01 15:10',
  },
  {
    id: 19,
    supplierId: 12,
    name: 'ISO 9001:2015 Quality Management',
    description: 'Third-party quality management system certification.',
    documentType: 'Quality Management',
    standard: 'ISO 9001',
    productId: null,
    validFrom: '2021-01-01',
    expiryDate: '2026-01-01',
    fileName: 'SC_ISO9001_2021.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Lars Nielsen',
    uploadDate: '2021-01-01 09:00',
  },
  // Additional certificates for suppliers behind INQ-L005-20260623 (Danske Bank)
  // Supplier 5 — PaperCraft ApS (Custom Card Holders)
  {
    id: 20,
    supplierId: 5,
    name: 'Leather Working Group (LWG) Certification',
    description: 'Audited leather tannery environmental performance rating for leather stock sourcing.',
    documentType: 'Leather Sourcing',
    standard: 'LWG',
    productId: null,
    validFrom: '2025-04-01',
    expiryDate: '2027-03-31',
    fileName: 'PC_LWG_LEATHER.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Maria Kristensen',
    uploadDate: '2025-04-01 10:20',
  },
  // Supplier 2 — PrintPartner ApS (Custom Card Holders / Corporate Notebooks)
  {
    id: 21,
    supplierId: 2,
    name: 'REACH Compliance Declaration',
    description: 'Supplier declaration of compliance with EU REACH chemical regulation for printing inks and foils.',
    documentType: 'Chemical Compliance',
    standard: 'REACH',
    productId: null,
    validFrom: '2025-05-12',
    expiryDate: '2026-07-15',
    fileName: 'PP_REACH_Declaration.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Lars Nielsen',
    uploadDate: '2025-05-12 13:50',
  },
  // Supplier 4 — TechGifts EU (Power Banks)
  {
    id: 22,
    supplierId: 4,
    name: 'CE Marking Declaration',
    description: 'Declaration of conformity for power bank electronics and battery safety.',
    documentType: 'Product Safety',
    standard: 'CE',
    productId: 16,
    validFrom: '2025-02-10',
    expiryDate: '2027-02-09',
    fileName: 'TG_CEMarking_PWR5K.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Maria Kristensen',
    uploadDate: '2025-02-10 09:15',
  },
  {
    id: 23,
    supplierId: 4,
    name: 'ISO 9001:2015 Quality Management',
    description: 'Third-party quality management system certification.',
    documentType: 'Quality Management',
    standard: 'ISO 9001',
    productId: null,
    validFrom: '2022-11-01',
    expiryDate: '2026-07-20',
    fileName: 'TG_ISO9001_2022.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Lars Nielsen',
    uploadDate: '2022-11-01 11:40',
  },
  // Supplier 8 — PromoGoods DK (Power Banks)
  {
    id: 24,
    supplierId: 8,
    name: 'RoHS Compliance Certificate',
    description: 'Restriction of Hazardous Substances compliance for power bank electronics.',
    documentType: 'Electronics Safety',
    standard: 'RoHS',
    productId: 16,
    validFrom: '2025-08-01',
    expiryDate: '2026-08-01',
    fileName: 'PG_RoHS_PWR5K.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Maria Kristensen',
    uploadDate: '2025-08-01 10:05',
  },
  {
    id: 25,
    supplierId: 8,
    name: 'ISO 9001:2015 Quality Management',
    description: 'Third-party quality management system certification.',
    documentType: 'Quality Management',
    standard: 'ISO 9001',
    productId: null,
    validFrom: '2024-03-15',
    expiryDate: '2026-05-15',
    fileName: 'PG_ISO9001_2024.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Lars Nielsen',
    uploadDate: '2024-03-15 09:30',
  },
  // Product-specific test reports for the Custom products (product-module-
  // analysis.md §12 mock data plan). CTM-000002 and CTM-000006 are copies
  // (Copy Product) of CTM-000001 / CTM-000004, so their inherited test
  // reports are deliberately left unverified to demonstrate the compliance
  // warning (§4.4 / Q2) — the other two are original, verified reports.
  {
    id: 26,
    supplierId: 8,
    name: 'EN71 Toy Safety Test Report',
    description: 'Mechanical, physical and chemical safety test report for the plush toy.',
    documentType: 'Test Report',
    standard: 'EN71',
    productId: 22,
    validFrom: '2025-08-20',
    expiryDate: '2027-08-19',
    fileName: 'PG_EN71_PLUSH_ARLA.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Lars Nielsen',
    uploadDate: '2025-08-20 10:00',
  },
  {
    id: 27,
    supplierId: 8,
    name: 'EN71 Toy Safety Test Report',
    description: 'Mechanical, physical and chemical safety test report for the plush toy.',
    documentType: 'Test Report',
    standard: 'EN71',
    productId: 24,
    validFrom: '2025-08-20',
    expiryDate: '2027-08-19',
    fileName: 'PG_EN71_PLUSH_ARLA.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Lars Nielsen',
    uploadDate: '2025-11-10 10:20',
    inherited: true,
  },
  {
    id: 28,
    supplierId: 8,
    name: 'REACH Compliance Declaration',
    description: 'Supplier declaration of compliance with EU REACH chemical regulation for the football.',
    documentType: 'REACH Declaration',
    standard: 'REACH',
    productId: 19,
    validFrom: '2025-11-15',
    expiryDate: '2027-11-14',
    fileName: 'PG_REACH_FOOTBALL_CAR.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Maria Kristensen',
    uploadDate: '2025-11-20 10:00',
  },
  {
    id: 29,
    supplierId: 8,
    name: 'REACH Compliance Declaration',
    description: 'Supplier declaration of compliance with EU REACH chemical regulation for the football.',
    documentType: 'REACH Declaration',
    standard: 'REACH',
    productId: 20,
    validFrom: '2025-11-15',
    expiryDate: '2027-11-14',
    fileName: 'PG_REACH_FOOTBALL_CAR.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Maria Kristensen',
    uploadDate: '2026-01-24 11:30',
    inherited: true,
  },
];

// Product Compliance tab / CompliancePage read `scope` (derived from
// `productId` for pre-existing records), `inherited` (defaults false unless
// set above), and `verifiedBy`/`verifiedAt` (set once a user clears an
// inherited document via the Verify action).
export const initialCertificates = CERTIFICATE_BASE.map((cert) => ({
  ...cert,
  scope: cert.scope || (cert.productId != null ? 'Product' : 'Supplier'),
  inherited: cert.inherited ?? false,
  verifiedBy: cert.verifiedBy ?? null,
  verifiedAt: cert.verifiedAt ?? null,
}));

// Generic per-supplier documents (price lists, agreements, factory audits,
// etc.) — stored the same way as Certificates but without expiry tracking.
export const initialDocuments = [
  {
    id: 1,
    supplierId: 1,
    name: 'Wholesale Price List 2026',
    description: 'Current price list for all garment categories.',
    fileName: 'NT_PriceList_2026.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Maria Kristensen',
    uploadDate: '2026-01-05 10:00',
  },
  {
    id: 2,
    supplierId: 1,
    name: 'Supplier Agreement',
    description: 'Signed framework agreement covering payment and delivery terms.',
    fileName: 'NT_Agreement_2025.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Lars Nielsen',
    uploadDate: '2025-02-10 09:30',
  },
  {
    id: 3,
    supplierId: 2,
    name: 'Factory Audit Report',
    description: 'Third-party social audit report for the printing facility.',
    fileName: 'PP_FactoryAudit_2025.pdf',
    fileUrl: '/mock-files/certificate-preview.pdf',
    uploadedBy: 'Maria Kristensen',
    uploadDate: '2025-06-18 13:15',
  },
];
